import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Building, Thermometer, RectangleHorizontal, Settings, Edit3, ChevronDown, ArrowLeft, Plus, Minus, Home, Ruler, Trash2, ChevronRight, Wind, Zap, Database, Wrench, Lightbulb, Camera, Image as ImageIcon } from 'lucide-react';
import type { SiteNotesData } from '../utils/fileUtils';
import { combineAddressFields, parseAddressFromString } from '../utils/fileUtils';
import AutosaveIndicator from './AutosaveIndicator';
import type { AutosaveStatus } from '../hooks/useAutosave';
import { debounce } from '../utils/helpers';
import PCDBSearch, { PCDBHeatingSystem } from './PCDBSearch';
import PropertyElevationDiagram from './PropertyElevationDiagram';
import RoomInRoofDiagram from './RoomInRoofDiagram';
import { findRoomAtPoint } from '../utils/floorPlanUtils';
import PhotoCapture from './PhotoCapture';
import PhotoGalleryModal from './PhotoGalleryModal';
import { useRetrofitPhotoUpload } from '../hooks/useRetrofitPhotoUpload';

interface SurveyorSiteNotesProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  propertyAddress?: string;
  onPropertyAddressChange?: (address: string) => void;
  initialData: SiteNotesData;
  onDataChange: (data: SiteNotesData) => void;
  // Autosave status props
  autosaveStatus: AutosaveStatus;
  lastSavedAt: Date | null;
  lastError: string | null;
  retryCount: number;
  canEnableAutosave: boolean;
  // Floor plan calculations callback
  onGetFloorCalculations?: (floorNumber: number) => Promise<{ area: string; hlp: string; pw: string; height: string } | null>;
  walls?: any[];
  roomCustomLabels?: Record<string, string>;
  // Radiator type sync callback
  onRadiatorTypeSync?: (roomName: string, newRadiatorType: string) => void;
  // Window deletion from floor plan callback
  onDeleteWindowFromFloorPlan?: (floorId: number, roomId: number, windowId: number) => void;
  // Radiator deletion from floor plan callback
  onDeleteRadiatorFromFloorPlan?: (floorId: number, roomId: number, radiatorId: number) => void;
  // Window dimension sync callback (site notes to floor plan)
  onWindowDimensionSync?: (roomName: string, floorId: number, windowIndex: number, dimensions: { width: number; height: number; trickleVents: boolean; orientation?: string }) => void;
  // Floor deletion callback to notify floor plan
  onFloorDelete?: (floorLevel: string, buildingType: string) => void;
  // Navigation function
  onNavigate?: (path: string) => void;
  // Element sync trigger callback to fix phantom elements
  onSyncElements?: () => void;
  // Floor plan ID for photo uploads
  floorPlanId?: string;
}

type SectionId = 'basic' | 'property' | 'wall-insulation' | 'room-in-roof' | 'windows' | 'heating' | 'ventilation' | 'lighting' | 'utilities' | 'rooms' | 'evidence' | 'additional';

interface Section {
  id: SectionId;
  label: string;
  icon: React.ElementType;
}



// Define the floor interface for type safety
type FloorData = SiteNotesData['floors'][0];

const SectionCard = ({ icon: Icon, title, children }: { icon: React.ElementType, title: string, children: React.ReactNode }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
    <div className="flex items-center gap-3 pb-4 mb-6 border-b-2 border-yellow-100">
      <div className="p-2 bg-yellow-100 rounded-lg">
        <Icon className="text-yellow-600" size={20} />
      </div>
      <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
    </div>
    <div className="space-y-6">
      {children}
    </div>
  </div>
);

const ToggleField = ({ label, name, value, onChange, options, required = false }: {
  label: string,
  name: string,
  value: string,
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  options: { value: string, label: string }[],
  required?: boolean
}) => (
  <div className="space-y-2">
    <label className="text-sm font-medium text-gray-800 flex items-center">
      {required && <span className="text-red-500 mr-1">*</span>}
      {label}
    </label>
    <div className="flex gap-2">
      {options.map(option => (
        <label key={option.value} className="flex items-center cursor-pointer">
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={onChange}
            className="sr-only"
            required={required}
          />
          <div className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
            value === option.value 
              ? 'bg-yellow-100 border-yellow-400 text-yellow-700' 
              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}>
            {option.label}
          </div>
        </label>
      ))}
    </div>
  </div>
);

const FormField = ({ label, name, value, onChange, onBlur, placeholder = "enter value", required = false, disabled = false, type = "text" }: { 
  label: string, 
  name: string, 
  value: string, 
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void, 
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void,
  placeholder?: string,
  required?: boolean,
  disabled?: boolean,
  type?: string
}) => (
  <div className="space-y-1">
    <label htmlFor={name} className="text-sm font-medium text-gray-700 flex items-center">
      {required && <span className="text-red-500 mr-1">*</span>}
      {label}
    </label>
    <input 
      id={name} 
      name={name} 
      type={type} 
      value={value} 
      onChange={onChange} 
      onBlur={onBlur}
      disabled={disabled}
      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent placeholder-gray-400 text-sm ${disabled ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : 'bg-white'}`}
      placeholder={placeholder}
      required={required}
    />
  </div>
);

const NumericFormField = ({ label, name, value, onChange, onBlur, placeholder = "0.00", required = false, disabled = false, min = 0, max = 999, step = 0.01 }: { 
  label: string, 
  name: string, 
  value: string, 
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void, 
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void,
  placeholder?: string,
  required?: boolean,
  disabled?: boolean,
  min?: number,
  max?: number,
  step?: number
}) => {
  const handleNumericInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Allow empty string for clearing the field
    if (inputValue === '') {
      onChange(e);
      return;
    }
    
    // Allow valid decimal numbers
    const numericRegex = /^\d*\.?\d*$/;
    if (numericRegex.test(inputValue)) {
      // Check if the value is within bounds
      const numValue = parseFloat(inputValue);
      if (!isNaN(numValue) && numValue >= min && numValue <= max) {
        onChange(e);
      } else if (inputValue.match(/^\d*\.?\d*$/) && inputValue.length <= 10) {
        // Allow partial input while typing (e.g., "1." or "0.1")
        onChange(e);
      }
    }
  };

  return (
    <div className="space-y-2">
      <label htmlFor={name} className="text-sm font-medium text-gray-800 flex items-center">
        {required && <span className="text-red-500 mr-1">*</span>}
        {label}
      </label>
      <input 
        id={name} 
        name={name} 
        type="number" 
        inputMode="decimal"
        value={value} 
        onChange={handleNumericInput} 
        onBlur={onBlur}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent placeholder-gray-400 ${disabled ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : 'bg-gray-50'}`}
        placeholder={placeholder}
        required={required}
      />
    </div>
  );
};

const SelectField = ({ label, name, value, onChange, onBlur, options, required = false }: {
  label: string,
  name: string,
  value: string,
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void,
  onBlur?: (e: React.FocusEvent<HTMLSelectElement>) => void,
  options: { value: string, label: string }[],
  required?: boolean
}) => (
  <div className="space-y-1">
    <label htmlFor={name} className="text-sm font-medium text-gray-700 flex items-center">
      {required && <span className="text-red-500 mr-1">*</span>}
      {label}
    </label>
    <div className="relative">
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white appearance-none cursor-pointer text-sm"
        required={required}
      >
        <option value="">Select {label}</option>
        {options.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={20} />
    </div>
  </div>
);

const CounterField = ({ label, name, value, onChange, required = false, min = 0, max = 999 }: {
  label: string,
  name: string,
  value: string,
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  required?: boolean,
  min?: number,
  max?: number
}) => {
  const numericValue = parseInt(value) || 0;
  
  const handleIncrement = () => {
    if (numericValue < max) {
      const newValue = numericValue + 1;
      onChange({ target: { name, value: newValue.toString() } } as React.ChangeEvent<HTMLInputElement>);
    }
  };
  
  const handleDecrement = () => {
    if (numericValue > min) {
      const newValue = numericValue - 1;
      onChange({ target: { name, value: newValue.toString() } } as React.ChangeEvent<HTMLInputElement>);
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // Allow empty string for clearing, or valid numbers within range
    if (newValue === '' || (!isNaN(parseInt(newValue)) && parseInt(newValue) >= min && parseInt(newValue) <= max)) {
      onChange(e);
    }
  };

  return (
    <div className="space-y-2">
      <label htmlFor={name} className="text-sm font-medium text-gray-800 flex items-center">
        {required && <span className="text-red-500 mr-1">*</span>}
        {label}
      </label>
      <div className="flex items-center bg-gray-50 border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-yellow-500 focus-within:border-transparent">
        <button
          type="button"
          onClick={handleDecrement}
          disabled={numericValue <= min}
          className="px-3 py-3 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Minus size={16} className="text-gray-600" />
        </button>
        <input
          id={name}
          name={name}
          type="number"
          value={value}
          onChange={handleInputChange}
          min={min}
          max={max}
          className="flex-1 px-4 py-3 text-center bg-transparent focus:outline-none text-gray-900 font-medium"
          required={required}
        />
        <button
          type="button"
          onClick={handleIncrement}
          disabled={numericValue >= max}
          className="px-3 py-3 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Plus size={16} className="text-gray-600" />
        </button>
      </div>
    </div>
  );
};

const SurveyorSiteNotes: React.FC<SurveyorSiteNotesProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  propertyAddress, 
  onPropertyAddressChange,
  initialData, 
  onDataChange,
  autosaveStatus,
  lastSavedAt,
  lastError,
  retryCount,
  canEnableAutosave,
  onGetFloorCalculations,
  walls,
  roomCustomLabels,
  onRadiatorTypeSync,
  onDeleteWindowFromFloorPlan,
  onDeleteRadiatorFromFloorPlan,
  onWindowDimensionSync,
  onFloorDelete,
  onNavigate,
  onSyncElements,
  floorPlanId
}: SurveyorSiteNotesProps) => {
  // Initialize CDN photo upload for evidence photos
  const { queuePhotoUpload } = useRetrofitPhotoUpload(floorPlanId, propertyAddress);
  // Track if we've already synced for this session to prevent loops
  const hasSyncedRef = useRef(false);
  
  // Trigger element sync when opening to fix phantom elements (only once per session)
  useEffect(() => {
    if (isOpen && onSyncElements && !hasSyncedRef.current) {
      console.log('🔄 SurveyorSiteNotes: Triggering element sync to fix phantom elements');
      onSyncElements();
      hasSyncedRef.current = true;
    }
    
    // Reset sync flag when closing so it can sync again on next open
    if (!isOpen) {
      hasSyncedRef.current = false;
    }
  }, [isOpen]); // Removed onSyncElements from dependencies to prevent loops

  // Migration logic: Convert old address format to new format
  const migratedInitialData = useMemo(() => {
    const migrated = { ...initialData };
    
    // Check if address is in old string format and convert to new object format
    if (migrated.formData && typeof migrated.formData.address === 'string') {
      const addressString = migrated.formData.address;
      migrated.formData = {
        ...migrated.formData,
        address: parseAddressFromString(addressString)
      };
    } else if (migrated.formData && !migrated.formData.address) {
      // Initialize empty address object if none exists
      migrated.formData = {
        ...migrated.formData,
        address: {
          houseNumber: '',
          street: '',
          town: '',
          postcode: ''
        }
      };
    }
    
    // Set default value for hasRoomInRoof if not already set
    if (migrated.formData && !migrated.formData.hasRoomInRoof) {
      migrated.formData = {
        ...migrated.formData,
        hasRoomInRoof: 'no'
      };
    }
    
    return migrated;
  }, [initialData]);

  const [currentSection, setCurrentSection] = useState<SectionId>('basic');
  const [formData, setFormData] = useState<SiteNotesData['formData']>(migratedInitialData.formData || {});
  const [viewingEvidenceGallery, setViewingEvidenceGallery] = useState<string | null>(null);
  const [floors, setFloors] = useState<FloorData[]>(
    migratedInitialData.floors && migratedInitialData.floors.length > 0 
      ? migratedInitialData.floors 
      : [
          {
            id: 1,
            floorLevel: 'ground floor',
            buildingType: 'main building',
            height: '',
            floorArea: '',
            hlp: '',
            pw: '',
            rooms: []
          }
        ]
  );
  const [heatingSystems, setHeatingSystems] = useState<NonNullable<SiteNotesData['heatingSystems']>>(
    initialData.heatingSystems || []
  );
  const isSyncingAddressRef = useRef(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string>('');
  const [selectedPCDBSystem, setSelectedPCDBSystem] = useState<PCDBHeatingSystem | null>(null);
  const [isManualHeatingEntry, setIsManualHeatingEntry] = useState(false);
  

  // Force refresh counter to trigger re-renders after external data updates
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Track when external sync operations are happening to prevent autosave conflicts
  const isUpdatingFromExternalSync = useRef(false);

  // Wall insulation elevation data tracking
  const [elevationData, setElevationData] = useState<Record<string, {
    wallArea: number;
    untreatableArea: number;
    treatableArea: number;
    dimensions: {
      height: number;
      width: number;
    };
    elements: {
      doors: Array<{
        id: string;
        height: number;
        width: number;
      }>;
      windows: Array<{
        id: string;
        height: number;
        width: number;
      }>;
      airVents: Array<{
        id: string;
      }>;
    };
    placedElements?: Array<{
      id: string;
      type: string;
      x: number;
      y: number;
      elevation: string;
      height?: number;
      width?: number;
    }>;
    wallInsulationType?: string;
    buildingHeight?: number;
  }>>({});


  // Room in Roof diagram data tracking - initialize with saved data or defaults
  const [rirData, setRirData] = useState<{
    measurements: Array<{
      id: string;
      label: string;
      value: string;
      unit: string;
    }>;
    annotations: Array<{
      id: string;
      x: number;
      y: number;
      text: string;
    }>;
    insulationType?: string;
    ventilationType?: string;
    diagramType?: 'standard' | 'with-dormer';
    diagramImage?: string;
  }>(() => {
    // Initialize with saved data if available, otherwise use defaults
    const savedData = formData.roomInRoofData || migratedInitialData.formData.roomInRoofData;
    
    console.log('🚀 SITE NOTES: Initializing rirData state:', {
      hasFormDataRoomInRoof: !!formData.roomInRoofData,
      hasMigratedData: !!migratedInitialData.formData.roomInRoofData,
      savedDiagramType: savedData?.diagramType,
      willUseDefaults: !savedData
    });
    
    return savedData || {
      measurements: [],
      annotations: [],
      insulationType: 'between-rafters',
      ventilationType: 'ridge-and-eaves',
      diagramType: 'standard',
      diagramImage: ''
    };
  });

  // Track data restoration to prevent constant re-initialization
  const restoredDataRef = useRef<Record<string, any>>({});
  const lastAddressRef = useRef<string>('');
  


  // Reset restoration tracking when modal closes to allow fresh restoration on next open
  useEffect(() => {
    if (!isOpen) {
      // Clear the restored data ref when modal closes
      // This ensures data will be restored fresh when modal reopens
      restoredDataRef.current = {};
    }
  }, [isOpen]);

  // Restore elevation data from loaded file data (only when needed)
  useEffect(() => {
    const currentAddress = combineAddressFields(migratedInitialData.formData.address);
    const hasWallInsulationData = migratedInitialData.formData.wallInsulationElevations && migratedInitialData.formData.wallInsulationElevations.length > 0;
    
    // Check if this is a different file/address OR if we have no restored data yet OR if modal just opened
    const isDifferentFile = lastAddressRef.current !== currentAddress;
    const hasNoRestoredData = Object.keys(restoredDataRef.current).length === 0;
    const modalJustOpened = isOpen && hasNoRestoredData;
    
    if (hasWallInsulationData && (isDifferentFile || hasNoRestoredData || modalJustOpened)) {
      // Only log on actual restoration to avoid spam
      if (isDifferentFile) {
        console.log('🔄 Loading wall insulation data for new file:', currentAddress || 'Unknown Address');
      } else if (modalJustOpened) {
        console.log('🔄 Restoring wall insulation data on modal open');
      }
      
      const restoredElevationData: typeof elevationData = {};
      
      migratedInitialData.formData.wallInsulationElevations.forEach(elevation => {
        if (elevation.dimensions && elevation.elements && elevation.calculations) {
          restoredElevationData[elevation.id] = {
            wallArea: elevation.calculations.totalWallArea,
            untreatableArea: elevation.calculations.totalElementsArea,
            treatableArea: elevation.calculations.treatableArea,
            dimensions: elevation.dimensions,
            elements: elevation.elements,
            placedElements: elevation.placedElements || [],
            wallInsulationType: elevation.wallInsulationType || 'cavity',
            buildingHeight: 2
          };
        }
      });
      
      if (Object.keys(restoredElevationData).length > 0) {
        setElevationData(restoredElevationData);
        restoredDataRef.current = restoredElevationData;
        lastAddressRef.current = currentAddress;
      }
    }
  }, [migratedInitialData.formData.address, isOpen]); // Depend on address and modal open state

  // Sync rirData with formData.roomInRoofData when RIR tab becomes active
  useEffect(() => {
    console.log('🔴 SYNC EFFECT RUNNING:', {
      isOpen,
      currentSection,
      hasFormDataRoomInRoof: !!formData.roomInRoofData,
      formDataDiagramType: formData.roomInRoofData?.diagramType
    });
    
    if (!isOpen || currentSection !== 'room-in-roof') {
      console.log('⛔ SYNC SKIPPED:', { isOpen, currentSection });
      return;
    }
    
    const sourceData = formData.roomInRoofData;
    
    if (sourceData && sourceData.diagramType) {
      console.log('🔄 SITE NOTES: Syncing rirData from formData:', {
        diagramType: sourceData.diagramType,
        currentSection
      });
      
      // Sync to ensure we have latest data
      setRirData(sourceData);
    } else {
      console.log('⚠️ NO SOURCE DATA TO SYNC:', { hasSourceData: !!sourceData, hasDiagramType: !!sourceData?.diagramType });
    }
  }, [isOpen, currentSection, formData.roomInRoofData]);
  
  // Log when modal opens with RIR data for debugging
  useEffect(() => {
    if (isOpen && (formData.roomInRoofData || migratedInitialData.formData.roomInRoofData)) {
      console.log('📥 SITE NOTES: RIR page opened with data:', {
        diagramType: rirData.diagramType,
        hasMeasurements: !!rirData.measurements?.length
      });
    }
  }, [isOpen]);

  // Handle elevation data changes
  const handleElevationDataChange = useCallback((elevationId: string | number, data: {
    wallArea: number;
    untreatableArea: number;
    treatableArea: number;
    dimensions: {
      height: number;
      width: number;
    };
    elements: {
      doors: Array<{
        id: string;
        height: number;
        width: number;
      }>;
      windows: Array<{
        id: string;
        height: number;
        width: number;
      }>;
      airVents: Array<{
        id: string;
      }>;
    };
    placedElements?: Array<{
      id: string;
      type: string;
      x: number;
      y: number;
      elevation: string;
      height?: number;
      width?: number;
    }>;
    wallInsulationType?: string;
    buildingHeight?: number;
  }) => {
    setElevationData(prev => ({
      ...prev,
      [elevationId]: data
    }));
  }, []);

  // Handle Room in Roof data changes
  const handleRirDataChange = useCallback((data: typeof rirData) => {
    console.log('🏗️ SITE NOTES: RIR data changed:', {
      diagramType: data.diagramType,
      hasDiagramImage: !!data.diagramImage,
      imageLength: data.diagramImage?.length,
      measurementsCount: data.measurements?.length
    });
    setRirData(data);
    
    // Update formData with RIR data for persistence
    setFormData(prev => {
      const updated = {
        ...prev,
        roomInRoofData: data
      };
      
      // Trigger debounced autosave with updated data
      if (debouncedOnDataChange.current && hasInitialized.current) {
        debouncedOnDataChange.current({
          formData: updated,
          floors,
          heatingSystems
        });
      }
      
      return updated;
    });
  }, [floors, heatingSystems]);

  // Calculate global totals from all elevations
  const globalCalculations = useMemo(() => {
    const allElevationData = Object.values(elevationData);
    
    const totalDwellingArea = allElevationData.reduce((sum, data) => sum + data.wallArea, 0);
    const totalUntreatableArea = allElevationData.reduce((sum, data) => sum + data.untreatableArea, 0);
    const totalTreatableArea = allElevationData.reduce((sum, data) => sum + data.treatableArea, 0);
    const totalWallAreaWholeHouse = totalDwellingArea; // Same as total dwelling area
    const poptPercentage = totalWallAreaWholeHouse > 0 ? (totalTreatableArea / totalWallAreaWholeHouse) * 100 : 0;

    return {
      totalDwellingArea,
      totalUntreatableArea,
      totalTreatableArea,
      totalWallAreaWholeHouse,
      poptPercentage
    };
  }, [elevationData]);

  // Effect to save detailed elevation data to formData for export
  useEffect(() => {
    if (Object.keys(elevationData).length > 0) {
      // Update formData with detailed elevation information
      const updatedElevations = formData.wallInsulationElevations.map(elevation => {
        const elevationDataForId = elevationData[elevation.id];
        if (elevationDataForId) {
          return {
            ...elevation,
            dimensions: elevationDataForId.dimensions,
            elements: elevationDataForId.elements,
            calculations: {
              totalWallArea: elevationDataForId.wallArea,
              totalElementsArea: elevationDataForId.untreatableArea,
              treatableArea: elevationDataForId.treatableArea
            },
            placedElements: elevationDataForId.placedElements, // Include exact placement data for PDF
            wallInsulationType: elevationDataForId.wallInsulationType // Include wall insulation type
          };
        }
        return elevation;
      });

      setFormData(prev => ({
        ...prev,
        wallInsulationElevations: updatedElevations,
        wallInsulationGlobalCalculations: globalCalculations
      }));

      // Trigger data change for autosave
      if (debouncedOnDataChange.current) {
        debouncedOnDataChange.current({
          formData: {
            ...formData,
            wallInsulationElevations: updatedElevations,
            wallInsulationGlobalCalculations: globalCalculations
          },
          floors,
          heatingSystems
        });
      }
    }
  }, [elevationData, globalCalculations]);

  const sections: Section[] = [
    { id: 'basic', label: 'Basic Information', icon: Home },
    { id: 'property', label: 'Property Details', icon: Building },
    { id: 'windows', label: 'Windows', icon: RectangleHorizontal },
    { id: 'heating', label: 'Heating System', icon: Thermometer },
    { id: 'ventilation', label: 'Ventilation', icon: Wind },
    { id: 'utilities', label: 'Doors & Utilities', icon: Zap },
    { id: 'rooms', label: 'Rooms', icon: Ruler },
    { id: 'wall-insulation', label: 'Wall Insulation', icon: Settings },
    { id: 'room-in-roof', label: 'Room in Roof', icon: Home },
    { id: 'evidence', label: 'Evidence', icon: Camera },
    { id: 'additional', label: 'Additional Info', icon: Edit3 },
  ];

  // Track initialization to prevent initial load from triggering autosave
  const hasInitialized = useRef(false);
  
  // Track auto-population to prevent loops
  const lastAutoPopulationRef = useRef<string | null>(null);

  // Create a stable debounced function
  const debouncedOnDataChange = useRef<(data: SiteNotesData) => void>();

  // Initialize debounced function once - balanced timing for reliable autosave
  useEffect(() => {
    debouncedOnDataChange.current = debounce((data: SiteNotesData) => {
  
      if (hasInitialized.current && !isSyncingAddressRef.current) {
        onDataChange(data);
      }
    }, 500); // Faster response - 500ms provides good balance between responsiveness and avoiding excessive saves
  }, []); // No dependencies - create once

  // Ref to track if we're in a batch operation (like room deletion)
  const isBatchOperationRef = useRef(false);
  const batchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Ref to track if we're in a delete operation (prevents autosave conflicts)
  const isDeletingRef = useRef(false);
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Removed manual editing tracking - we now sync only on blur events

  // Sync to floor plan when user finishes editing (onBlur)
  const syncWindowToFloorPlan = (roomName: string, floorId: number, windowIndex: number, dimensions: any) => {
    if (onWindowDimensionSync) {
      console.log(`🪟 Syncing to floor plan after field edit: ${roomName}`);
      onWindowDimensionSync(roomName, floorId, windowIndex, dimensions);
    }
  };

  // Handle blur events for single window dimensions
  const handleSingleWindowBlur = (floorId: number, roomId: number, field: string) => {
    const targetFloor = floors.find(f => f.id === floorId);
    if (targetFloor) {
      const targetRoom = targetFloor.rooms.find(r => r.id === roomId);
      if (targetRoom && targetRoom.name) {
        const dimensions = {
          width: targetRoom.windowWidth ? parseFloat(targetRoom.windowWidth) * 1000 : 0,
          height: targetRoom.windowHeight ? parseFloat(targetRoom.windowHeight) * 1000 : 0,
          trickleVents: targetRoom.windowTrickleVents === 'Yes',
          orientation: (targetRoom as any).windowOrientation || '',
          isDraughtProofed: (targetRoom as any).windowDraughtProofed === 'Yes',
          windowType: (targetRoom as any).windowType || undefined,
          glazingGap: (targetRoom as any).windowGlazingGap || undefined,
          glazingType: (targetRoom as any).windowGlazingType || undefined,
          windowCategory: (targetRoom as any).windowCategory || undefined
        };
        syncWindowToFloorPlan(targetRoom.name, floorId, 0, dimensions);
      }
    }
  };

  // Handle blur events for multiple window dimensions
  const handleMultipleWindowBlur = (floorId: number, roomId: number, windowId: number, field: string) => {
    const targetFloor = floors.find(f => f.id === floorId);
    if (targetFloor) {
      const targetRoom = targetFloor.rooms.find(r => r.id === roomId);
      if (targetRoom && targetRoom.name && targetRoom.windows) {
        const targetWindow = targetRoom.windows.find(w => w.id === windowId);
        if (targetWindow) {
          const dimensions = {
            width: targetWindow.width ? parseFloat(targetWindow.width) * 1000 : 0,
            height: targetWindow.height ? parseFloat(targetWindow.height) * 1000 : 0,
            trickleVents: (typeof targetWindow.trickleVents === 'boolean' && targetWindow.trickleVents) || (typeof targetWindow.trickleVents === 'string' && targetWindow.trickleVents === 'Yes'),
            orientation: targetWindow.orientation || '',
            isDraughtProofed: (targetWindow as any).isDraughtProofed || false,
            windowType: (targetWindow as any).windowType || undefined,
            glazingGap: (targetWindow as any).glazingGap || undefined,
            glazingType: (targetWindow as any).glazingType || undefined,
            windowCategory: (targetWindow as any).windowCategory || undefined
          };
          syncWindowToFloorPlan(targetRoom.name, floorId, windowId - 1, dimensions);
        }
      }
    }
  };

  // Trigger autosave immediately (for structural changes)
  const triggerImmediateAutosave = () => {
    console.log('⚡ Immediate autosave triggered'); // Debug log
    
    // If we're in a delete operation, skip autosave to prevent conflicts
    if (isDeletingRef.current) {
      console.log('🗑️ Skipping autosave during delete operation');
      return;
    }
    
    // If we're in a batch operation, debounce the autosave
    if (isBatchOperationRef.current) {
      console.log('🔄 Batching autosave - debouncing rapid changes');
      
      // Clear existing timeout
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
      }
      
      // Set new timeout to trigger autosave after batch operation completes
      batchTimeoutRef.current = setTimeout(() => {
        console.log('✅ Batch operation complete - triggering final autosave');
        isBatchOperationRef.current = false;
        // Ensure we're not in a deletion state before triggering autosave
        if (!isDeletingRef.current) {
          triggerImmediateAutosave();
        }
      }, 100); // 100ms debounce
      
      return;
    }
    
    if (hasInitialized.current && !isSyncingAddressRef.current) {
      console.log('💾 Processing immediate autosave with current data');
      const currentData: SiteNotesData = { 
        formData, 
        floors,
        heatingSystems: heatingSystems.length > 0 ? heatingSystems : undefined,
        selectedPCDBSystem: selectedPCDBSystem ? {
          id: selectedPCDBSystem.id,
          brand: selectedPCDBSystem.brand,
          model: selectedPCDBSystem.model,
          model_qualifier: selectedPCDBSystem.model_qualifier,
          type: selectedPCDBSystem.type,
          fuel: selectedPCDBSystem.fuel,
          main_type: selectedPCDBSystem.main_type,
          output_power_kw: selectedPCDBSystem.output_power_kw,
          sap_winter_seasonal_efficiency: selectedPCDBSystem.sap_winter_seasonal_efficiency,
          sap_summer_seasonal_efficiency: selectedPCDBSystem.sap_summer_seasonal_efficiency,
          efficiency_category: selectedPCDBSystem.efficiency_category,
          condensing: selectedPCDBSystem.condensing,
          flue_type: selectedPCDBSystem.flue_type,
          fan_assisted: selectedPCDBSystem.fan_assisted,
          year_of_commission: selectedPCDBSystem.year_of_commission,
          features: selectedPCDBSystem.features,
          certifications: selectedPCDBSystem.certifications,
          pcdb_reference: selectedPCDBSystem.pcdb_reference,
          boiler_id: selectedPCDBSystem.boiler_id,
          last_updated: selectedPCDBSystem.last_updated,
          is_discontinued: selectedPCDBSystem.is_discontinued,
          replacement_models: selectedPCDBSystem.replacement_models,
          installation_type: selectedPCDBSystem.installation_type
        } : undefined
      };
      onDataChange(currentData);
    }
  };

  // Function to start a batch operation (prevents rapid autosave triggers)
  const startBatchOperation = () => {
    console.log('🔄 Starting batch operation - autosave will be debounced');
    isBatchOperationRef.current = true;
  };

  // Handle elevation count changes for wall insulation
  const handleElevationCountChange = (newCount: string) => {
    const count = parseInt(newCount) || 0;
    const currentElevations = formData.wallInsulationElevations || [];
    
    let newElevations = [...currentElevations];
    
    if (count > currentElevations.length) {
      // Add new elevations
      for (let i = currentElevations.length; i < count; i++) {
        newElevations.push({
          id: i + 1,
          type: ''
        });
      }
    } else if (count < currentElevations.length) {
      // Remove excess elevations
      newElevations = newElevations.slice(0, count);
      
      // 🔧 CLEANUP FIX: Remove elevation data for deleted elevations
      const validElevationIds = new Set(newElevations.map(e => e.id.toString()));
      setElevationData(prev => {
        const cleaned: typeof prev = {};
        Object.keys(prev).forEach(key => {
          if (validElevationIds.has(key)) {
            cleaned[key] = prev[key];
          }
        });
        console.log(`🧹 Cleaned elevation data: removed ${Object.keys(prev).length - Object.keys(cleaned).length} stale entries`);
        return cleaned;
      });
    }
    
    setFormData(prev => ({
      ...prev,
      wallInsulationElevationsCount: newCount,
      wallInsulationElevations: newElevations
    }));
    
    // Trigger data change for autosave
    if (debouncedOnDataChange.current) {
      debouncedOnDataChange.current({
        formData: {
          ...formData,
          wallInsulationElevationsCount: newCount,
          wallInsulationElevations: newElevations
        },
        floors,
        heatingSystems
      });
    }
  };

  // Handle individual elevation type changes
  const handleElevationTypeChange = (elevationId: number, newType: string) => {
    const updatedElevations = formData.wallInsulationElevations.map(elevation =>
      elevation.id === elevationId
        ? { ...elevation, type: newType }
        : elevation
    );
    
    setFormData(prev => ({
      ...prev,
      wallInsulationElevations: updatedElevations
    }));
    
    // Trigger data change for autosave
    if (debouncedOnDataChange.current) {
      debouncedOnDataChange.current({
        formData: {
          ...formData,
          wallInsulationElevations: updatedElevations
        },
        floors,
        heatingSystems
      });
    }
  };

  // Load initial data when component first mounts
  useEffect(() => {
    if (!hasInitialized.current) {
      // First time initialization - sync address from prop if form data doesn't have it
      const initialFormData = {
        ...initialData.formData,
        address: initialData.formData.address || propertyAddress || ''
      };
      
      setFormData(initialFormData);
      setFloors(initialData.floors && initialData.floors.length > 0 
        ? initialData.floors 
        : [
            {
              id: 1,
              floorLevel: 'ground floor',
              buildingType: 'main building',
              height: '',
              floorArea: '',
              hlp: '',
              pw: '',
              rooms: []
            }
          ]
      );
      setHeatingSystems(initialData.heatingSystems || []);
      
      // Load PCDB system data if available
      if (initialData.selectedPCDBSystem) {
        setSelectedPCDBSystem(initialData.selectedPCDBSystem as PCDBHeatingSystem);
        setIsManualHeatingEntry(false); // Set to PCDB mode
        console.log('🔥 Setting PCDB system during initialization:', {
          brand: initialData.selectedPCDBSystem.brand,
          model: initialData.selectedPCDBSystem.model,
          id: initialData.selectedPCDBSystem.id
        });
      }
      // Note: Do NOT set isManualHeatingEntry to true here - it should respect the saved state from heating systems
      
      hasInitialized.current = true;
      console.log('🎯 Site notes initialized with initial data', {
        hasSelectedPCDBSystem: !!initialData.selectedPCDBSystem,
        isManualHeatingEntry: !initialData.selectedPCDBSystem
      });
    }
  }, []); // Only run on mount, not on initialData changes

  // Update formData address when propertyAddress prop changes from parent component
  useEffect(() => {
    const currentCombinedAddress = combineAddressFields(formData.address);
    if (propertyAddress && propertyAddress !== currentCombinedAddress && hasInitialized.current) {
      console.log('🔄 Syncing address from prop:', propertyAddress);
      isSyncingAddressRef.current = true;
      setFormData(prev => ({
        ...prev,
        address: parseAddressFromString(propertyAddress)
      }));
      // Reset flag immediately after update
      setTimeout(() => {
        isSyncingAddressRef.current = false;
      }, 10);
    }
  }, [propertyAddress]); // Only monitor prop changes, not form data changes

  // Auto-populate form fields when PCDB system is loaded (for re-opening saved data)
  useEffect(() => {
    if (hasInitialized.current && selectedPCDBSystem) {
      // Create a key to track this auto-population
      const populationKey = `${selectedPCDBSystem.id}-${selectedPCDBSystem.brand}-${selectedPCDBSystem.model}`;
      
      // Skip if we already auto-populated for this exact PCDB system
      if (lastAutoPopulationRef.current === populationKey) {
        return;
      }
      
      console.log('🔍 Auto-populate check:', {
        hasInitialized: hasInitialized.current,
        selectedPCDBSystem: !!selectedPCDBSystem,
        populationKey,
        lastPopulationKey: lastAutoPopulationRef.current,
        currentFormData: {
          heatingType: formData.heatingType,
          heatingBrand: formData.heatingBrand,
          heatingModel: formData.heatingModel,
          heatingSerialNumber: formData.heatingSerialNumber,
          heatingSystemType: formData.heatingSystemType
        },
        pcdbSystemData: {
          type: selectedPCDBSystem.type,
          brand: selectedPCDBSystem.brand,
          model: selectedPCDBSystem.model,
          pcdb_reference: selectedPCDBSystem.pcdb_reference,
          installation_type: selectedPCDBSystem.installation_type
        }
      });
      
      // Mark this auto-population as completed
      lastAutoPopulationRef.current = populationKey;
      
      // Always populate from PCDB system when it exists (PCDB data takes priority)
      setFormData(prev => ({
        ...prev,
        heatingType: selectedPCDBSystem.type,
        heatingBrand: selectedPCDBSystem.brand,
        heatingModel: selectedPCDBSystem.model,
        heatingSerialNumber: selectedPCDBSystem.pcdb_reference || prev.heatingSerialNumber,
        // Map installation type to system type
        heatingSystemType: selectedPCDBSystem.installation_type === 'combi' ? 'combi-boiler' : 
                          selectedPCDBSystem.installation_type === 'system' ? 'system-boiler' :
                          selectedPCDBSystem.installation_type === 'regular' ? 'regular-boiler' : 
                          selectedPCDBSystem.type === 'heat-pump' ? 'heat-pump' : 
                          prev.heatingSystemType || 'radiators'
      }));
      
      console.log('💾 Auto-populated form fields from PCDB system');
    }
  }, [selectedPCDBSystem]); // Removed hasInitialized.current to prevent loop

  // REMOVED: This useEffect was causing infinite loops by continuously updating floors
  // The floors refresh is now handled only on modal open (see useEffect below)

  // 🔄 REFRESH WINDOWS DATA: Update floors when modal opens
  // This ensures windows section always shows the latest data from the floor plan
  useEffect(() => {
    if (isOpen && hasInitialized.current && initialData.floors && initialData.floors.length > 0) {
      const currentFloorsString = JSON.stringify(floors);
      const newFloorsString = JSON.stringify(initialData.floors);
      
      if (currentFloorsString !== newFloorsString) {
        console.log('🔄 Refreshing floors data on modal open', {
          currentFloorCount: floors.length,
          newFloorCount: initialData.floors.length,
          currentWindowCount: floors.flatMap(f => f.rooms.flatMap(r => r.windows || [])).length,
          newWindowCount: initialData.floors.flatMap(f => f.rooms.flatMap(r => r.windows || [])).length
        });
        
        setFloors(initialData.floors);
      }
    }
  }, [isOpen]);

  // 🔄 REFRESH HEATING SYSTEMS DATA: Update heating systems when modal opens
  // This ensures heating system manual entry state persists across open/close
  useEffect(() => {
    if (isOpen && hasInitialized.current && initialData.heatingSystems) {
      const currentSystemsString = JSON.stringify(heatingSystems);
      const newSystemsString = JSON.stringify(initialData.heatingSystems);
      
      if (currentSystemsString !== newSystemsString) {
        console.log('🔄 Refreshing heating systems from saved data');
        setHeatingSystems(initialData.heatingSystems);
      }
    }
  }, [isOpen, initialData.heatingSystems]);

  // 🔄 REFRESH ON SECTION CHANGE: Update floors when navigating to windows/rooms section
  // This catches any updates that happened while on another section
  useEffect(() => {
    if (isOpen && hasInitialized.current && (currentSection === 'windows' || currentSection === 'rooms')) {
      if (initialData.floors && initialData.floors.length > 0) {
        const currentFloorsString = JSON.stringify(floors);
        const newFloorsString = JSON.stringify(initialData.floors);
        
        if (currentFloorsString !== newFloorsString) {
          console.log('🔄 Refreshing floors data for section', {
            section: currentSection,
            newWindowCount: initialData.floors.flatMap(f => f.rooms.flatMap(r => r.windows || [])).length
          });
          
          setFloors(initialData.floors);
        }
      }
    }
  }, [currentSection]);

  // Update formData when external sync operations update the data
  useEffect(() => {
    if (hasInitialized.current && initialData.formData && !isUpdatingFromExternalSync.current) {
      const currentFormDataString = JSON.stringify(formData);
      const newFormDataString = JSON.stringify(initialData.formData);
      
      // Only update if formData has actually changed (avoid infinite loops)
      if (currentFormDataString !== newFormDataString) {
        console.log('🔄 Updating formData from external sync operation', {
          windowChanges: {
            before: {
              totalWindows: formData.totalWindows,
              windowsMissingTrickleVents: formData.windowsMissingTrickleVents,
              missingTrickleVentsLocation: formData.missingTrickleVentsLocation
            },
            after: {
              totalWindows: initialData.formData.totalWindows,
              windowsMissingTrickleVents: initialData.formData.windowsMissingTrickleVents,
              missingTrickleVentsLocation: initialData.formData.missingTrickleVentsLocation
            }
          },
          lightingChanges: {
            before: {
              totalLightsX: formData.totalLightsX,
              totalLightsO: formData.totalLightsO
            },
            after: {
              totalLightsX: initialData.formData.totalLightsX,
              totalLightsO: initialData.formData.totalLightsO
            }
          }
        });
        
        // Set flag to prevent infinite loops during external sync
        isUpdatingFromExternalSync.current = true;
        setFormData(initialData.formData);
        
        // Force a re-render to ensure UI updates
        setRefreshCounter(prev => prev + 1);
        
        // Reset flag after a brief delay to allow the update to settle
        setTimeout(() => {
          isUpdatingFromExternalSync.current = false;
        }, 100);
      }
    }
  }, [initialData.formData]); // Watch for changes in initialData.formData





  // Trigger debounced autosave when data changes
  useEffect(() => {
    
    if (hasInitialized.current && debouncedOnDataChange.current && !isUpdatingFromExternalSync.current) {
      const currentData: SiteNotesData = { 
        formData, 
        floors,
        heatingSystems: heatingSystems.length > 0 ? heatingSystems : undefined,
        selectedPCDBSystem: selectedPCDBSystem ? {
          id: selectedPCDBSystem.id,
          brand: selectedPCDBSystem.brand,
          model: selectedPCDBSystem.model,
          model_qualifier: selectedPCDBSystem.model_qualifier,
          type: selectedPCDBSystem.type,
          fuel: selectedPCDBSystem.fuel,
          main_type: selectedPCDBSystem.main_type,
          output_power_kw: selectedPCDBSystem.output_power_kw,
          sap_winter_seasonal_efficiency: selectedPCDBSystem.sap_winter_seasonal_efficiency,
          sap_summer_seasonal_efficiency: selectedPCDBSystem.sap_summer_seasonal_efficiency,
          efficiency_category: selectedPCDBSystem.efficiency_category,
          condensing: selectedPCDBSystem.condensing,
          flue_type: selectedPCDBSystem.flue_type,
          fan_assisted: selectedPCDBSystem.fan_assisted,
          year_of_commission: selectedPCDBSystem.year_of_commission,
          features: selectedPCDBSystem.features,
          certifications: selectedPCDBSystem.certifications,
          pcdb_reference: selectedPCDBSystem.pcdb_reference,
          boiler_id: selectedPCDBSystem.boiler_id,
          last_updated: selectedPCDBSystem.last_updated,
          is_discontinued: selectedPCDBSystem.is_discontinued,
          replacement_models: selectedPCDBSystem.replacement_models,
          installation_type: selectedPCDBSystem.installation_type
        } : undefined
      };
      debouncedOnDataChange.current(currentData);
    }
  }, [formData, floors, heatingSystems, selectedPCDBSystem]);

  // Reset initialization flag when modal closes - but don't reset hasInitialized to preserve data
  useEffect(() => {
    if (!isOpen) {
      // Before clearing operations, trigger one final immediate save if there are pending changes
      if (hasInitialized.current && !isSyncingAddressRef.current) {
    
        const currentData: SiteNotesData = { 
          formData, 
          floors,
          heatingSystems: heatingSystems.length > 0 ? heatingSystems : undefined,
          selectedPCDBSystem: selectedPCDBSystem ? {
            id: selectedPCDBSystem.id,
            brand: selectedPCDBSystem.brand,
            model: selectedPCDBSystem.model,
            model_qualifier: selectedPCDBSystem.model_qualifier,
            type: selectedPCDBSystem.type,
            fuel: selectedPCDBSystem.fuel,
            main_type: selectedPCDBSystem.main_type,
            output_power_kw: selectedPCDBSystem.output_power_kw,
            sap_winter_seasonal_efficiency: selectedPCDBSystem.sap_winter_seasonal_efficiency,
            sap_summer_seasonal_efficiency: selectedPCDBSystem.sap_summer_seasonal_efficiency,
            efficiency_category: selectedPCDBSystem.efficiency_category,
            condensing: selectedPCDBSystem.condensing,
            flue_type: selectedPCDBSystem.flue_type,
            fan_assisted: selectedPCDBSystem.fan_assisted,
            year_of_commission: selectedPCDBSystem.year_of_commission,
            features: selectedPCDBSystem.features,
            certifications: selectedPCDBSystem.certifications,
            pcdb_reference: selectedPCDBSystem.pcdb_reference,
            boiler_id: selectedPCDBSystem.boiler_id,
            last_updated: selectedPCDBSystem.last_updated,
            is_discontinued: selectedPCDBSystem.is_discontinued,
            replacement_models: selectedPCDBSystem.replacement_models,
            installation_type: selectedPCDBSystem.installation_type
          } : undefined
        };
        onDataChange(currentData);
      }
      
      // Clear any pending delete operations and batch operations
      isDeletingRef.current = false;
      isBatchOperationRef.current = false;
      if (deleteTimeoutRef.current) {
        clearTimeout(deleteTimeoutRef.current);
        deleteTimeoutRef.current = null;
      }
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
        batchTimeoutRef.current = null;
      }
  
    }
  }, [isOpen, formData, floors, heatingSystems, selectedPCDBSystem]); // Removed onDataChange to prevent infinite loop

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      // Before cleanup, trigger one final immediate save if there are pending changes
      if (hasInitialized.current && !isSyncingAddressRef.current) {

        const currentData: SiteNotesData = { 
          formData, 
          floors,
          heatingSystems: heatingSystems.length > 0 ? heatingSystems : undefined,
          selectedPCDBSystem: selectedPCDBSystem ? {
            id: selectedPCDBSystem.id,
            brand: selectedPCDBSystem.brand,
            model: selectedPCDBSystem.model,
            model_qualifier: selectedPCDBSystem.model_qualifier,
            type: selectedPCDBSystem.type,
            fuel: selectedPCDBSystem.fuel,
            main_type: selectedPCDBSystem.main_type,
            output_power_kw: selectedPCDBSystem.output_power_kw,
            sap_winter_seasonal_efficiency: selectedPCDBSystem.sap_winter_seasonal_efficiency,
            sap_summer_seasonal_efficiency: selectedPCDBSystem.sap_summer_seasonal_efficiency,
            efficiency_category: selectedPCDBSystem.efficiency_category,
            condensing: selectedPCDBSystem.condensing,
            flue_type: selectedPCDBSystem.flue_type,
            fan_assisted: selectedPCDBSystem.fan_assisted,
            year_of_commission: selectedPCDBSystem.year_of_commission,
            features: selectedPCDBSystem.features,
            certifications: selectedPCDBSystem.certifications,
            pcdb_reference: selectedPCDBSystem.pcdb_reference,
            boiler_id: selectedPCDBSystem.boiler_id,
            last_updated: selectedPCDBSystem.last_updated,
            is_discontinued: selectedPCDBSystem.is_discontinued,
            replacement_models: selectedPCDBSystem.replacement_models,
            installation_type: selectedPCDBSystem.installation_type
          } : undefined
        };
        onDataChange(currentData);
      }
      
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
        batchTimeoutRef.current = null;
      }
      if (deleteTimeoutRef.current) {
        clearTimeout(deleteTimeoutRef.current);
        deleteTimeoutRef.current = null;
      }
      // Reset flags on cleanup
      isDeletingRef.current = false;
      isBatchOperationRef.current = false;

    };
  }, []);

  // Debug heating systems state changes
  useEffect(() => {
    // console.log('🔥 heatingSystems state updated to (length: ' + heatingSystems.length + '):', heatingSystems);
  }, [heatingSystems]);

  // Handle Save & Close - ensure all current data is captured before saving
  const handleSaveAndClose = () => {
    console.log('[SiteNotes] Save & Close clicked - ensuring all data is captured');
    
    // Force immediate data sync to ensure all current form state is captured
    const currentData: SiteNotesData = { 
      formData, 
      floors,
      heatingSystems: heatingSystems.length > 0 ? heatingSystems : undefined,
      selectedPCDBSystem: selectedPCDBSystem ? {
        id: selectedPCDBSystem.id,
        brand: selectedPCDBSystem.brand,
        model: selectedPCDBSystem.model,
        model_qualifier: selectedPCDBSystem.model_qualifier,
        type: selectedPCDBSystem.type,
        fuel: selectedPCDBSystem.fuel,
        main_type: selectedPCDBSystem.main_type,
        output_power_kw: selectedPCDBSystem.output_power_kw,
        sap_winter_seasonal_efficiency: selectedPCDBSystem.sap_winter_seasonal_efficiency,
        sap_summer_seasonal_efficiency: selectedPCDBSystem.sap_summer_seasonal_efficiency,
        efficiency_category: selectedPCDBSystem.efficiency_category,
        condensing: selectedPCDBSystem.condensing,
        flue_type: selectedPCDBSystem.flue_type,
        fan_assisted: selectedPCDBSystem.fan_assisted,
        year_of_commission: selectedPCDBSystem.year_of_commission,
        features: selectedPCDBSystem.features,
        certifications: selectedPCDBSystem.certifications,
        pcdb_reference: selectedPCDBSystem.pcdb_reference,
        boiler_id: selectedPCDBSystem.boiler_id,
        last_updated: selectedPCDBSystem.last_updated,
        is_discontinued: selectedPCDBSystem.is_discontinued,
        replacement_models: selectedPCDBSystem.replacement_models,
        installation_type: selectedPCDBSystem.installation_type
      } : undefined
    };
    
    console.log('[SiteNotes] Final data being sent:', currentData);
    
    // Send the final data immediately
    onDataChange(currentData);
    
    // Call the parent's save handler
    onSave();
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Handle property address changes specially
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1]; // Extract field name (houseNumber, street, town, postcode)
      
      // Update local form data with nested address structure (immediately for UI responsiveness)
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
      
      return; // Don't trigger save on every keystroke - wait for onBlur
    }
    
    // Handle special field types
    let processedValue: any = value;
    
    // Handle efficiency and temperature fields (convert to numbers)
    if (name === 'winterEfficiency' || name === 'summerEfficiency' || name === 'flowTemperature') {
      processedValue = value ? parseFloat(value) : undefined;
    }
    
    // Handle boolean fields
    if (name === 'isCondensing' || name === 'isLowTemperature') {
      processedValue = value === 'yes' ? true : value === 'no' ? false : undefined;
    }
    
    // Update local state immediately for UI responsiveness
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    // Don't trigger save on every keystroke - wait for onBlur or form completion
  };

  // Handle field blur events (when user finishes editing a field)
  const handleFieldBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    console.log('🔔 [SiteNotes] handleFieldBlur triggered:', {
      fieldName: name,
      fieldValue: value,
      isGroundLevel: name === 'groundLevel',
      isRoofType: name === 'roofTypeRdSAP',
      isFloorType: name === 'floorType',
      currentFormDataGroundLevel: formData.groundLevel,
      currentFormDataRoofType: formData.roofTypeRdSAP,
      currentFormDataFloorType: formData.floorType
    });
    
    // Handle address fields specially - notify parent and trigger save
    if (name.startsWith('address.')) {
      if (onPropertyAddressChange) {
        // 🔧 FIX: Use current formData.address values instead of raw input value
        // The onChange handler already parsed the values correctly, so use those
        const combinedAddress = combineAddressFields(formData.address);
        
        onPropertyAddressChange(combinedAddress);
      }
    }
    
    // Trigger debounced save when user finishes editing any field
    if (debouncedOnDataChange.current) {
      const currentData: SiteNotesData = { 
        formData, 
        floors,
        heatingSystems,
        selectedPCDBSystem: selectedPCDBSystem ? {
          id: selectedPCDBSystem.id,
          brand: selectedPCDBSystem.brand,
          model: selectedPCDBSystem.model,
          type: selectedPCDBSystem.type,
          fuel: selectedPCDBSystem.fuel,
          installation_type: selectedPCDBSystem.installation_type
        } : undefined
      };
      
      console.log('💾 [SiteNotes] Triggering debounced save with data:', {
        groundLevel: currentData.formData.groundLevel,
        roofTypeRdSAP: currentData.formData.roofTypeRdSAP,
        floorType: currentData.formData.floorType
      });
      
      debouncedOnDataChange.current(currentData);
    }
  };

  // Date Built handler functions
  const handleDateBuiltChange = (index: number, field: 'dateBuilt' | 'rirDateBuilt', value: string) => {
    setFormData(prev => {
      // Always ensure we have Main and 1st Extension as minimum
      const defaultEntries = [
        { id: '1', type: 'main' as const, label: 'Main', dateBuilt: '', rirDateBuilt: '' },
        { id: '2', type: 'extension' as const, label: '1st Extension', dateBuilt: '', rirDateBuilt: '' }
      ];
      
      const currentEntries = prev.dateBuiltEntries || defaultEntries;
      const updatedEntries = [...currentEntries];
      
      // Ensure we have the minimum entries if not present
      if (updatedEntries.length < 2) {
        while (updatedEntries.length < 2) {
          if (updatedEntries.length === 0) {
            updatedEntries.push(defaultEntries[0]);
          } else {
            updatedEntries.push(defaultEntries[1]);
          }
        }
      }
      
      // Update the specific entry
      if (updatedEntries[index]) {
        updatedEntries[index] = {
          ...updatedEntries[index],
          [field]: value
        };
      }
      
      // Auto-add next extension if current extension has a build date
      if (value && index > 0) { // Only for extensions (index > 0)
        const currentEntry = updatedEntries[index];
        if (currentEntry.type === 'extension' && (currentEntry.dateBuilt || currentEntry.rirDateBuilt)) {
          // Check if we need to add the next extension
          const nextIndex = index + 1;
          if (!updatedEntries[nextIndex]) {
            const extensionNumber = updatedEntries.filter(entry => entry.type === 'extension').length + 1;
            const ordinal = extensionNumber === 1 ? '1st' : extensionNumber === 2 ? '2nd' : extensionNumber === 3 ? '3rd' : `${extensionNumber}th`;
            updatedEntries.push({
              id: Date.now().toString(),
              type: 'extension',
              label: `${ordinal} Extension`,
              dateBuilt: '',
              rirDateBuilt: ''
            });
          }
        }
      }
      
      // Manage wall construction sections based on date built entries
      const currentWallSections = prev.wallConstructionSections || [];
      let updatedWallSections = [...currentWallSections];
      
      // Add or remove wall construction sections based on date built entries
      updatedEntries.forEach(entry => {
        const hasDateBuilt = entry.dateBuilt && entry.dateBuilt !== '';
        const existingSectionIndex = updatedWallSections.findIndex(section => section.sectionId === entry.id);
        
        if (hasDateBuilt && existingSectionIndex === -1) {
          // Add new wall construction section
          updatedWallSections.push({
            sectionId: entry.id,
            sectionLabel: entry.label,
            wallType: '',
            wallInsulationType: '',
            wallInsulationThickness: '',
            partyWall: '',
            wallThickness: '',
            dryLining: ''
          });
        } else if (!hasDateBuilt && existingSectionIndex !== -1) {
          // Remove wall construction section
          updatedWallSections.splice(existingSectionIndex, 1);
        }
      });
      
      return {
        ...prev,
        dateBuiltEntries: updatedEntries,
        wallConstructionSections: updatedWallSections
      };
    });
  };

  // Wall Construction handler for specific sections
  const handleWallConstructionChange = (sectionId: string, fieldName: string, value: string) => {
    setFormData(prev => {
      const wallSections = prev.wallConstructionSections || [];
      const updatedWallSections = wallSections.map(section => {
        if (section.sectionId === sectionId) {
          return {
            ...section,
            [fieldName]: value
          };
        }
        return section;
      });
      
      return {
        ...prev,
        wallConstructionSections: updatedWallSections
      };
    });
  };



  const addFloor = () => {
    // Determine the next floor level based on existing floors
    const floorLevelOrder = ['basement', 'ground floor', 'first floor', 'second floor', 'third floor', 'fourth floor', 'fifth floor'];
    const existingLevels = floors.map(f => f.floorLevel);
    
    // Find the highest floor level and increment
    let nextFloorLevel = 'ground floor'; // default
    for (let i = floorLevelOrder.length - 1; i >= 0; i--) {
      if (existingLevels.includes(floorLevelOrder[i])) {
        // Found the highest existing level, get the next one
        const nextIndex = i + 1;
        if (nextIndex < floorLevelOrder.length) {
          nextFloorLevel = floorLevelOrder[nextIndex];
        } else {
          nextFloorLevel = floorLevelOrder[floorLevelOrder.length - 1]; // Stay at highest if at max
        }
        break;
      }
    }
    
    const newFloor = {
      id: Math.max(...floors.map(f => f.id)) + 1,
      floorLevel: nextFloorLevel,
      buildingType: 'main building',
      height: '',
      floorArea: '',
      hlp: '',
      pw: '',
      rooms: [] // Start with no default rooms
    };
    setFloors(prev => [...prev, newFloor]);
    // Structural changes will be picked up by debounced autosave
    // No need for immediate save - let debouncing handle it
  };

  const removeFloor = (id: number) => {
    if (floors.length > 1) {
      // Get the floor being deleted to capture its values
      const floorToDelete = floors.find(f => f.id === id);
      
      if (floorToDelete) {
        console.log(`🗑️ Deleting floor: ${floorToDelete.buildingType} ${floorToDelete.floorLevel}`, {
          area: floorToDelete.floorArea,
          hlp: floorToDelete.hlp,
          pw: floorToDelete.pw
        });
        
        // Parse the values from the deleted floor
        const deletedValues = {
          area: parseFloat(floorToDelete.floorArea?.replace(' m²', '')) || 0,
          hlp: parseFloat(floorToDelete.hlp?.replace(' m', '')) || 0,
          pw: parseFloat(floorToDelete.pw?.replace(' m', '')) || 0
        };
        
        // Only proceed with recalculation if the deleted floor had significant values
        if (deletedValues.area > 0 || deletedValues.hlp > 0 || deletedValues.pw > 0) {
          setFloors(prev => {
            const updatedFloors = prev.filter(floor => floor.id !== id);
            
            if (floorToDelete.buildingType === 'main building') {
              // If deleting main building, distribute values to extensions or create new main building
              const sameFloorExtensions = updatedFloors.filter(f => 
                f.floorLevel === floorToDelete.floorLevel && f.buildingType !== 'main building'
              );
              
              if (sameFloorExtensions.length > 0) {
                // Distribute main building values equally among extensions
                const valuePerExtension = {
                  area: deletedValues.area / sameFloorExtensions.length,
                  hlp: deletedValues.hlp / sameFloorExtensions.length,
                  pw: deletedValues.pw / sameFloorExtensions.length
                };
                
                console.log(`📊 Distributing main building values among ${sameFloorExtensions.length} extensions:`, valuePerExtension);
                
                return updatedFloors.map(floor => {
                  if (sameFloorExtensions.some(ext => ext.id === floor.id)) {
                    const currentArea = parseFloat(floor.floorArea?.replace(' m²', '')) || 0;
                    const currentHlp = parseFloat(floor.hlp?.replace(' m', '')) || 0;
                    const currentPw = parseFloat(floor.pw?.replace(' m', '')) || 0;
                    
                    return {
                      ...floor,
                      floorArea: (currentArea + valuePerExtension.area).toFixed(2),
                      hlp: (currentHlp + valuePerExtension.hlp).toFixed(2),
                      pw: (currentPw + valuePerExtension.pw).toFixed(2)
                    };
                  }
                  return floor;
                });
              } else {
                // No extensions to distribute to, just remove the main building
                console.log(`⚠️ No extensions found to distribute main building values to`);
                return updatedFloors;
              }
            } else {
              // If deleting an extension, add its values back to the main building
              const mainBuildingFloor = updatedFloors.find(f => 
                f.floorLevel === floorToDelete.floorLevel && f.buildingType === 'main building'
              );
              
              if (mainBuildingFloor) {
                console.log(`📊 Adding extension values back to main building:`, deletedValues);
                
                return updatedFloors.map(floor => {
                  if (floor.id === mainBuildingFloor.id) {
                    const currentArea = parseFloat(floor.floorArea?.replace(' m²', '')) || 0;
                    const currentHlp = parseFloat(floor.hlp?.replace(' m', '')) || 0;
                    const currentPw = parseFloat(floor.pw?.replace(' m', '')) || 0;
                    
                    const newValues = {
                      area: currentArea + deletedValues.area,
                      hlp: currentHlp + deletedValues.hlp,
                      pw: currentPw + deletedValues.pw
                    };
                    
                    console.log(`✅ Updated main building values:`, newValues);
                    
                    return {
                      ...floor,
                      floorArea: newValues.area.toFixed(2),
                      hlp: newValues.hlp.toFixed(2),
                      pw: newValues.pw.toFixed(2)
                    };
                  }
                  return floor;
                });
              } else {
                // No main building found, create a new one with the deleted extension's values
                console.log(`🏗️ Creating new main building floor with extension values:`, deletedValues);
                
                const newMainBuildingFloor = {
                  id: Math.max(...updatedFloors.map(f => f.id), 0) + 1,
                  floorLevel: floorToDelete.floorLevel,
                  buildingType: 'main building',
                  height: floorToDelete.height || '',
                  floorArea: deletedValues.area.toFixed(2),
                  hlp: deletedValues.hlp.toFixed(2),
                  pw: deletedValues.pw.toFixed(2),
                  rooms: []
                };
                
                return [...updatedFloors, newMainBuildingFloor];
              }
            }
          });
        } else {
          // If deleted floor had no significant values, just remove it normally
          setFloors(prev => prev.filter(floor => floor.id !== id));
        }
        
        // Notify floor plan about the deletion so highlighted areas can be unmarked
        if (onFloorDelete) {
          onFloorDelete(floorToDelete.floorLevel, floorToDelete.buildingType);
        }
      } else {
        // If floor not found, just remove it normally
        setFloors(prev => prev.filter(floor => floor.id !== id));
      }
      
      // Structural changes will be picked up by debounced autosave
    }
  };

  const handleFloorChange = (id: number, field: string, value: string) => {
    setFloors(prev => prev.map(floor => 
      floor.id === id ? { ...floor, [field]: value } : floor
    ));
    // Floor changes will be picked up by debounced autosave
  };

  const addRoom = (floorId: number) => {
    setFloors(prevFloors => 
      prevFloors.map(floor => 
        floor.id === floorId 
          ? {
              ...floor,
              rooms: [
                ...floor.rooms,
                {
                  id: floor.rooms.length + 1,
                  name: `Room ${floor.rooms.length + 1}`,
                  length: '',
                  width: '',
                  height: '',
                  lowEnergyLights: '',
                  nonLowEnergyLights: '',
                  radiatorWidth: '',
                  radiatorHeight: '',
                  radiatorType: '',
                  windowWidth: '',
                  windowHeight: '',
                  windowTrickleVents: '',
                  radiators: [],
                  windows: []
                }
              ]
            }
          : floor
      )
    );
  };

  const removeRoom = (floorId: number, roomId: number) => {
    // Allow room deletion even if it's the last room on a floor (for duplicate cleanup)
    // But show a confirmation for the last room
    const targetFloor = floors.find(f => f.id === floorId);
    const isLastRoom = targetFloor && targetFloor.rooms.length === 1;
    const roomToDelete = targetFloor?.rooms.find(r => r.id === roomId);
    const roomName = roomToDelete?.name || `Room ${roomId}`;
    
    if (isLastRoom) {
      if (!window.confirm(`Are you sure you want to delete "${roomName}"? This is the last room on this floor.`)) {
        return;
      }
    }
    
    // Enhanced confirmation for rooms with data
    const hasRoomData = roomToDelete && (
      roomToDelete.length || roomToDelete.width || roomToDelete.height ||
      (roomToDelete.radiators && roomToDelete.radiators.length > 0) ||
      (roomToDelete.windows && roomToDelete.windows.length > 0) ||
      roomToDelete.lowEnergyLights || roomToDelete.nonLowEnergyLights
    );
    
    if (hasRoomData && !isLastRoom) {
      if (!window.confirm(`Are you sure you want to delete room "${roomName}"? This room contains dimension and element data that will be lost.`)) {
        return;
      }
    }
    
    // Start batch operation to prevent rapid autosave triggers
    startBatchOperation();
    
    // Log the deletion for debugging
    console.log(`🗑️ Deleting room "${roomName}" (ID: ${roomId}) from floor ${floorId}`, {
      hasData: hasRoomData,
      roomData: roomToDelete
    });
    
    setFloors(prev => prev.map(floor => {
      if (floor.id === floorId) {
        return { ...floor, rooms: floor.rooms.filter(room => room.id !== roomId) };
      }
      return floor;
    }));
    
    // Show user feedback
    console.log(`✅ Room "${roomName}" deleted from site notes on floor ${floorId}`);
    
    // 🔧 LIGHT COUNT FIX: Trigger element sync after room deletion to ensure lights are reassigned correctly
    // When a room is deleted from site notes, lights that were assigned to that room would be lost from the total count
    // By triggering a sync, lights will be reassigned based on their actual floor plan positions
    if (onSyncElements) {
      console.log(`🔄 Triggering element sync after room deletion to fix light counts`);
      onSyncElements();
    }
    
    // Note: Room deletion from site notes doesn't require floor plan sync since this is manual cleanup
    // If this room was synced from the floor plan, it will be re-synced on the next auto-sync
    // Structural changes will be picked up by debounced autosave automatically
  };

  const handleRoomChange = (floorId: number, roomId: number, field: string, value: string) => {
    // Manual editing now handled by onBlur events
    
    setFloors(prev => prev.map(floor => {
      if (floor.id === floorId) {
        return {
          ...floor,
          rooms: floor.rooms.map(room =>
            room.id === roomId ? { ...room, [field]: value } : room
          )
        };
      }
      return floor;
    }));
    
    // Handle radiator type sync to floor plan
    if (field === 'radiatorType' && onRadiatorTypeSync) {
      // Get the room name to match with floor plan elements
      const targetFloor = floors.find(f => f.id === floorId);
      const targetRoom = targetFloor?.rooms.find(r => r.id === roomId);
      
      if (targetRoom?.name) {
        console.log(`🔄 Site notes: Syncing radiator type change for room "${targetRoom.name}" to ${value}`);
        onRadiatorTypeSync(targetRoom.name, value);
      } else {
        console.log(`⚠️ Site notes: Could not find room name for floor ${floorId}, room ${roomId}`);
      }
    }
    
    // Handle single window field sync to floor plan with debouncing
    if (field.startsWith('window') && onWindowDimensionSync) {
      const targetFloor = floors.find(f => f.id === floorId);
      const targetRoom = targetFloor?.rooms.find(r => r.id === roomId);
      
      if (targetRoom?.name) {
        // Get the updated room data after the state change
        const updatedRoom = { ...targetRoom, [field]: value };
        
        // Convert values from cm to mm (floor plan uses mm)
        const dimensions = {
          width: updatedRoom.windowWidth ? parseFloat(updatedRoom.windowWidth) * 1000 : 0,
          height: updatedRoom.windowHeight ? parseFloat(updatedRoom.windowHeight) * 1000 : 0,
  
          trickleVents: updatedRoom.windowTrickleVents === 'Yes',
          orientation: (updatedRoom as any).windowOrientation || ''
        };
        
        // Sync will happen on blur - no need to sync on every keystroke
        console.log(`🪟 Window dimension updated: ${targetRoom.name}, field: ${field}, value: ${value}`);
      }
    }
    
    // Room changes will be picked up by debounced autosave
  };

  // Add radiator to room
  const addRadiator = (floorId: number, roomId: number) => {
    setFloors(prev => prev.map(floor => {
      if (floor.id === floorId) {
        return {
          ...floor,
          rooms: floor.rooms.map(room => {
            if (room.id === roomId) {
              const radiators = room.radiators || [];
              const newRadiator = {
                id: Math.max(0, ...radiators.map(r => r.id)) + 1,
                width: '',
                height: '',
                type: ''
              };
              return { ...room, radiators: [...radiators, newRadiator] };
            }
            return room;
          })
        };
      }
      return floor;
    }));
    // Changes will be picked up by debounced autosave
  };

  // Remove radiator from room
  const removeRadiator = (floorId: number, roomId: number, radiatorId: number) => {
    console.log(`🗑️ Removing radiator ${radiatorId} from room ${roomId} on floor ${floorId}`);
    
    // Set deletion flag to prevent autosave conflicts
    isDeletingRef.current = true;
    
    // Clear any existing delete timeout
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
    }
    
    // Perform the deletion immediately
    setFloors(prev => prev.map(floor => {
      if (floor.id === floorId) {
        return {
          ...floor,
          rooms: floor.rooms.map(room => {
            if (room.id === roomId) {
              const radiators = room.radiators || [];
              const updatedRadiators = radiators.filter(r => r.id !== radiatorId);
              console.log(`🔥 Radiators before: ${radiators.length}, after: ${updatedRadiators.length}`);
              return { ...room, radiators: updatedRadiators };
            }
            return room;
          })
        };
      }
      return floor;
    }));
    
    // Use a shorter delay and ensure proper cleanup
    deleteTimeoutRef.current = setTimeout(() => {
      isDeletingRef.current = false;
      console.log('💾 Triggering autosave after radiator deletion');
      triggerImmediateAutosave();
    }, 300); // Reduced delay to 300ms for better responsiveness
  };

  // Handle radiator field change
  const handleRadiatorChange = (floorId: number, roomId: number, radiatorId: number, field: string, value: string) => {
    setFloors(prev => prev.map(floor => {
      if (floor.id === floorId) {
        return {
          ...floor,
          rooms: floor.rooms.map(room => {
            if (room.id === roomId) {
              const radiators = room.radiators || [];
              return {
                ...room,
                radiators: radiators.map(r =>
                  r.id === radiatorId ? { ...r, [field]: value } : r
                )
              };
            }
            return room;
          })
        };
      }
      return floor;
    }));
    // Changes will be picked up by debounced autosave unless we're deleting
  };

  // Add window to room
  const addWindow = (floorId: number, roomId: number) => {
    setFloors(prevFloors => 
      prevFloors.map(floor => 
        floor.id === floorId 
          ? {
              ...floor,
              rooms: floor.rooms.map(room => 
                room.id === roomId 
                  ? {
                      ...room,
                      windows: [
                                        ...(room.windows || []),
                {
                  id: (room.windows || []).length + 1,
                  width: '',
                  height: '',
                  orientation: '',
                  trickleVents: false
                }
                      ]
                    }
                  : room
              )
            }
          : floor
      )
    );
  };

  // Remove window from room
  const removeWindow = (floorId: number, roomId: number, windowId: number) => {
    console.log(`🗑️ Removing window ${windowId} from room ${roomId} on floor ${floorId}`);
    
    // Set deletion flag to prevent autosave conflicts
    isDeletingRef.current = true;
    
    // Clear any existing delete timeout
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
    }
    
    // 🪟 NEW: Also remove the window from the floor plan canvas
    if (onDeleteWindowFromFloorPlan) {
      console.log(`🪟 Calling floor plan window deletion callback`);
      onDeleteWindowFromFloorPlan(floorId, roomId, windowId);
    }
    
    // Perform the deletion from site notes immediately
    setFloors(prev => prev.map(floor => {
      if (floor.id === floorId) {
        return {
          ...floor,
          rooms: floor.rooms.map(room => {
            if (room.id === roomId) {
              const windows = room.windows || [];
              const updatedWindows = windows.filter(w => w.id !== windowId);
              console.log(`🪟 Windows before: ${windows.length}, after: ${updatedWindows.length}`);
              return { ...room, windows: updatedWindows };
            }
            return room;
          })
        };
      }
      return floor;
    }));
    
    // Use a shorter delay and ensure proper cleanup
    deleteTimeoutRef.current = setTimeout(() => {
      isDeletingRef.current = false;
      console.log('💾 Triggering autosave after window deletion');
      triggerImmediateAutosave();
    }, 300); // Reduced delay to 300ms for better responsiveness
  };

  // 🪟 NEW: Remove single window (clears the single window fields)
  const removeSingleWindow = (floorId: number, roomId: number) => {
    console.log(`🗑️ Removing single window from room ${roomId} on floor ${floorId}`);
    
    // Set deletion flag to prevent autosave conflicts
    isDeletingRef.current = true;
    
    // Clear any existing delete timeout
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
    }
    
    // 🪟 FIRST: Remove the window from the floor plan canvas (before clearing site notes data)
    // For single windows, we use windowId = 1 (since single windows are treated as the first window)
    if (onDeleteWindowFromFloorPlan) {
      console.log(`🪟 Calling floor plan window deletion callback for single window`);
      onDeleteWindowFromFloorPlan(floorId, roomId, 1);
    }
    
    // THEN: Clear the single window fields from site notes
    setFloors(prev => prev.map(floor => {
      if (floor.id === floorId) {
        return {
          ...floor,
          rooms: floor.rooms.map(room => {
            if (room.id === roomId) {
              console.log(`🪟 Clearing single window fields for room ${roomId}`);
              return { 
                ...room, 
                windowWidth: '',
                windowHeight: '',
                windowTrickleVents: '',
                windowOrientation: ''
              };
            }
            return room;
          })
        };
      }
      return floor;
    }));
    
    // Use a shorter delay and ensure proper cleanup
    deleteTimeoutRef.current = setTimeout(() => {
      isDeletingRef.current = false;
      console.log('💾 Triggering autosave after single window deletion');
      triggerImmediateAutosave();
    }, 300); // Reduced delay to 300ms for better responsiveness
  };

  // Handle window field change
  const handleWindowChange = (floorId: number, roomId: number, windowId: number, field: string, value: string) => {
    // Manual editing now handled by onBlur events
    
    setFloors(prev => prev.map(floor => {
      if (floor.id === floorId) {
        return {
          ...floor,
          rooms: floor.rooms.map(room => {
            if (room.id === roomId) {
              const windows = room.windows || [];
              return {
                ...room,
                windows: windows.map(w =>
                  w.id === windowId ? { ...w, [field]: value } : w
                )
              };
            }
            return room;
          })
        };
      }
      return floor;
    }));
    
    // Sync changes to floor plan with debouncing to prevent conflicts
    if (onWindowDimensionSync) {
      const targetFloor = floors.find(f => f.id === floorId);
      const targetRoom = targetFloor?.rooms.find(r => r.id === roomId);
      const targetWindow = targetRoom?.windows?.find(w => w.id === windowId);
      
      if (targetRoom?.name && targetWindow) {
        // Convert values from meters to mm (floor plan uses mm)
        const dimensions = {
          width: targetWindow.width ? parseFloat(targetWindow.width) * 1000 : 0,
          height: targetWindow.height ? parseFloat(targetWindow.height) * 1000 : 0,
          trickleVents: (typeof targetWindow.trickleVents === 'string' && targetWindow.trickleVents === 'Yes') || (typeof targetWindow.trickleVents === 'boolean' && targetWindow.trickleVents === true),
          orientation: targetWindow.orientation || ''
        };
        
        // Update the field that just changed
        if (field === 'width') dimensions.width = value ? parseFloat(value) * 1000 : 0;
        else if (field === 'height') dimensions.height = value ? parseFloat(value) * 1000 : 0;
        else if (field === 'trickleVents') dimensions.trickleVents = value === 'Yes';
        else if (field === 'orientation') dimensions.orientation = value;
        
        // Sync will happen on blur - no need to sync on every keystroke
        console.log(`🪟 Window dimension updated: ${targetRoom.name}, field: ${field}, value: ${value}`);
      }
    }
    
    // Changes will be picked up by debounced autosave unless we're deleting
  };

  const handleGetCalculations = async (floorId: number) => {
    if (!onGetFloorCalculations) {
      console.warn('No floor calculations callback provided');
      return;
    }

    try {
      // 🎯 FLOOR MAPPING FIX: Map floorLevel string to actual floor number in floor plan
      const floor = floors.find(f => f.id === floorId);
      if (!floor) {
        console.error(`Floor with id ${floorId} not found`);
        return;
      }
      
      // Convert floorLevel string to floor number (matches FloorPlanCreator mapping)
      const floorLevelToNumber: Record<string, number> = {
        'basement': 0,
        'ground floor': 1,
        'first floor': 2,
        'second floor': 3,
        'third floor': 4,
        'fourth floor': 5,
        'fifth floor': 6
      };
      
      const actualFloorNumber = floorLevelToNumber[floor.floorLevel];
      
      if (actualFloorNumber === undefined) {
        console.error(`Unknown floor level: ${floor.floorLevel}`);
        return;
      }
      
      console.log(`🎯 FLOOR MAPPING: Site notes floor.id=${floorId} ("${floor.floorLevel}") → Floor plan floor number=${actualFloorNumber}`);
      
      const calculations = await onGetFloorCalculations(actualFloorNumber);
      if (calculations) {
        // Update the floor data with the calculations
        setFloors(prevFloors => 
          prevFloors.map(floor => 
            floor.id === floorId 
              ? {
                  ...floor,
                  floorArea: calculations.area + ' m²',
                  hlp: calculations.hlp + ' m',
                  pw: calculations.pw + ' m',
                  // 🔧 HEIGHT PRESERVATION: Only update height if it's empty or hasn't been manually set
                  // This preserves manual height edits when clicking "Get Calculations"
                  height: floor.height && floor.height.trim() !== '' && floor.height !== 'N/A' 
                    ? floor.height // Keep existing manual height
                    : (() => {
                        const heightValue = calculations.height.trim();
                        if (heightValue.includes('m') || heightValue.includes('M')) {
                          return heightValue; // Height already has units - use as is
                        } else {
                          return heightValue + ' m'; // Height is just a number - add meters unit
                        }
                      })()
                }
              : floor
          )
        );
        // The useEffect will handle autosave automatically
        console.log(`✅ Floor ${floorId} ("${floor.floorLevel}") calculations updated:`, calculations);
      }
    } catch (error) {
      console.error('Error getting floor calculations:', error);
    }
  };


  const handlePCDBSystemSelect = (system: PCDBHeatingSystem | null) => {
    setSelectedPCDBSystem(system);
    
    let updatedFormData = formData;
    if (system) {
      // Auto-populate form fields from PCDB system
      updatedFormData = {
        ...formData,
        heatingType: system.type,
        heatingBrand: system.brand,
        heatingModel: system.model,
        heatingSerialNumber: system.pcdb_reference || '',
        // Map installation type to system type
        heatingSystemType: system.installation_type === 'combi' ? 'combi-boiler' : 
                          system.installation_type === 'system' ? 'system-boiler' :
                          system.installation_type === 'regular' ? 'regular-boiler' : 
                          system.type === 'heat-pump' ? 'heat-pump' : 'radiators'
      };
      setFormData(updatedFormData);
    }
    
    // Trigger immediate autosave with the updated values
    if (hasInitialized.current && !isSyncingAddressRef.current) {
      const currentData: SiteNotesData = { 
        formData: updatedFormData, 
        floors,
        heatingSystems: heatingSystems.length > 0 ? heatingSystems : undefined,
        selectedPCDBSystem: system ? {
          id: system.id,
          brand: system.brand,
          model: system.model,
          model_qualifier: system.model_qualifier,
          type: system.type,
          fuel: system.fuel,
          main_type: system.main_type,
          output_power_kw: system.output_power_kw,
          sap_winter_seasonal_efficiency: system.sap_winter_seasonal_efficiency,
          sap_summer_seasonal_efficiency: system.sap_summer_seasonal_efficiency,
          efficiency_category: system.efficiency_category,
          condensing: system.condensing,
          flue_type: system.flue_type,
          fan_assisted: system.fan_assisted,
          year_of_commission: system.year_of_commission,
          features: system.features,
          certifications: system.certifications,
          pcdb_reference: system.pcdb_reference,
          boiler_id: system.boiler_id,
          last_updated: system.last_updated,
          is_discontinued: system.is_discontinued,
          replacement_models: system.replacement_models,
          installation_type: system.installation_type
        } : undefined
      };
      console.log('💾 PCDB system selection - immediate autosave with updated data');
      onDataChange(currentData);
    }
  };

  const handleManualHeatingEntryChange = (isManual: boolean) => {
    setIsManualHeatingEntry(isManual);
    
    let updatedFormData = formData;
    let updatedSelectedPCDBSystem = selectedPCDBSystem;
    
    if (isManual) {
      // Clear PCDB selection when switching to manual
      updatedSelectedPCDBSystem = null;
      setSelectedPCDBSystem(null);
    } else {
      // Clear manual form fields when switching to PCDB
      updatedFormData = {
        ...formData,
        heatingType: '',
        heatingBrand: '',
        heatingModel: '',
        heatingSerialNumber: '',
        heatingSystemType: ''
      };
      setFormData(updatedFormData);
    }
    
    // Trigger immediate autosave with the updated values
    if (hasInitialized.current && !isSyncingAddressRef.current) {
      const currentData: SiteNotesData = { 
        formData: updatedFormData, 
        floors,
        heatingSystems: heatingSystems.length > 0 ? heatingSystems : undefined,
        selectedPCDBSystem: updatedSelectedPCDBSystem ? {
          id: updatedSelectedPCDBSystem.id,
          brand: updatedSelectedPCDBSystem.brand,
          model: updatedSelectedPCDBSystem.model,
          model_qualifier: updatedSelectedPCDBSystem.model_qualifier,
          type: updatedSelectedPCDBSystem.type,
          fuel: updatedSelectedPCDBSystem.fuel,
          main_type: updatedSelectedPCDBSystem.main_type,
          output_power_kw: updatedSelectedPCDBSystem.output_power_kw,
          sap_winter_seasonal_efficiency: updatedSelectedPCDBSystem.sap_winter_seasonal_efficiency,
          sap_summer_seasonal_efficiency: updatedSelectedPCDBSystem.sap_summer_seasonal_efficiency,
          efficiency_category: updatedSelectedPCDBSystem.efficiency_category,
          condensing: updatedSelectedPCDBSystem.condensing,
          flue_type: updatedSelectedPCDBSystem.flue_type,
          fan_assisted: updatedSelectedPCDBSystem.fan_assisted,
          year_of_commission: updatedSelectedPCDBSystem.year_of_commission,
          features: updatedSelectedPCDBSystem.features,
          certifications: updatedSelectedPCDBSystem.certifications,
          pcdb_reference: updatedSelectedPCDBSystem.pcdb_reference,
          boiler_id: updatedSelectedPCDBSystem.boiler_id,
          last_updated: updatedSelectedPCDBSystem.last_updated,
          is_discontinued: updatedSelectedPCDBSystem.is_discontinued,
          replacement_models: updatedSelectedPCDBSystem.replacement_models,
          installation_type: updatedSelectedPCDBSystem.installation_type
        } : undefined
      };
      console.log('💾 Manual entry mode change - immediate autosave with updated data');
      onDataChange(currentData);
    }
  };

  // Heating Systems Management Functions
  const addHeatingSystem = () => {
    try {
      const newSystems = [...heatingSystems];
      
      // If this is the first heating system being added and we have legacy data, migrate it
      if (heatingSystems.length === 0 && (
        formData.heatingType || 
        formData.heatingBrand || 
        formData.heatingModel || 
        selectedPCDBSystem
      )) {
        // Create legacy system as the first system
        const legacySystem = {
          id: `heating-system-legacy-${Date.now()}`,
          isPrimary: true,
          isManualEntry: isManualHeatingEntry, // Preserve manual entry state from legacy system
          isRadiatorCountManuallyOverridden: false, // Legacy data allows auto-sync initially
          heatingType: formData.heatingType || '',
          heatingSystemType: formData.heatingSystemType || '',
          heatingFuelType: formData.heatingFuelType || '',
          heatingBrand: formData.heatingBrand || '',
          heatingModel: formData.heatingModel || '',
          heatingSerialNumber: formData.heatingSerialNumber || '',
          heatingSourceLocation: formData.heatingSourceLocation || '',
          tankLocation: formData.tankLocation || '',
          totalRads: formData.totalRads || '0',
          trvs: formData.trvs || '0',
          tRad: formData.tRad || '',
          
          // Migrate SAP fields from legacy form data
          heatingControl: formData.heatingControl,
          winterEfficiency: formData.winterEfficiency,
          summerEfficiency: formData.summerEfficiency,
          flowTemperature: formData.flowTemperature,
          isCondensing: formData.isCondensing,
          isLowTemperature: formData.isLowTemperature,
          // Include PCDB system data if selected
          pcdbSystem: selectedPCDBSystem ? {
            id: selectedPCDBSystem.id,
            brand: selectedPCDBSystem.brand,
            model: selectedPCDBSystem.model,
            model_qualifier: selectedPCDBSystem.model_qualifier,
            type: selectedPCDBSystem.type,
            fuel: selectedPCDBSystem.fuel,
            main_type: selectedPCDBSystem.main_type,
            output_power_kw: selectedPCDBSystem.output_power_kw,
            sap_winter_seasonal_efficiency: selectedPCDBSystem.sap_winter_seasonal_efficiency,
            sap_summer_seasonal_efficiency: selectedPCDBSystem.sap_summer_seasonal_efficiency,
            efficiency_category: selectedPCDBSystem.efficiency_category,
            condensing: selectedPCDBSystem.condensing,
            flue_type: selectedPCDBSystem.flue_type,
            fan_assisted: selectedPCDBSystem.fan_assisted,
            year_of_commission: selectedPCDBSystem.year_of_commission,
            features: selectedPCDBSystem.features,
            certifications: selectedPCDBSystem.certifications,
            pcdb_reference: selectedPCDBSystem.pcdb_reference,
            boiler_id: selectedPCDBSystem.boiler_id,
            last_updated: selectedPCDBSystem.last_updated,
            is_discontinued: selectedPCDBSystem.is_discontinued,
            replacement_models: selectedPCDBSystem.replacement_models,
            installation_type: selectedPCDBSystem.installation_type
          } : undefined
        };
        newSystems.push(legacySystem);
        
        // Clear legacy data since it's now in the systems array
        setSelectedPCDBSystem(null);
        setFormData(prev => ({
          ...prev,
          heatingType: '',
          heatingSystemType: '',
          heatingFuelType: '',
          heatingBrand: '',
          heatingModel: '',
          heatingSerialNumber: '',
          heatingSourceLocation: '',
          tankLocation: '',
          totalRads: '0',
          trvs: '0',
          tRad: '',
          
          // Clear SAP fields too
          heatingControl: undefined,
          winterEfficiency: undefined,
          summerEfficiency: undefined,
          flowTemperature: undefined,
          isCondensing: undefined,
          isLowTemperature: undefined
        }));
      }
      
      // Add the new empty system
      const newSystem = {
        id: `heating-system-${Date.now()}`,
        isPrimary: newSystems.length === 0, // First system is primary by default
        isManualEntry: false, // Default to PCDB search mode
        isRadiatorCountManuallyOverridden: false, // Default to auto-sync from floor plan
        heatingType: '',
        heatingSystemType: '',
        heatingFuelType: '',
        heatingBrand: '',
        heatingModel: '',
        heatingSerialNumber: '',
        heatingSourceLocation: '',
        tankLocation: '',
        totalRads: '0',
        trvs: '0',
        tRad: ''
      };
      newSystems.push(newSystem);
      
      setHeatingSystems(newSystems);
      
      // Trigger autosave with the new data immediately (don't wait for state update)
      if (hasInitialized.current && !isSyncingAddressRef.current) {
        const currentData: SiteNotesData = { 
          formData, 
          floors,
          heatingSystems: newSystems.length > 0 ? newSystems : undefined, // Use newSystems instead of heatingSystems
          selectedPCDBSystem: selectedPCDBSystem ? {
            id: selectedPCDBSystem.id,
            brand: selectedPCDBSystem.brand,
            model: selectedPCDBSystem.model,
            model_qualifier: selectedPCDBSystem.model_qualifier,
            type: selectedPCDBSystem.type,
            fuel: selectedPCDBSystem.fuel,
            main_type: selectedPCDBSystem.main_type,
            output_power_kw: selectedPCDBSystem.output_power_kw,
            sap_winter_seasonal_efficiency: selectedPCDBSystem.sap_winter_seasonal_efficiency,
            sap_summer_seasonal_efficiency: selectedPCDBSystem.sap_summer_seasonal_efficiency,
            efficiency_category: selectedPCDBSystem.efficiency_category,
            condensing: selectedPCDBSystem.condensing,
            flue_type: selectedPCDBSystem.flue_type,
            fan_assisted: selectedPCDBSystem.fan_assisted,
            year_of_commission: selectedPCDBSystem.year_of_commission,
            features: selectedPCDBSystem.features,
            certifications: selectedPCDBSystem.certifications,
            pcdb_reference: selectedPCDBSystem.pcdb_reference,
            boiler_id: selectedPCDBSystem.boiler_id,
            last_updated: selectedPCDBSystem.last_updated,
            is_discontinued: selectedPCDBSystem.is_discontinued,
            replacement_models: selectedPCDBSystem.replacement_models,
            installation_type: selectedPCDBSystem.installation_type
          } : undefined
        };
        onDataChange(currentData);
      }
    } catch (error) {
      console.error('❌ Error in addHeatingSystem:', error);
      alert('Error adding heating system. Please check the console for details.');
    }
  };

  const removeHeatingSystem = (systemId: string) => {
    setHeatingSystems(prev => {
      const filteredSystems = prev.filter(system => system.id !== systemId);
      // If we removed the primary system, make the first remaining system primary
      if (filteredSystems.length > 0 && !filteredSystems.some(s => s.isPrimary)) {
        filteredSystems[0].isPrimary = true;
      }
      
      // Trigger autosave with the filtered data immediately (inside the callback)
      if (hasInitialized.current && !isSyncingAddressRef.current) {
        const currentData: SiteNotesData = { 
          formData, 
          floors,
          heatingSystems: filteredSystems.length > 0 ? filteredSystems : undefined,
          selectedPCDBSystem: selectedPCDBSystem ? {
            id: selectedPCDBSystem.id,
            brand: selectedPCDBSystem.brand,
            model: selectedPCDBSystem.model,
            model_qualifier: selectedPCDBSystem.model_qualifier,
            type: selectedPCDBSystem.type,
            fuel: selectedPCDBSystem.fuel,
            main_type: selectedPCDBSystem.main_type,
            output_power_kw: selectedPCDBSystem.output_power_kw,
            sap_winter_seasonal_efficiency: selectedPCDBSystem.sap_winter_seasonal_efficiency,
            sap_summer_seasonal_efficiency: selectedPCDBSystem.sap_summer_seasonal_efficiency,
            efficiency_category: selectedPCDBSystem.efficiency_category,
            condensing: selectedPCDBSystem.condensing,
            flue_type: selectedPCDBSystem.flue_type,
            fan_assisted: selectedPCDBSystem.fan_assisted,
            year_of_commission: selectedPCDBSystem.year_of_commission,
            features: selectedPCDBSystem.features,
            certifications: selectedPCDBSystem.certifications,
            pcdb_reference: selectedPCDBSystem.pcdb_reference,
            boiler_id: selectedPCDBSystem.boiler_id,
            last_updated: selectedPCDBSystem.last_updated,
            is_discontinued: selectedPCDBSystem.is_discontinued,
            replacement_models: selectedPCDBSystem.replacement_models,
            installation_type: selectedPCDBSystem.installation_type
          } : undefined
        };
        onDataChange(currentData);
        console.log('💾 Immediate autosave with filtered data triggered');
      }
      
      return filteredSystems;
    });
  };

  const updateHeatingSystem = (systemId: string, updates: Partial<NonNullable<SiteNotesData['heatingSystems']>[0]>) => {
    setHeatingSystems(prev => {
      const updatedSystems = prev.map(system => 
        system.id === systemId ? { ...system, ...updates } : system
      );
      
      // Trigger autosave with the updated data immediately (inside the callback where updatedSystems is valid)
      if (hasInitialized.current && !isSyncingAddressRef.current) {
        const currentData: SiteNotesData = { 
          formData, 
          floors,
          heatingSystems: updatedSystems.length > 0 ? updatedSystems : undefined,
          selectedPCDBSystem: selectedPCDBSystem ? {
            id: selectedPCDBSystem.id,
            brand: selectedPCDBSystem.brand,
            model: selectedPCDBSystem.model,
            model_qualifier: selectedPCDBSystem.model_qualifier,
            type: selectedPCDBSystem.type,
            fuel: selectedPCDBSystem.fuel,
            main_type: selectedPCDBSystem.main_type,
            output_power_kw: selectedPCDBSystem.output_power_kw,
            sap_winter_seasonal_efficiency: selectedPCDBSystem.sap_winter_seasonal_efficiency,
            sap_summer_seasonal_efficiency: selectedPCDBSystem.sap_summer_seasonal_efficiency,
            efficiency_category: selectedPCDBSystem.efficiency_category,
            condensing: selectedPCDBSystem.condensing,
            flue_type: selectedPCDBSystem.flue_type,
            fan_assisted: selectedPCDBSystem.fan_assisted,
            year_of_commission: selectedPCDBSystem.year_of_commission,
            features: selectedPCDBSystem.features,
            certifications: selectedPCDBSystem.certifications,
            pcdb_reference: selectedPCDBSystem.pcdb_reference,
            boiler_id: selectedPCDBSystem.boiler_id,
            last_updated: selectedPCDBSystem.last_updated,
            is_discontinued: selectedPCDBSystem.is_discontinued,
            replacement_models: selectedPCDBSystem.replacement_models,
            installation_type: selectedPCDBSystem.installation_type
          } : undefined
        };
        onDataChange(currentData);
      }
      
      return updatedSystems;
    });
  };

  const setPrimaryHeatingSystem = (systemId: string) => {
    setHeatingSystems(prev => {
      const updatedSystems = prev.map(system => ({
        ...system,
        isPrimary: system.id === systemId
      }));
      
      // Trigger autosave with the updated data immediately (inside the callback)
      if (hasInitialized.current && !isSyncingAddressRef.current) {
        const currentData: SiteNotesData = { 
          formData, 
          floors,
          heatingSystems: updatedSystems.length > 0 ? updatedSystems : undefined,
          selectedPCDBSystem: selectedPCDBSystem ? {
            id: selectedPCDBSystem.id,
            brand: selectedPCDBSystem.brand,
            model: selectedPCDBSystem.model,
            model_qualifier: selectedPCDBSystem.model_qualifier,
            type: selectedPCDBSystem.type,
            fuel: selectedPCDBSystem.fuel,
            main_type: selectedPCDBSystem.main_type,
            output_power_kw: selectedPCDBSystem.output_power_kw,
            sap_winter_seasonal_efficiency: selectedPCDBSystem.sap_winter_seasonal_efficiency,
            sap_summer_seasonal_efficiency: selectedPCDBSystem.sap_summer_seasonal_efficiency,
            efficiency_category: selectedPCDBSystem.efficiency_category,
            condensing: selectedPCDBSystem.condensing,
            flue_type: selectedPCDBSystem.flue_type,
            fan_assisted: selectedPCDBSystem.fan_assisted,
            year_of_commission: selectedPCDBSystem.year_of_commission,
            features: selectedPCDBSystem.features,
            certifications: selectedPCDBSystem.certifications,
            pcdb_reference: selectedPCDBSystem.pcdb_reference,
            boiler_id: selectedPCDBSystem.boiler_id,
            last_updated: selectedPCDBSystem.last_updated,
            is_discontinued: selectedPCDBSystem.is_discontinued,
            replacement_models: selectedPCDBSystem.replacement_models,
            installation_type: selectedPCDBSystem.installation_type
          } : undefined
        };
        onDataChange(currentData);
      }
      
      return updatedSystems;
    });
  };

  const handleHeatingSystemPCDBSelect = (systemId: string, pcdbSystem: PCDBHeatingSystem | null) => {
    if (pcdbSystem) {
      updateHeatingSystem(systemId, {
        heatingType: pcdbSystem.type,
        heatingBrand: pcdbSystem.brand,
        heatingModel: pcdbSystem.model,
        heatingSerialNumber: pcdbSystem.pcdb_reference || '',
        heatingSystemType: pcdbSystem.installation_type === 'combi' ? 'combi-boiler' : 
                          pcdbSystem.installation_type === 'system' ? 'system-boiler' :
                          pcdbSystem.installation_type === 'regular' ? 'regular-boiler' : 
                          pcdbSystem.type === 'heat-pump' ? 'heat-pump' : 'radiators',
        pcdbSystem: {
          id: pcdbSystem.id,
          brand: pcdbSystem.brand,
          model: pcdbSystem.model,
          model_qualifier: pcdbSystem.model_qualifier,
          type: pcdbSystem.type,
          fuel: pcdbSystem.fuel,
          main_type: pcdbSystem.main_type,
          output_power_kw: pcdbSystem.output_power_kw,
          sap_winter_seasonal_efficiency: pcdbSystem.sap_winter_seasonal_efficiency,
          sap_summer_seasonal_efficiency: pcdbSystem.sap_summer_seasonal_efficiency,
          efficiency_category: pcdbSystem.efficiency_category,
          condensing: pcdbSystem.condensing,
          flue_type: pcdbSystem.flue_type,
          fan_assisted: pcdbSystem.fan_assisted,
          year_of_commission: pcdbSystem.year_of_commission,
          features: pcdbSystem.features,
          certifications: pcdbSystem.certifications,
          pcdb_reference: pcdbSystem.pcdb_reference,
          boiler_id: pcdbSystem.boiler_id,
          last_updated: pcdbSystem.last_updated,
          is_discontinued: pcdbSystem.is_discontinued,
          replacement_models: pcdbSystem.replacement_models,
          installation_type: pcdbSystem.installation_type
        }
      });
    } else {
      updateHeatingSystem(systemId, {
        pcdbSystem: undefined
      });
    }
  };

  const handleHeatingSystemManualEntryChange = (systemId: string, isManual: boolean) => {
    // Combine all updates into a single call to avoid multiple autosave triggers
    if (isManual) {
      // Clear PCDB selection when switching to manual
      updateHeatingSystem(systemId, { 
        isManualEntry: isManual,
        pcdbSystem: undefined 
      });
    } else {
      // Clear manual form fields when switching to PCDB
      updateHeatingSystem(systemId, {
        isManualEntry: isManual,
        heatingType: '',
        heatingBrand: '',
        heatingModel: '',
        heatingSerialNumber: '',
        heatingSystemType: ''
      });
    }
  };

  // Special handler for radiator count changes to mark as manually overridden
  const handleRadiatorCountChange = (systemId: string, field: 'totalRads' | 'trvs', value: string) => {
    updateHeatingSystem(systemId, { 
      [field]: value,
      isRadiatorCountManuallyOverridden: true // Mark as manually overridden
    });
  };

  // Handler to reset radiator count override and sync from floor plan
  const resetRadiatorCountOverride = (systemId: string) => {
    updateHeatingSystem(systemId, { 
      isRadiatorCountManuallyOverridden: false // Reset override flag
    });
    // Note: The sync will happen automatically next time the radiator data is updated from the floor plan
  };


  if (!isOpen) return null;

  const wallTypeOptions = [
    { value: 'SG', label: 'SG Stone: granite or whinstone' },
    { value: 'SS', label: 'SS Stone: sandstone or limestone' },
    { value: 'SO', label: 'SO Solid Brick' },
    { value: 'CO', label: 'CO Cob' },
    { value: 'CA', label: 'CA Cavity' },
    { value: 'TI', label: 'TI Timber Frame' },
    { value: 'SY', label: 'SY System Build' },
    { value: 'CW', label: 'CW Curtain Wall' },
  ];

  const wallInsulationTypeOptions = [
    { value: 'cavity', label: 'Cavity Wall Insulation' },
    { value: 'internal-iwi', label: 'Internal Wall Insulation (IWI)' },
    { value: 'external-ewi', label: 'External Wall Insulation (EWI)' },
  ];

  const wallInsulationThicknessOptions = [
    { value: '10', label: '10 mm' },
    { value: '25', label: '25 mm' },
    { value: '50', label: '50 mm' },
    { value: '75', label: '75 mm' },
    { value: '100', label: '100 mm' },
    { value: '125', label: '125 mm' },
    { value: '150', label: '150 mm' },
    { value: '175', label: '175 mm' },
    { value: '200', label: '200 mm' },
    { value: 'measured', label: 'Measured' },
    { value: 'unknown', label: 'Unknown' },
  ];

  const conditionOptions = [
    { value: 'good', label: 'Good' },
    { value: 'fair', label: 'Fair' },
    { value: 'poor', label: 'Poor' },
    { value: 'very-poor', label: 'Very Poor' },
  ];

  const windowFrameTypeOptions = [
    { value: 'upvc', label: 'uPVC' },
    { value: 'timber', label: 'Timber' },
    { value: 'aluminium', label: 'Aluminium' },
    { value: 'steel', label: 'Steel' },
  ];

  const glazingOptions = [
    { value: '6', label: '6mm' },
    { value: '12', label: '12mm' },
    { value: '16', label: '16mm' },
    { value: '20', label: '20mm' },
  ];

  const heatingTypeOptions = [
    // SAP 10.2 Official Main Heating System Categories
    { value: 'gas-boiler-regular', label: 'Gas Boiler - Regular' },
    { value: 'gas-boiler-combi', label: 'Gas Boiler - Combi' },
    { value: 'gas-boiler-system', label: 'Gas Boiler - System' },
    { value: 'gas-boiler-cpsu', label: 'Gas Boiler - CPSU' },
    { value: 'gas-boiler-back-boiler', label: 'Gas Boiler - Back Boiler' },
    { value: 'gas-warm-air', label: 'Gas Warm Air Heating' },
    { value: 'gas-range-cooker-boiler', label: 'Gas Range Cooker with Boiler' },
    
    { value: 'oil-boiler-regular', label: 'Oil Boiler - Regular' },
    { value: 'oil-boiler-combi', label: 'Oil Boiler - Combi' },
    { value: 'oil-boiler-system', label: 'Oil Boiler - System' },
    { value: 'oil-boiler-cpsu', label: 'Oil Boiler - CPSU' },
    { value: 'oil-warm-air', label: 'Oil Warm Air Heating' },
    { value: 'oil-range-cooker-boiler', label: 'Oil Range Cooker with Boiler' },
    
    { value: 'lpg-boiler-regular', label: 'LPG Boiler - Regular' },
    { value: 'lpg-boiler-combi', label: 'LPG Boiler - Combi' },
    { value: 'lpg-boiler-system', label: 'LPG Boiler - System' },
    { value: 'lpg-boiler-cpsu', label: 'LPG Boiler - CPSU' },
    { value: 'lpg-warm-air', label: 'LPG Warm Air Heating' },
    
    { value: 'solid-fuel-boiler-regular', label: 'Solid Fuel Boiler - Regular' },
    { value: 'solid-fuel-boiler-system', label: 'Solid Fuel Boiler - System' },
    { value: 'solid-fuel-open-fire-back-boiler', label: 'Solid Fuel Open Fire with Back Boiler' },
    { value: 'solid-fuel-room-heater-boiler', label: 'Solid Fuel Room Heater with Boiler' },
    { value: 'solid-fuel-range-cooker-boiler', label: 'Solid Fuel Range Cooker with Boiler' },
    { value: 'biomass-boiler-regular', label: 'Biomass Boiler - Regular' },
    { value: 'biomass-boiler-system', label: 'Biomass Boiler - System' },
    
    { value: 'electric-boiler-direct-acting', label: 'Electric Boiler - Direct Acting' },
    { value: 'electric-boiler-dry-core-storage', label: 'Electric Boiler - Dry Core Storage' },
    { value: 'electric-cpsu', label: 'Electric CPSU' },
    { value: 'electric-storage-heaters', label: 'Electric Storage Heaters' },
    { value: 'electric-panel-heaters', label: 'Electric Panel Heaters' },
    { value: 'electric-warm-air', label: 'Electric Warm Air Heating' },
    { value: 'electric-underfloor', label: 'Electric Underfloor Heating' },
    
    { value: 'heat-pump-air-source-wet', label: 'Air Source Heat Pump - Wet System' },
    { value: 'heat-pump-air-source-warm-air', label: 'Air Source Heat Pump - Warm Air' },
    { value: 'heat-pump-ground-source-wet', label: 'Ground Source Heat Pump - Wet System' },
    { value: 'heat-pump-ground-source-warm-air', label: 'Ground Source Heat Pump - Warm Air' },
    { value: 'heat-pump-water-source', label: 'Water Source Heat Pump' },
    { value: 'heat-pump-exhaust-air', label: 'Exhaust Air Heat Pump' },
    { value: 'heat-pump-hybrid', label: 'Hybrid Heat Pump' },
    
    { value: 'micro-chp-gas', label: 'Micro-CHP - Gas' },
    { value: 'micro-chp-oil', label: 'Micro-CHP - Oil' },
    { value: 'micro-chp-lpg', label: 'Micro-CHP - LPG' },
    { value: 'micro-chp-solid-fuel', label: 'Micro-CHP - Solid Fuel' },
    
    { value: 'heat-network-communal', label: 'Heat Network - Communal Heating' },
    { value: 'heat-network-district', label: 'Heat Network - District Heating' },
    { value: 'heat-network-chp', label: 'Heat Network - with CHP' },
    
    { value: 'room-heaters-gas-fire', label: 'Gas Room Heater/Fire' },
    { value: 'room-heaters-electric-fire', label: 'Electric Room Heater/Fire' },
    { value: 'room-heaters-solid-fuel-fire', label: 'Solid Fuel Room Heater/Fire' },
    { value: 'room-heaters-oil-fire', label: 'Oil Room Heater/Fire' },
    
    { value: 'other-heating-system', label: 'Other Heating System' },
    { value: 'no-heating-system', label: 'No Heating System' },
  ];

  const heatingSystemTypeOptions = [
    // SAP 10.2 Official Heat Emitter Types
    { value: 'radiators-standard', label: 'Radiators - Standard' },
    { value: 'radiators-fan-assisted', label: 'Radiators - Fan Assisted' },
    { value: 'radiators-panel', label: 'Panel Radiators' },
    { value: 'underfloor-heating-electric', label: 'Underfloor Heating - Electric' },
    { value: 'underfloor-heating-wet', label: 'Underfloor Heating - Wet System' },
    { value: 'warm-air-ducted', label: 'Warm Air - Ducted' },
    { value: 'warm-air-room-sealed', label: 'Warm Air - Room Sealed' },
    { value: 'storage-heaters-standard', label: 'Storage Heaters - Standard' },
    { value: 'storage-heaters-high-retention', label: 'Storage Heaters - High Heat Retention' },
    { value: 'storage-heaters-integrated', label: 'Storage Heaters - Integrated' },
    { value: 'panel-heaters-electric', label: 'Panel Heaters - Electric' },
    { value: 'convector-heaters-electric', label: 'Convector Heaters - Electric' },
    { value: 'convector-heaters-gas', label: 'Convector Heaters - Gas' },
    { value: 'fan-coil-units', label: 'Fan Coil Units' },
    { value: 'radiant-heating', label: 'Radiant Heating' },
    { value: 'ceiling-heating', label: 'Ceiling Heating' },
    { value: 'duct-heaters', label: 'Duct Heaters' },
    { value: 'room-heaters-individual', label: 'Individual Room Heaters' },
    { value: 'other-emitter-type', label: 'Other Emitter Type' },
  ];

  const heatingFuelTypeOptions = [
    // SAP 10.2 Official Fuel Types
    { value: 'mains-gas', label: 'Mains Gas' },
    { value: 'lpg-bulk', label: 'LPG - Bulk' },
    { value: 'lpg-bottled', label: 'LPG - Bottled' },
    { value: 'heating-oil-kerosene', label: 'Heating Oil (Kerosene)' },
    { value: 'heating-oil-gas-oil', label: 'Heating Oil (Gas Oil)' },
    { value: 'heating-oil-lpg', label: 'Heating Oil (LPG)' },
    { value: 'electricity-standard', label: 'Electricity - Standard Rate' },
    { value: 'electricity-7-hour', label: 'Electricity - 7 Hour Tariff' },
    { value: 'electricity-10-hour', label: 'Electricity - 10 Hour Tariff' },
    { value: 'electricity-24-hour', label: 'Electricity - 24 Hour Heating' },
    { value: 'house-coal', label: 'House Coal' },
    { value: 'anthracite', label: 'Anthracite' },
    { value: 'manufactured-smokeless', label: 'Manufactured Smokeless Fuel' },
    { value: 'wood-logs', label: 'Wood Logs' },
    { value: 'wood-pellets', label: 'Wood Pellets' },
    { value: 'wood-chips', label: 'Wood Chips' },
    { value: 'biodiesel', label: 'Biodiesel' },
    { value: 'bioethanol', label: 'Bioethanol' },
    { value: 'biomass-other', label: 'Biomass - Other' },
    { value: 'heat-network', label: 'Heat from Heat Network' },
    { value: 'waste-heat', label: 'Waste Heat' },
    { value: 'geothermal', label: 'Geothermal' },
    { value: 'solar-thermal', label: 'Solar Thermal' },
    { value: 'ambient-heat', label: 'Ambient Heat (Heat Pump)' },
    { value: 'hydrogen', label: 'Hydrogen' },
    { value: 'other-fuel', label: 'Other Fuel Type' },
  ];

  const glazingTypeOptions = [
    { value: 'single', label: 'Single Glazed' },
    { value: 'double', label: 'Double Glazed' },
    { value: 'triple', label: 'Triple Glazed' },
  ];

  const ageBandOptions = [
    { value: 'A', label: 'A - Before 1900' },
    { value: 'B', label: 'B - 1900-1929' },
    { value: 'C', label: 'C - 1930-1949' },
    { value: 'D', label: 'D - 1950-1966' },
    { value: 'E', label: 'E - 1967-1975' },
    { value: 'F', label: 'F - 1976-1982' },
    { value: 'G', label: 'G - 1983-1990' },
    { value: 'H', label: 'H - 1991-1995' },
    { value: 'I', label: 'I - 1996-2002' },
    { value: 'J', label: 'J - 2003-2006' },
    { value: 'K', label: 'K - 2007-2011' },
    { value: 'L', label: 'L - 2012 onwards' },
  ];

  const groundLevelOptions = [
    { value: 'G', label: 'G - Ground floor' },
    { value: 'P', label: 'P - Above partially heated space' },
    { value: 'U', label: 'U - Above unheated space' },
    { value: 'E', label: 'E - To external air' },
    { value: 'S', label: 'S - Same dwelling below' },
    { value: 'A', label: 'A - Another dwelling below' },
  ];

  const insulationTypeOptions = [
    { value: 'N', label: 'N None' },
    { value: 'J', label: 'J Joists' },
    { value: 'R', label: 'R Rafters' },
    { value: 'U', label: 'U Unknown' },
  ];

  const insulationThicknessOptions = [
    { value: '12', label: '12 mm' },
    { value: '25', label: '25 mm' },
    { value: '50', label: '50 mm' },
    { value: '75', label: '75 mm' },
    { value: '100', label: '100 mm' },
    { value: '125', label: '125 mm' },
    { value: '150', label: '150 mm' },
    { value: '175', label: '175 mm' },
    { value: '200', label: '200 mm' },
    { value: '225', label: '225 mm' },
    { value: '250', label: '250 mm' },
    { value: '270', label: '270 mm' },
    { value: '300', label: '300 mm' },
    { value: '350', label: '350 mm' },
    { value: '400+', label: '400+ mm' },
    { value: 'Unknown', label: 'Unknown' },
  ];

  const regionOptions = [
    { value: 'England', label: 'England' },
    { value: 'Wales', label: 'Wales' },
    { value: 'Scotland', label: 'Scotland' },
    { value: 'Northern Ireland', label: 'Northern Ireland' },
  ];

  const assessmentPurposeOptions = [
    { value: 'Retrofit - Pre assessment', label: 'Retrofit - Pre assessment' },
    { value: 'Retrofit - Post assessment', label: 'Retrofit - Post assessment' },
    { value: 'Retrofit - Modelling', label: 'Retrofit - Modelling' },
    { value: 'Energy Modelling (Not Retrofit)', label: 'Energy Modelling (Not Retrofit)' },
    { value: 'None of the above', label: 'None of the above' },
  ];

  const transactionTypeOptions = [
    { value: 'Marketed sale', label: 'Marketed sale' },
    { value: 'Non-marketed sale', label: 'Non-marketed sale' },
    { value: 'Rental', label: 'Rental' },
    { value: 'Stock condition survey', label: 'Stock condition survey' },
    { value: 'Grant scheme', label: 'Grant scheme' },
    { value: 'Non-grant scheme', label: 'Non-grant scheme' },
    { value: 'Re-mortgaging', label: 'Re-mortgaging' },
    { value: 'None of the above', label: 'None of the above' },
  ];

  const dateBuiltOptions = [
    { value: 'A before 1900', label: 'A before 1900' },
    { value: 'B 1900 to 1929', label: 'B 1900 to 1929' },
    { value: 'C 1930 to 1949', label: 'C 1930 to 1949' },
    { value: 'D 1950 to 1966', label: 'D 1950 to 1966' },
    { value: 'E 1967 to 1975', label: 'E 1967 to 1975' },
    { value: 'F 1976 to 1982', label: 'F 1976 to 1982' },
    { value: 'G 1983 to 1990', label: 'G 1983 to 1990' },
    { value: 'H 1991 to 1995', label: 'H 1991 to 1995' },
    { value: 'I 1996 to 2002', label: 'I 1996 to 2002' },
    { value: 'J 2003 to 2006', label: 'J 2003 to 2006' },
    { value: 'K 2007 to 2011', label: 'K 2007 to 2011' },
    { value: 'L 2012 onwards', label: 'L 2012 onwards' },
  ];

  const roofTypeRdSAPOptions = [
    { value: 'PA', label: 'PA - Pitched (slates/tiles), access to loft' },
    { value: 'PN', label: 'PN - Pitched (slates/tiles), no access' },
    { value: 'PS', label: 'PS - Pitched, sloping ceiling' },
    { value: 'PT', label: 'PT - Pitched (thatch)' },
    { value: 'F', label: 'F - Flat' },
    { value: 'S', label: 'S - Same dwelling above' },
    { value: 'A', label: 'A - Another dwelling above' },
    { value: 'NR', label: 'NR - Non-residential space above' },
  ];

  const partyWallOptions = [
    { value: 'S', label: 'S Solid masonry / timber / system build' },
    { value: 'CU', label: 'CU Cavity masonry unfilled' },
    { value: 'CF', label: 'CF Cavity masonry filled' },
    { value: 'U', label: 'U Unable to determine' },
  ];

  const roofTypeOptions = [
    { value: 'pitched-tile', label: 'Pitched - Tile' },
    { value: 'pitched-slate', label: 'Pitched - Slate' },
    { value: 'pitched-thatch', label: 'Pitched - Thatch' },
    { value: 'pitched-metal', label: 'Pitched - Metal' },
    { value: 'flat-felt', label: 'Flat - Felt' },
    { value: 'flat-asphalt', label: 'Flat - Asphalt' },
    { value: 'flat-membrane', label: 'Flat - Membrane' },
    { value: 'flat-green', label: 'Flat - Green Roof' },
    { value: 'mansard', label: 'Mansard' },
    { value: 'other', label: 'Other' },
  ];

  const wallInsulationElevationsCountOptions = [
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' },
    { value: '6', label: '6' },
    { value: '7', label: '7' },
    { value: '8', label: '8' },
    { value: '9', label: '9' },
    { value: '10', label: '10' },
  ];

  const elevationTypeOptions = [
    { value: 'front', label: 'Front Elevation' },
    { value: 'right', label: 'Right Elevation' },
    { value: 'left', label: 'Left Elevation' },
    { value: 'rear', label: 'Rear Elevation' },
  ];

  const floorTypeOptions = [
    { value: 'solid', label: 'Solid' },
    { value: 'suspended-timber', label: 'Suspended Timber' },
    { value: 'suspended-not-timber', label: 'Suspended Not Timber' },
    { value: 'unknown', label: 'Unknown' },
  ];

  const yesNoOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
  ];

  const buildingTypeOptions = [
    { value: 'main building', label: 'Main Building' },
    { value: 'ext 1', label: 'Ext 1' },
    { value: 'ext 2', label: 'Ext 2' },
    { value: 'ext 3', label: 'Ext 3' },
    { value: 'ext 4', label: 'Ext 4' },
    { value: 'ext 5', label: 'Ext 5' },
  ];

  const floorLevelOptions = [
    { value: 'basement', label: 'Basement' },
    { value: 'ground floor', label: 'Ground Floor' },
    { value: 'first floor', label: 'First Floor' },
    { value: 'second floor', label: 'Second Floor' },
    { value: 'third floor', label: 'Third Floor' },
    { value: 'fourth floor', label: 'Fourth Floor' },
    { value: 'fifth floor', label: 'Fifth Floor' },
  ];

  const windowOrientationOptions = [
    { value: '', label: 'Select Orientation' },
    { value: 'front', label: 'Front' },
    { value: 'back', label: 'Back' },
    { value: 'left', label: 'Left' },
    { value: 'right', label: 'Right' },
    { value: 'north', label: 'N' },
    { value: 'south', label: 'S' },
    { value: 'east', label: 'E' },
    { value: 'west', label: 'W' },
    { value: 'northeast', label: 'NE' },
    { value: 'southeast', label: 'SE' },
    { value: 'southwest', label: 'SW' },
    { value: 'northwest', label: 'NW' },
  ];

  const heatingControlOptions = [
    // SAP 10.2 Official Heating Controls
    { value: 'manual-charge-control', label: 'Manual Charge Control' },
    { value: 'automatic-charge-control', label: 'Automatic Charge Control' },
    { value: 'celect-type-control', label: 'Celect-type Control' },
    { value: 'programmer-room-thermostat', label: 'Programmer + Room Thermostat' },
    { value: 'programmer-room-thermostat-trvs', label: 'Programmer + Room Thermostat + TRVs' },
    { value: 'programmer-trvs', label: 'Programmer + TRVs' },
    { value: 'room-thermostat-only', label: 'Room Thermostat Only' },
    { value: 'programmable-room-thermostat', label: 'Programmable Room Thermostat' },
    { value: 'time-and-temperature-zone-control', label: 'Time and Temperature Zone Control' },
    { value: 'programmer-room-thermostat-delayed-start', label: 'Programmer + Room Thermostat + Delayed Start' },
    { value: 'no-thermostatic-control', label: 'No Thermostatic Control of Room Temperature' },
    { value: 'appliance-thermostat', label: 'Appliance Thermostat' },
    { value: 'programmer-appliance-thermostat', label: 'Programmer + Appliance Thermostat' },
    { value: 'room-thermostat-appliance-thermostat', label: 'Room Thermostat + Appliance Thermostat' },
    { value: 'programmer-room-thermostat-appliance-thermostat', label: 'Programmer + Room Thermostat + Appliance Thermostat' },
    { value: 'flow-switch', label: 'Flow Switch' },
    { value: 'load-compensating-control', label: 'Load Compensating Control' },
    { value: 'weather-compensating-control', label: 'Weather Compensating Control' },
    { value: 'flat-rate-charging', label: 'Flat Rate Charging' },
    { value: 'manually-controlled', label: 'Manually Controlled' },
    { value: 'no-control', label: 'No Control' },
  ];



  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex justify-center items-start overflow-y-auto">
        <div className="bg-white w-full max-w-6xl mx-auto relative min-h-screen">
          {/* Main Header */}
          <header className="bg-white shadow-md sticky top-0 z-40 flex-shrink-0 rounded-b-2xl border-b-2 border-yellow-400">
            <div className="w-full px-4 sm:px-6 flex items-center justify-between app-header-padding">
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  title="Back to Floor Plan"
                  className="p-2.5 rounded-xl text-gray-600 hover:bg-yellow-50 hover:text-yellow-600 transition-all duration-200 border border-gray-200 hover:border-yellow-300"
                >
                  <ArrowLeft size={18} />
                </button>
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                  Surveyor Site Notes
                </h1>
              </div>
              <div className="flex items-center space-x-3">
                <AutosaveIndicator
                  status={autosaveStatus}
                  lastSavedAt={lastSavedAt}
                  lastError={lastError}
                  retryCount={retryCount}
                  canEnableAutosave={canEnableAutosave}
                  showLastSaved={true}
                  className="scale-90"
                />
                <button onClick={onClose} className="text-gray-500 hover:text-gray-800 p-2">
                  <X size={20} />
                </button>
              </div>
            </div>
          </header>

          {/* Section Navigation Bar */}
          <div className="bg-white shadow-lg sticky top-[85px] z-30 border-b-2 border-yellow-300 overflow-hidden">
            <div className="container mx-auto px-4 sm:px-6 py-4">
              <div className="flex items-center space-x-2 overflow-x-auto overflow-y-hidden">
                <div className="flex items-center space-x-1 min-w-0 flex-1">
                  {sections.map((section, index) => {
                    const IconComponent = section.icon;
                    const isActive = currentSection === section.id;
                    
                    return (
                      <React.Fragment key={section.id}>
                        <button
                          onClick={() => setCurrentSection(section.id)}
                          title={section.label}
                          className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-medium transition-all duration-150 whitespace-nowrap flex-shrink-0 shadow-sm
                            ${isActive 
                              ? 'bg-yellow-500 text-white ring-2 ring-yellow-400 ring-offset-2 transform scale-105' 
                              : 'bg-white text-gray-700 hover:bg-yellow-50 hover:text-yellow-700 border border-gray-200'
                            }`}
                        >
                          <IconComponent size={16} />
                          <span className="hidden sm:inline">{section.label}</span>
                        </button>
                        {index < sections.length - 1 && (
                          <ChevronRight className="text-gray-400 flex-shrink-0" size={16} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <main className="container mx-auto px-4 sm:px-6 py-8 space-y-8">
            {renderSectionContent()}
          </main>
        </div>
      </div>


    </>
  );

  function renderSectionContent() {
    switch (currentSection) {
      case 'basic':
        return (
          <div className="space-y-6">
            {/* Property Info */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Property Info</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <FormField 
                  label="Building / Flat Name & Number" 
                  name="address.buildingName" 
                  value={formData.address?.buildingName || ''} 
                  onChange={handleFormChange} 
                  onBlur={handleFieldBlur}
                  placeholder="e.g. Flat 2 Ferndale Court"
                  required={false}
                  disabled={false}
                />
                <FormField 
                  label="House Number & Street" 
                  name="address.street" 
                  value={
                    formData.address?.houseNumber && formData.address?.street
                      ? `${formData.address.houseNumber} ${formData.address.street}`
                      : formData.address?.street || formData.address?.houseNumber || ''
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    // 🔧 FIX: Allow free-form typing - store as street temporarily during typing
                    // The parsing will happen on blur to avoid interfering with user input
                    setFormData(prev => ({
                      ...prev,
                      address: {
                        ...prev.address,
                        street: value,
                        houseNumber: '' // Clear house number during typing, will be parsed on blur
                      }
                    }));
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    // Parse house number from street on blur
                    const match = value.match(/^(\d+[a-zA-Z]?)\s+(.+)$/);
                    
                    if (match) {
                      // Has house number prefix - split into houseNumber and street
                      setFormData(prev => ({
                        ...prev,
                        address: {
                          ...prev.address,
                          houseNumber: match[1],
                          street: match[2]
                        }
                      }));
                    } else {
                      // No house number pattern detected - treat entire value as street or houseNumber
                      const isJustNumber = /^\d+[a-zA-Z]?$/.test(value.trim());
                      setFormData(prev => ({
                        ...prev,
                        address: {
                          ...prev.address,
                          houseNumber: isJustNumber ? value.trim() : '',
                          street: isJustNumber ? '' : value
                        }
                      }));
                    }
                    
                    // Call original blur handler for parent notification
                    handleFieldBlur(e);
                  }}
                  placeholder="e.g. 1 Kendra Hall Road"
                  required 
                  disabled={false}
                />
                <FormField 
                  label="Town" 
                  name="address.town" 
                  value={formData.address?.town || ''} 
                  onChange={handleFormChange} 
                  onBlur={handleFieldBlur}
                  placeholder="e.g. London"
                  required 
                  disabled={false}
                />
                <FormField 
                  label="Postcode" 
                  name="address.postcode" 
                  value={formData.address?.postcode || ''} 
                  onChange={handleFormChange} 
                  onBlur={handleFieldBlur}
                  placeholder="e.g. SW1A 1AA"
                  required 
                  disabled={false}
                />
                <SelectField
                  label="Property Type"
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleFormChange}
                  options={[
                    { value: 'house', label: 'House' },
                    { value: 'bungalow', label: 'Bungalow' },
                    { value: 'flat', label: 'Flat' },
                    { value: 'maisonette', label: 'Maisonette' },
                    { value: 'park-home', label: 'Park Home' },
                  ]}
                  required
                />
                <SelectField
                  label="Detachment Type"
                  name="detachment"
                  value={formData.detachment}
                  onChange={handleFormChange}
                  options={[
                    { value: 'detached', label: 'Detached' },
                    { value: 'semi-detached', label: 'Semi-detached' },
                    { value: 'mid-terrace', label: 'Mid-terrace' },
                    { value: 'end-terrace', label: 'End-terrace' },
                    { value: 'enclosed-mid-terrace', label: 'Enclosed Mid-terrace' },
                    { value: 'enclosed-end-terrace', label: 'Enclosed End-terrace' },
                  ]}
                  required
                />
                <SelectField
                  label="Age Band"
                  name="ageBand"
                  value={formData.ageBand}
                  onChange={handleFormChange}
                  options={ageBandOptions}
                  required
                />
              </div>
            </div>

            {/* Building Structure */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Building Structure</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <SelectField
                  label="Roof Type"
                  name="roofTypeRdSAP"
                  value={formData.roofTypeRdSAP}
                  onChange={handleFormChange}
                  onBlur={handleFieldBlur}
                  options={roofTypeRdSAPOptions}
                  required
                />
                <SelectField
                  label="Insulation"
                  name="insulationType"
                  value={formData.insulationType || ''}
                  onChange={handleFormChange}
                  onBlur={handleFieldBlur}
                  options={insulationTypeOptions}
                />
                {(formData.insulationType === 'J' || formData.insulationType === 'R') && (
                  <SelectField
                    label="Insulation Thickness (mm)"
                    name="roofInsulation"
                    value={formData.roofInsulation}
                    onChange={handleFormChange}
                    onBlur={handleFieldBlur}
                    options={insulationThicknessOptions}
                  />
                )}
                <SelectField
                  label="Ground Level"
                  name="groundLevel"
                  value={formData.groundLevel}
                  onChange={handleFormChange}
                  onBlur={handleFieldBlur}
                  options={groundLevelOptions}
                  required
                />
                {formData.groundLevel === 'G' && (
                  <SelectField
                    label="Floor Type"
                    name="floorType"
                    value={formData.floorType}
                    onChange={handleFormChange}
                    onBlur={handleFieldBlur}
                    options={floorTypeOptions}
                  />
                )}
              </div>
            </div>

            {/* Project Details */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Project Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <SelectField
                  label="Region"
                  name="region"
                  value={formData.region || 'England'}
                  onChange={handleFormChange}
                  options={regionOptions}
                  required
                />
                <SelectField
                  label="Assessment Purpose"
                  name="assessmentPurpose"
                  value={formData.assessmentPurpose || ''}
                  onChange={handleFormChange}
                  options={assessmentPurposeOptions}
                />
                <SelectField
                  label="Transaction Type"
                  name="transactionType"
                  value={formData.transactionType || ''}
                  onChange={handleFormChange}
                  options={transactionTypeOptions}
                />
                <FormField
                  label="Inspection Date"
                  name="inspectionDate"
                  value={formData.inspectionDate || ''}
                  onChange={handleFormChange}
                  type="date"
                />
                <FormField
                  label="Process Date"
                  name="processDate"
                  value={formData.processDate || new Date().toISOString().split('T')[0]}
                  onChange={handleFormChange}
                  type="date"
                />
              </div>
            </div>


          </div>
          );

      case 'property':
        return (
          <div className="space-y-8">
            {/* Property Details */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Property Details</h3>
              
              {/* Date Built */}
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-700 mb-3 border-b border-gray-200 pb-1">Date Built</h4>
                <div className="space-y-3">
                  {(() => {
                    // Always ensure we have Main and 1st Extension as minimum
                    const defaultEntries = [
                      { id: '1', type: 'main', label: 'Main', dateBuilt: '', rirDateBuilt: '' },
                      { id: '2', type: 'extension', label: '1st Extension', dateBuilt: '', rirDateBuilt: '' }
                    ];
                    
                    let entries = formData.dateBuiltEntries || defaultEntries;
                    
                    // Ensure we always have minimum entries
                    if (entries.length < 2) {
                      entries = [...defaultEntries];
                    }
                    
                    // Only show extensions that have dates or are the immediate next extension
                    const visibleEntries = [];
                    
                    // Always show main building
                    visibleEntries.push(entries[0]);
                    
                    // Show extensions based on rule: show if it has data, or if it's the next logical extension
                    for (let i = 1; i < entries.length; i++) {
                      const entry = entries[i];
                      const prevEntry = entries[i - 1];
                      
                      if (entry.type === 'extension') {
                        // Show if current entry has data
                        if (entry.dateBuilt || entry.rirDateBuilt) {
                          visibleEntries.push(entry);
                        }
                        // Or show if previous entry (extension) has data (to allow next entry)
                        else if (i === 1 || (prevEntry.type === 'extension' && (prevEntry.dateBuilt || prevEntry.rirDateBuilt))) {
                          visibleEntries.push(entry);
                        }
                      }
                    }
                    
                    return visibleEntries.map((entry, index) => {
                      const actualIndex = entries.findIndex(e => e.id === entry.id);
                      return (
                        <div key={entry.id} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                          <div className="font-medium text-gray-700 text-sm">
                            {entry.label}
                          </div>
                          <SelectField
                            label="Date Built"
                            name={`dateBuiltEntries[${actualIndex}].dateBuilt`}
                            value={entry.dateBuilt || ''}
                            onChange={(e) => handleDateBuiltChange(actualIndex, 'dateBuilt', e.target.value)}
                            options={dateBuiltOptions}
                          />
                          <SelectField
                            label="Room(s) in Roof Date Built"
                            name={`dateBuiltEntries[${actualIndex}].rirDateBuilt`}
                            value={entry.rirDateBuilt || ''}
                            onChange={(e) => handleDateBuiltChange(actualIndex, 'rirDateBuilt', e.target.value)}
                            options={dateBuiltOptions}
                          />
                        </div>
                      );
                    });
                  })()
                  }
                </div>
              </div>
              
              {/* Wall Construction - Conditional sections based on Date Built entries */}
              {(() => {
                const wallSections = formData.wallConstructionSections || [];
                
                if (wallSections.length === 0) {
                  return (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <h4 className="text-md font-medium text-gray-700 mb-2">Wall Construction</h4>
                      <p className="text-sm text-gray-600 italic">Select a construction date above to add wall construction details for that section.</p>
                    </div>
                  );
                }
                
                return (
                  <div className="mb-6">
                    <h4 className="text-md font-medium text-gray-700 mb-4 border-b border-gray-200 pb-1">Wall Construction</h4>
                    <div className="space-y-6">
                      {wallSections.map((section) => (
                        <div key={section.sectionId} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <h5 className="text-sm font-semibold text-gray-800 mb-3">{section.sectionLabel} - Wall Details</h5>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <SelectField
                              label="Wall Type"
                              name={`wallType-${section.sectionId}`}
                              value={section.wallType || ''}
                              onChange={(e) => handleWallConstructionChange(section.sectionId, 'wallType', e.target.value)}
                              options={wallTypeOptions}
                            />
                            <SelectField
                              label="Wall Insulation Type"
                              name={`wallInsulationType-${section.sectionId}`}
                              value={section.wallInsulationType || ''}
                              onChange={(e) => handleWallConstructionChange(section.sectionId, 'wallInsulationType', e.target.value)}
                              options={wallInsulationTypeOptions}
                            />
                            <SelectField
                              label="Party Wall"
                              name={`partyWall-${section.sectionId}`}
                              value={section.partyWall || ''}
                              onChange={(e) => handleWallConstructionChange(section.sectionId, 'partyWall', e.target.value)}
                              options={partyWallOptions}
                            />
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                            <FormField 
                              label="Wall Thickness (mm)" 
                              name={`wallThickness-${section.sectionId}`}
                              value={section.wallThickness || ''}
                              onChange={(e) => handleWallConstructionChange(section.sectionId, 'wallThickness', e.target.value)}
                              placeholder="e.g., 225"
                            />
                            {/* Show insulation thickness only if Internal or External insulation is selected */}
                            {(section.wallInsulationType === 'I' || section.wallInsulationType === 'E') && (
                              <SelectField
                                label="Insulation Thickness"
                                name={`wallInsulationThickness-${section.sectionId}`}
                                value={section.wallInsulationThickness || ''}
                                onChange={(e) => handleWallConstructionChange(section.sectionId, 'wallInsulationThickness', e.target.value)}
                                options={wallInsulationThicknessOptions}
                              />
                            )}
                            {/* Dry Lining */}
                            <div className="flex flex-col">
                              <ToggleField
                                label="Dry Lining"
                                name={`dryLining-${section.sectionId}`}
                                value={section.dryLining || ''}
                                onChange={(e) => handleWallConstructionChange(section.sectionId, 'dryLining', e.target.value)}
                                options={[
                                  { value: 'yes', label: 'Yes' },
                                  { value: 'no', label: 'No' }
                                ]}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Property Layout */}
              <div>
                <h4 className="text-md font-medium text-gray-700 mb-3 border-b border-gray-200 pb-1">Property Layout</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <CounterField 
                    label="Number of Storeys" 
                    name="numberOfStoreys" 
                    value={formData.numberOfStoreys || ''} 
                    onChange={handleFormChange} 
                    max={10}
                  />
                  <CounterField 
                    label="Total Habitable Rooms" 
                    name="totalHabitableRooms" 
                    value={formData.totalHabitableRooms} 
                    onChange={handleFormChange} 
                    max={50}
                  />
                  <CounterField 
                    label="Total Heated Habitable Rooms" 
                    name="totalHeatedHabitableRooms" 
                    value={formData.totalHeatedHabitableRooms} 
                    onChange={handleFormChange} 
                    max={50}
                  />
                </div>
              </div>
              
              {/* Conservatory */}
              <div>
                <h4 className="text-md font-medium text-gray-700 mb-3 border-b border-gray-200 pb-1">Conservatory</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                  <ToggleField
                    label="Is there a conservatory?"
                    name="hasConservatory"
                    value={formData.hasConservatory || ''}
                    onChange={handleFormChange}
                    options={[
                      { value: 'yes', label: 'Yes' },
                      { value: 'no', label: 'No' }
                    ]}
                  />
                  
                  {/* Show conservatory details only if hasConservatory is 'yes' */}
                  {formData.hasConservatory === 'yes' && (
                    <div className="bg-white rounded-lg p-4 border border-gray-200 space-y-4">
                      <ToggleField
                        label="Is it thermally separated?"
                        name="conservatoryThermallySeparated"
                        value={formData.conservatoryThermallySeparated || ''}
                        onChange={handleFormChange}
                        options={[
                          { value: 'yes', label: 'Yes' },
                          { value: 'no', label: 'No' }
                        ]}
                      />
                      
                      {/* If thermally separated is YES, show fixed heaters question only */}
                      {formData.conservatoryThermallySeparated === 'yes' && (
                        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                          <ToggleField
                            label="Does it have fixed heaters?"
                            name="conservatoryFixedHeaters"
                            value={formData.conservatoryFixedHeaters || ''}
                            onChange={handleFormChange}
                            options={[
                              { value: 'yes', label: 'Yes' },
                              { value: 'no', label: 'No' }
                            ]}
                          />
                        </div>
                      )}
                      
                      {/* If thermally separated is NO or not selected, show other fields */}
                      {formData.conservatoryThermallySeparated !== 'yes' && (
                        <div className="space-y-4">
                          <ToggleField
                            label="Double Glazed"
                            name="conservatoryDoubleGlazed"
                            value={formData.conservatoryDoubleGlazed || ''}
                            onChange={handleFormChange}
                            options={[
                              { value: 'yes', label: 'Yes' },
                              { value: 'no', label: 'No' }
                            ]}
                          />
                          
                          {/* Floor Area and Glazed Perimeter */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormField
                              label="Floor Area [m2]"
                              name="conservatoryFloorArea"
                              value={formData.conservatoryFloorArea || ''}
                              onChange={handleFormChange}
                              placeholder="0.00"
                              type="number"
                            />
                            <FormField
                              label="Glazed Perimeter [m]"
                              name="conservatoryGlazedPerimeter"
                              value={formData.conservatoryGlazedPerimeter || ''}
                              onChange={handleFormChange}
                              placeholder="0.00"
                              type="number"
                            />
                          </div>
                          
                          {/* Room Height */}
                          <ToggleField
                            label="Room Height"
                            name="conservatoryRoomHeight"
                            value={formData.conservatoryRoomHeight || ''}
                            onChange={handleFormChange}
                            options={[
                              { value: '1', label: '1 Storey' },
                              { value: '1.5', label: '1.5 Storey' },
                              { value: '2', label: '2 Storey' },
                              { value: '2.5', label: '2.5 Storey' },
                              { value: '3', label: '3 Storey' }
                            ]}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
            </div>
            </div>
          );

      case 'wall-insulation':
        return (
          <div className="space-y-8">
            {/* Wall Insulation */}
            <SectionCard icon={Settings} title="Wall Insulation">
                  <SelectField
                    label="Type of Insulation"
                    name="wallInsulationType"
                    value={formData.wallInsulationType}
                    onChange={handleFormChange}
                    options={wallInsulationTypeOptions}
                    required
                  />
                  <SelectField
                    label="Number of Elevations"
                    name="wallInsulationElevationsCount"
                    value={formData.wallInsulationElevationsCount}
                    onChange={(e) => handleElevationCountChange(e.target.value)}
                    options={wallInsulationElevationsCountOptions}
                    required
                  />
                  
                  {/* Dynamic Elevation Sections */}
                  {formData.wallInsulationElevations && formData.wallInsulationElevations.length > 0 && (
                    <div className="mt-6 space-y-4">
                      <h4 className="text-lg font-medium text-gray-800">Elevation Types</h4>
                      {formData.wallInsulationElevations.map((elevation, index) => (
                        <div key={elevation.id} className="p-4 bg-gray-50 rounded-lg border">
                          <SelectField
                            label={`Elevation ${index + 1}`}
                            name={`elevation-${elevation.id}`}
                            value={elevation.type}
                            onChange={(e) => handleElevationTypeChange(elevation.id, e.target.value)}
                            options={elevationTypeOptions}
                            required
                          />
                          {/* Show diagram when elevation type is selected */}
                          {elevation.type && (
                            <PropertyElevationDiagram 
                              key={`${elevation.type}-${elevation.id}`}
                              elevationType={elevation.type}
                              elevationId={elevation.id}
                              elevationNumber={index + 1}
                              propertyData={{
                                propertyType: formData.propertyType || 'house',
                                roofTypeRdSAP: formData.roofTypeRdSAP || 'PA',
                                groundLevel: formData.groundLevel || 'G',
                                detachment: formData.detachment || 'detached',
                                floorCount: floors.filter(f => f.buildingType === 'main building').length
                              }}
                              wallInsulationType={formData.wallInsulationType}
                              initialPlacedElements={elevationData[elevation.id]?.placedElements?.filter(el => 
                                ['window', 'door', 'airvent'].includes(el.type)
                              ) as Array<{
                                id: string;
                                type: 'window' | 'door' | 'airvent';
                                x: number;
                                y: number;
                                elevation: string;
                                height?: number;
                                width?: number;
                              }> || []}
                              initialDimensions={elevationData[elevation.id]?.dimensions}
                              floorPlanWindows={
                                // Use the same floors/rooms data structure as the dimensions section
                                floors.flatMap(floor => 
                                  floor.rooms
                                    .filter(room => 
                                      // Only include rooms that have window data (same logic as dimensions section)
                                      ((room.windowWidth && room.windowWidth.trim() !== '' && room.windowWidth !== '0') || 
                                       (room.windowHeight && room.windowHeight.trim() !== '' && room.windowHeight !== '0') || 
                                       (room.windows && room.windows.length > 0 && room.windows.some(w => 
                                         (w.width && w.width.trim() !== '' && w.width !== '0') || 
                                         (w.height && w.height.trim() !== '' && w.height !== '0') || 
                                         (w.orientation && w.orientation.trim() !== '')
                                       )))
                                    )
                                    .flatMap(room => {
                                      const roomWindows: Array<{
                                        id: string;
                                        width: number;
                                        height: number;
                                        floor: number;
                                        orientation: string;
                                        roomName: string;
                                      }> = [];
                                      
                                      // Priority 1: Use windows from room.windows array (multiple windows)
                                      if (room.windows && room.windows.length > 0) {
                                        room.windows.forEach((window, index) => {
                                          if ((window.width && window.width.trim() !== '' && window.width !== '0') || 
                                              (window.height && window.height.trim() !== '' && window.height !== '0')) {
                                            roomWindows.push({
                                              id: `${floor.id}-${room.id}-${index}`,
                                              width: parseFloat(window.width || '1.5') * 1000, // Convert to mm
                                              height: parseFloat(window.height || '1.0') * 1000, // Convert to mm
                                              floor: floor.id,
                                              orientation: window.orientation || 'Unknown',
                                              roomName: room.name || 'Unknown Room'
                                            });
                                          }
                                        });
                                      }
                                      // Priority 2: Fallback to single window properties (if no windows array)
                                      else if (room.windowWidth || room.windowHeight) {
                                        roomWindows.push({
                                          id: `${floor.id}-${room.id}-main`,
                                          width: parseFloat(room.windowWidth || '1.5') * 1000, // Convert to mm
                                          height: parseFloat(room.windowHeight || '1.0') * 1000, // Convert to mm
                                          floor: floor.id,
                                          orientation: (room as any).windowOrientation || 'Unknown',
                                          roomName: room.name || 'Unknown Room'
                                        });
                                      }
                                      
                                      return roomWindows;
                                    })
                                )
                              }
                              walls={walls}
                              roomCustomLabels={roomCustomLabels}
                              currentRooms={[]}
                              onElevationDataChange={handleElevationDataChange}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Global Property-Wide Calculations */}
                  {formData.wallInsulationElevations && formData.wallInsulationElevations.length > 0 && Object.keys(elevationData).length > 0 && (
                    <div className="mt-8 p-6 bg-blue-50 rounded-lg border-2 border-blue-200">
                      <h4 className="text-xl font-semibold text-blue-900 mb-6 text-center">
                        Property-Wide Wall Insulation Calculations
                      </h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="bg-white p-4 rounded-lg border border-blue-200 shadow-sm">
                          <div className="text-sm font-medium text-blue-700 mb-2">Total Dwelling Area</div>
                          <div className="text-2xl font-bold text-blue-900">
                            {globalCalculations.totalDwellingArea.toFixed(2)}m²
                          </div>
                          <div className="text-xs text-blue-600 mt-1">Sum of all elevation areas</div>
                        </div>
                        
                        <div className="bg-white p-4 rounded-lg border border-blue-200 shadow-sm">
                          <div className="text-sm font-medium text-red-700 mb-2">Total Untreatable Area</div>
                          <div className="text-2xl font-bold text-red-900">
                            {globalCalculations.totalUntreatableArea.toFixed(2)}m²
                          </div>
                          <div className="text-xs text-red-600 mt-1">Windows & Doors across all elevations</div>
                        </div>
                        
                        <div className="bg-white p-4 rounded-lg border border-blue-200 shadow-sm">
                          <div className="text-sm font-medium text-green-700 mb-2">Total Treatable Area</div>
                          <div className="text-2xl font-bold text-green-900">
                            {globalCalculations.totalTreatableArea.toFixed(2)}m²
                          </div>
                          <div className="text-xs text-green-600 mt-1">
                            {globalCalculations.totalDwellingArea.toFixed(2)} - {globalCalculations.totalUntreatableArea.toFixed(2)}
                          </div>
                        </div>
                        
                        <div className="bg-white p-4 rounded-lg border border-blue-200 shadow-sm">
                          <div className="text-sm font-medium text-purple-700 mb-2">POPT</div>
                          <div className="text-2xl font-bold text-purple-900">
                            {globalCalculations.totalWallAreaWholeHouse > 0 
                              ? ((globalCalculations.totalTreatableArea / globalCalculations.totalWallAreaWholeHouse) * 100).toFixed(1)
                              : '0.0'
                            }%
                          </div>
                          <div className="text-xs text-purple-600 mt-1">
                            Proportion of Properly Treatable area
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-6 p-4 bg-blue-100 rounded-lg">
                        <div className="text-sm font-medium text-blue-800 mb-3">Calculation Details:</div>
                        <div className="space-y-2 text-sm text-blue-700">
                          <div><strong>Total Dwelling Area:</strong> Sum of all elevation wall areas (height × width)</div>
                          <div><strong>Total Untreatable Area:</strong> Sum of all windows and doors across all elevations</div>
                          <div><strong>Total Treatable Area:</strong> Total Dwelling Area - Total Untreatable Area</div>
                          <div className="pt-2 border-t border-blue-200">
                            <strong>POPT Formula:</strong> (Total Treatable Area ÷ Total Wall Area of Whole House) × 100 = {globalCalculations.totalWallAreaWholeHouse > 0 
                              ? `(${globalCalculations.totalTreatableArea.toFixed(2)} ÷ ${globalCalculations.totalWallAreaWholeHouse.toFixed(2)}) × 100 = ${((globalCalculations.totalTreatableArea / globalCalculations.totalWallAreaWholeHouse) * 100).toFixed(1)}%`
                              : '0%'
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </SectionCard>
            </div>
          );

      case 'room-in-roof':
        return (
          <div className="space-y-8">
            {/* Room in Roof */}
            <SectionCard icon={Home} title="Room in Roof Construction">
              {/* Toggle Field */}
              <div className="mb-6">
                <ToggleField
                  label="Does the property have a room in the roof?"
                  name="hasRoomInRoof"
                  value={formData.hasRoomInRoof || 'no'}
                  onChange={handleFormChange}
                  options={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' }
                  ]}
                />
              </div>
              
              {/* Show content only when toggle is 'yes' */}
              {formData.hasRoomInRoof === 'yes' ? (
                <>
                  <div className="mb-6">
                    <p className="text-sm text-slate-600 mb-4">
                      Document the construction details of any room in roof spaces, including insulation configuration, 
                      ventilation systems, and key measurements.
                    </p>
                  </div>
                  
                  <RoomInRoofDiagram
                    onDataChange={handleRirDataChange}
                    initialMeasurements={rirData.measurements}
                    initialAnnotations={rirData.annotations}
                    initialInsulationType={rirData.insulationType}
                    initialVentilationType={rirData.ventilationType}
                    initialDiagramType={rirData.diagramType}
                    initialDiagramImage={rirData.diagramImage}
                  />
                  
                  {/* Additional Notes */}
                  <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <h4 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
                      <Lightbulb size={16} className="text-amber-600" />
                      Important Considerations
                    </h4>
                    <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
                      <li>Ensure adequate ventilation to prevent condensation</li>
                      <li>Check insulation continuity at eaves and ridge</li>
                      <li>Verify fire safety requirements are met</li>
                      <li>Consider thermal bridging at structural elements</li>
                      <li>Document access arrangements and maintenance provisions</li>
                    </ul>
                  </div>
                </>
              ) : (
                <div className="p-6 bg-slate-50 rounded-lg border border-slate-200 text-center">
                  <p className="text-sm text-slate-600">
                    Set the toggle above to "Yes" to document room in roof construction details.
                  </p>
                </div>
              )}
            </SectionCard>
          </div>
        );

      case 'windows':
        // Extract window data from floors structure
        const windowData = floors.flatMap(floor => 
          floor.rooms
            .filter(room => 
              // Only include rooms that have window data
              ((room.windowWidth && room.windowWidth.trim() !== '' && room.windowWidth !== '0') || 
               (room.windowHeight && room.windowHeight.trim() !== '' && room.windowHeight !== '0') || 
               (room.windows && room.windows.length > 0 && room.windows.some(w => 
                 (w.width && w.width.trim() !== '' && w.width !== '0') || 
                 (w.height && w.height.trim() !== '' && w.height !== '0')
               )))
            )
            .flatMap(room => {
              const roomWindows = [];
              
              // 🔧 FIX: Use EXACT same logic as Rooms badge to prevent duplicates
              // Priority: windows array > single window data (but NOT both)
              
              // Check if room has windows array (regardless of data)
              const hasWindowsArray = room.windows && room.windows.length > 0;
              
              if (hasWindowsArray && room.windows) {
                // Prioritize multiple windows array - iterate through each window
                room.windows.forEach((window, index) => {
                  // Only add windows that have actual dimension data
                  if ((window.width && window.width.trim() !== '' && window.width !== '0') || 
                      (window.height && window.height.trim() !== '' && window.height !== '0')) {
                    roomWindows.push({
                      id: `${floor.id}-${room.id}-${index}`,
                      width: parseFloat(window.width || '0'),
                      height: parseFloat(window.height || '0'),
                      floor: floor.id,
                      roomName: room.name || `Room ${room.id}`,
                      trickleVents: (window as any).trickleVents || false,
                      orientation: window.orientation || '',
                      glazingGap: (window as any).glazingGap || '',
                      glazingType: (window as any).glazingType || '',
                      windowCategory: (window as any).windowCategory || 'Window',
                      windowType: (window as any).windowType || '',
                      isDraughtProofed: (window as any).isDraughtProofed || false,
                      type: 'multiple',
                      index: index + 1
                    });
                  }
                });
              } else if ((room.windowWidth || room.windowHeight) && !hasWindowsArray) {
                // 🔧 CRITICAL FIX: Only use single window data if NO windows array exists
                // This matches the Rooms badge logic: (!room.windows || room.windows.length === 0)
                roomWindows.push({
                  id: `${floor.id}-${room.id}-main`,
                  width: parseFloat(room.windowWidth || '0'),
                  height: parseFloat(room.windowHeight || '0'),
                  floor: floor.id,
                  roomName: room.name || `Room ${room.id}`,
                  trickleVents: room.windowTrickleVents === 'Yes',
                  orientation: (room as any).windowOrientation || '',
                  glazingGap: (room as any).windowGlazingGap || '',
                  glazingType: (room as any).windowGlazingType || '',
                  windowCategory: (room as any).windowCategory || 'Window',
                  windowType: (room as any).windowType || '',
                  isDraughtProofed: (room as any).windowDraughtProofed === 'Yes',
                  type: 'single'
                });
              }
              
              return roomWindows;
            })
        );

        // Modern display components based on WindowDimensionPrompt modal
        const ModernDisplayCard = ({ title, children, icon }: { title: string; children: React.ReactNode; icon?: React.ReactNode }) => (
          <div className="bg-white rounded-3xl shadow-lg border border-slate-200/50 overflow-hidden">
            <div className="relative bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 px-4 py-3">
              <div className="flex items-center space-x-2">
                {icon && (
                  <div className="p-1 bg-white/20 rounded-lg backdrop-blur-sm">
                    {icon}
                  </div>
                )}
                <div>
                  <h3 className="text-sm font-bold text-white">{title}</h3>
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/0 via-white/5 to-indigo-600/0 pointer-events-none"></div>
            </div>
            <div className="p-4">
              {children}
            </div>
          </div>
        );

        const ModernDataDisplay = ({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) => (
          <div className="space-y-1">
            <label className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
              {icon && <span className="text-slate-500">{icon}</span>}
              <span>{label}</span>
            </label>
            <div className="px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-800 font-medium">
              {value || 'Not specified'}
            </div>
          </div>
        );

        const ModernToggleDisplay = ({ label, value, icon }: { label: string; value: boolean; icon?: React.ReactNode }) => (
          <div className="space-y-1">
            <label className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
              {icon && <span className="text-slate-500">{icon}</span>}
              <span>{label}</span>
            </label>
            <div className="flex items-center space-x-2">
              <div className={`inline-flex h-5 w-10 items-center justify-center rounded-full border-2 ${
                value 
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 border-blue-500' 
                  : 'bg-slate-200 border-slate-300'
              }`}>
                <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
                  value ? 'translate-x-1.5' : '-translate-x-1.5'
                }`} />
              </div>
              <span className={`text-xs font-medium ${value ? 'text-blue-600' : 'text-slate-500'}`}>
                {value ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        );

        const ModernBadgeDisplay = ({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) => (
          <div className="space-y-1">
            <label className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
              {icon && <span className="text-slate-500">{icon}</span>}
              <span>{label}</span>
            </label>
            <div className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
              {value || 'Not specified'}
            </div>
          </div>
        );

        return (
          <div className="space-y-6">
            {/* Summary Card */}
            <ModernDisplayCard 
              title="Windows Summary" 
              icon={
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 00-2-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 00-2-2z" />
                </svg>
              }
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                  <div className="text-2xl font-bold text-blue-900 mb-1">{windowData.length}</div>
                  <div className="text-xs text-blue-700 font-medium">Total Windows</div>
                </div>
                <div className="text-center p-3 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
                  <div className="text-2xl font-bold text-green-900 mb-1">
                    {windowData.filter(w => w.trickleVents).length}
                  </div>
                  <div className="text-xs text-green-700 font-medium">With Trickle Vents</div>
                </div>
                <div className="text-center p-3 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200">
                  <div className="text-2xl font-bold text-purple-900 mb-1">
                    {new Set(windowData.map(w => w.floor)).size}
                  </div>
                  <div className="text-xs text-purple-700 font-medium">Floors Covered</div>
                </div>
              </div>
            </ModernDisplayCard>

            {/* Individual Windows */}
            {windowData.length > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center space-x-2 mb-4">
                  <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <h3 className="text-lg font-bold text-slate-800">Window Details</h3>
                </div>
                
                {windowData.map((window) => (
                  <ModernDisplayCard 
                    key={window.id}
                    title={`${window.roomName} - Floor ${window.floor}${window.type === 'multiple' ? ` (Window ${(window as any).index})` : ''}`}
                    icon={
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    }
                  >
                    <div className="space-y-4">
                      {/* Dimensions Section */}
                      <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                        <h4 className="text-xs font-bold text-slate-800 mb-3 flex items-center">
                          <svg className="w-3.5 h-3.5 mr-1.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                          </svg>
                          Dimensions
                        </h4>
                        <div className="grid grid-cols-2 gap-3">
                          <ModernDataDisplay
                            label="Width"
                            value={window.width > 0 ? `${window.width.toFixed(2)}m` : 'Not specified'}
                            icon={
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                              </svg>
                            }
                          />
                          <ModernDataDisplay
                            label="Height"
                            value={window.height > 0 ? `${window.height.toFixed(2)}m` : 'Not specified'}
                            icon={
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7l4-4m0 0l4 4m-4-4v18" />
                              </svg>
                            }
                          />
                        </div>
                      </div>

                      {/* Window Type & Glazing */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ModernBadgeDisplay
                          label="Window Type"
                          value={window.windowCategory}
                          icon={
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          }
                        />
                        <ModernBadgeDisplay
                          label="Frame Material"
                          value={window.windowType ? window.windowType.toUpperCase() : 'Not specified'}
                          icon={
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                            </svg>
                          }
                        />
                      </div>

                      {/* Glazing Information */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ModernDataDisplay
                          label="Glazing Type"
                          value={window.glazingType}
                          icon={
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          }
                        />
                        <ModernDataDisplay
                          label="Glazing Gap"
                          value={window.glazingGap}
                          icon={
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                            </svg>
                          }
                        />
                      </div>

                      {/* Properties & Orientation */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <ModernToggleDisplay
                          label="Trickle Vents"
                          value={window.trickleVents}
                          icon={
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          }
                        />
                        <ModernToggleDisplay
                          label="Draught Proofed"
                          value={window.isDraughtProofed}
                          icon={
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                          }
                        />
                        <ModernBadgeDisplay
                          label="Orientation"
                          value={window.orientation || 'Not calculated'}
                          icon={
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 013.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                            </svg>
                          }
                        />
                      </div>
                    </div>
                  </ModernDisplayCard>
                ))}
              </div>
            ) : (
              <ModernDisplayCard 
                title="No Windows Found" 
                icon={
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5C3.312 17.333 4.773 19 6.314 19z" />
                  </svg>
                }
              >
                <div className="text-center py-8">
                  <div className="text-slate-500 mb-2">
                    <svg className="w-16 h-16 mx-auto mb-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="text-slate-600 font-medium mb-2">No window data available</p>
                  <p className="text-xs text-slate-500">Windows will appear here once they are added to your floor plan</p>
                </div>
              </ModernDisplayCard>
            )}
          </div>
        );

      case 'heating':
        return (
          <div className="space-y-8">
            {/* Legacy Single Heating System - Show if no multiple systems exist */}
            {heatingSystems.length === 0 && (
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-slate-200/30 shadow-xl hover:shadow-2xl transition-all duration-500">
                {/* Modern Section Header */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <div className="p-4 bg-gradient-to-br from-orange-600 to-red-600 rounded-2xl shadow-lg">
                        <Thermometer className="w-7 h-7 text-white" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full border-3 border-white shadow-sm"></div>
                    </div>
                    <div>
                      <h3 className="text-2xl font-black text-slate-800 tracking-tight">Primary Heating System</h3>
                      <p className="text-sm font-medium text-slate-600 mt-1">Main heating configuration & specifications</p>
                    </div>
                  </div>
                </div>

                {/* Enhanced PCDB Search with Modern Styling */}
                <div className="relative z-50 bg-gradient-to-br from-orange-50/60 via-red-50/60 to-orange-50/60 backdrop-blur-sm rounded-3xl p-6 border border-orange-200/50 shadow-lg mb-6">
                  <div className="flex items-center mb-4">
                    <div className="p-2 bg-gradient-to-br from-orange-600 to-red-600 rounded-xl shadow-md mr-3">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-bold text-slate-800">System Database Search</h4>
                  </div>
                  <PCDBSearch
                    key={selectedPCDBSystem ? `pcdb-${selectedPCDBSystem.id}` : 'pcdb-empty'}
                    onSelect={handlePCDBSystemSelect}
                    selectedSystem={selectedPCDBSystem}
                    placeholder="Search by brand, model, or type..."
                    className="w-full"
                    onManualEntryChange={handleManualHeatingEntryChange}
                    isManualEntry={isManualHeatingEntry}
                  />
                </div>

                {/* Manual Entry Fields - Only show when manual entry is enabled */}
                {isManualHeatingEntry && (
                  <div className="bg-gradient-to-br from-slate-50/60 via-gray-50/60 to-slate-50/60 backdrop-blur-sm rounded-3xl p-6 border border-slate-200/50 shadow-lg">
                    <div className="flex items-center mb-6">
                      <div className="p-2 bg-gradient-to-br from-slate-600 to-gray-600 rounded-xl shadow-md mr-3">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-bold text-slate-800">Manual System Configuration</h4>
                    </div>
                    
                    <SelectField
                      label="Main Heating Source"
                      name="heatingType"
                      value={formData.heatingType}
                      onChange={handleFormChange}
                      options={heatingTypeOptions}
                      required
                    />
                    <SelectField
                      label="Heating System Type"
                      name="heatingSystemType"
                      value={formData.heatingSystemType}
                      onChange={handleFormChange}
                      options={heatingSystemTypeOptions}
                      required
                    />
                    <SelectField
                      label="Fuel Type"
                      name="heatingFuelType"
                      value={formData.heatingFuelType}
                      onChange={handleFormChange}
                      options={heatingFuelTypeOptions}
                      required
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField 
                        label="Heating System Brand" 
                        name="heatingBrand" 
                        value={formData.heatingBrand} 
                        onChange={handleFormChange} 
                        placeholder="e.g., Worcester Bosch"
                      />
                      <FormField 
                        label="Heating System Model" 
                        name="heatingModel" 
                        value={formData.heatingModel} 
                        onChange={handleFormChange} 
                        placeholder="e.g., Greenstar 30i"
                      />
                    </div>
                    <FormField 
                      label="Serial Number / GC Number" 
                      name="heatingSerialNumber" 
                      value={formData.heatingSerialNumber} 
                      onChange={handleFormChange} 
                      placeholder="Enter serial or GC number"
                    />

                    {/* Additional SAP Fields for Legacy System */}
                    <SelectField
                      label="Heating Control"
                      name="heatingControl"
                      value={formData.heatingControl || ''}
                      onChange={handleFormChange}
                      options={heatingControlOptions}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField 
                        label="Winter Efficiency (%)" 
                        name="winterEfficiency"
                        value={formData.winterEfficiency?.toString() || ''} 
                        onChange={handleFormChange}
                        placeholder="e.g., 92.5"
                      />
                      <FormField 
                        label="Summer Efficiency (%)" 
                        name="summerEfficiency"
                        value={formData.summerEfficiency?.toString() || ''} 
                        onChange={handleFormChange}
                        placeholder="e.g., 89.0"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <FormField 
                        label="Flow Temperature (°C)" 
                        name="flowTemperature"
                        value={formData.flowTemperature?.toString() || ''} 
                        onChange={handleFormChange}
                        placeholder="e.g., 55"
                      />
                      <ToggleField 
                        label="Condensing System" 
                        name="isCondensing" 
                        value={formData.isCondensing === true ? 'yes' : formData.isCondensing === false ? 'no' : ''} 
                        onChange={handleFormChange} 
                        options={[
                          { value: '', label: 'Not specified' },
                          { value: 'yes', label: 'Yes' },
                          { value: 'no', label: 'No' }
                        ]}
                      />
                      <ToggleField 
                        label="Low Temperature System" 
                        name="isLowTemperature" 
                        value={formData.isLowTemperature === true ? 'yes' : formData.isLowTemperature === false ? 'no' : ''} 
                        onChange={handleFormChange} 
                        options={[
                          { value: '', label: 'Not specified' },
                          { value: 'yes', label: 'Yes' },
                          { value: 'no', label: 'No' }
                        ]}
                      />
                    </div>
                  </div>
                )}

                {/* Common Fields - Always show */}
                <div className="bg-gradient-to-br from-indigo-50/60 via-blue-50/60 to-indigo-50/60 backdrop-blur-sm rounded-3xl p-6 border border-indigo-200/50 shadow-lg">
                  <div className="flex items-center mb-6">
                    <div className="p-2 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-xl shadow-md mr-3">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-bold text-slate-800">System Configuration</h4>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <CounterField 
                      label="Total Radiators" 
                      name="totalRads" 
                      value={formData.totalRads} 
                      onChange={handleFormChange} 
                      max={50}
                    />
                    <CounterField 
                      label="Radiators with TRVs" 
                      name="trvs" 
                      value={formData.trvs} 
                      onChange={handleFormChange} 
                      max={50}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <FormField 
                      label="Heating Source Location" 
                      name="heatingSourceLocation" 
                      value={formData.heatingSourceLocation} 
                      onChange={handleFormChange} 
                      placeholder="e.g., Kitchen cupboard, Garage, etc."
                    />
                    <FormField 
                      label="Tank Location" 
                      name="tankLocation" 
                      value={formData.tankLocation} 
                      onChange={handleFormChange} 
                      placeholder="e.g., Loft, Airing cupboard, etc."
                    />
                  </div>
                </div>
              </div>
            )}
            {heatingSystems.length > 0 && (
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-slate-200/30 shadow-xl hover:shadow-2xl transition-all duration-500">
                {/* Modern Section Header */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                    <div className="p-4 bg-gradient-to-br from-orange-600 to-red-600 rounded-2xl shadow-lg">
                      <Thermometer className="w-7 h-7 text-white" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full border-3 border-white shadow-sm flex items-center justify-center">
                      <span className="text-xs font-bold text-white">{heatingSystems.length}</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-slate-800 tracking-tight">Heating Systems</h3>
                    <p className="text-sm font-medium text-slate-600 mt-1">
                      {heatingSystems.length === 0 ? 'No heating systems configured' : `${heatingSystems.length} system${heatingSystems.length > 1 ? 's' : ''} configured`}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={addHeatingSystem}
                  className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-2xl hover:from-orange-600 hover:to-red-600 transition-all duration-300 text-sm font-semibold flex items-center gap-3 shadow-lg hover:shadow-xl hover:scale-105"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Heating System
                </button>
              </div>

              <div className="space-y-6">

                {/* Heating Systems List */}
                {heatingSystems.map((system, index) => (
                  <div key={system.id} className="relative z-40 bg-gradient-to-br from-orange-50/60 via-red-50/60 to-orange-50/60 backdrop-blur-sm rounded-3xl p-6 border border-orange-200/50 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center space-x-4">
                        <div className="relative">
                          <div className="p-3 bg-gradient-to-br from-orange-600 to-red-600 rounded-2xl shadow-lg">
                            <Thermometer className="w-6 h-6 text-white" />
                          </div>
                          {system.isPrimary && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full border-2 border-white shadow-sm flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div>
                          <h5 className="text-xl font-bold text-slate-800">System {index + 1}</h5>
                          <p className="text-sm text-slate-600">
                            {system.isPrimary ? 'Primary heating system' : 'Secondary heating system'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {!system.isPrimary && (
                          <button
                            type="button"
                            onClick={() => setPrimaryHeatingSystem(system.id)}
                            className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-4 py-2 rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all duration-300 text-sm font-semibold shadow-md hover:shadow-lg hover:scale-105"
                          >
                            Set Primary
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeHeatingSystem(system.id)}
                          className="bg-gradient-to-r from-red-500 to-red-600 text-white p-2.5 rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* PCDB Search for this system */}
                    <PCDBSearch
                      key={system.pcdbSystem ? `heating-pcdb-${system.id}-${system.pcdbSystem.id}` : `heating-pcdb-${system.id}-empty`}
                      onSelect={(pcdbSystem) => handleHeatingSystemPCDBSelect(system.id, pcdbSystem)}
                      selectedSystem={system.pcdbSystem ? {
                        id: system.pcdbSystem.id,
                        brand: system.pcdbSystem.brand,
                        model: system.pcdbSystem.model,
                        model_qualifier: system.pcdbSystem.model_qualifier,
                        type: system.pcdbSystem.type,
                        fuel: system.pcdbSystem.fuel,
                        main_type: system.pcdbSystem.main_type,
                        output_power_kw: system.pcdbSystem.output_power_kw,
                        sap_winter_seasonal_efficiency: system.pcdbSystem.sap_winter_seasonal_efficiency,
                        sap_summer_seasonal_efficiency: system.pcdbSystem.sap_summer_seasonal_efficiency,
                        efficiency_category: system.pcdbSystem.efficiency_category,
                        condensing: system.pcdbSystem.condensing,
                        flue_type: system.pcdbSystem.flue_type,
                        fan_assisted: system.pcdbSystem.fan_assisted,
                        year_of_commission: system.pcdbSystem.year_of_commission,
                        features: system.pcdbSystem.features,
                        certifications: system.pcdbSystem.certifications,
                        pcdb_reference: system.pcdbSystem.pcdb_reference,
                        boiler_id: system.pcdbSystem.boiler_id,
                        last_updated: system.pcdbSystem.last_updated,
                        is_discontinued: system.pcdbSystem.is_discontinued,
                        replacement_models: system.pcdbSystem.replacement_models,
                        installation_type: system.pcdbSystem.installation_type
                      } : null}
                      placeholder="Search by brand, model, or type..."
                      className="w-full"
                      onManualEntryChange={(isManual) => handleHeatingSystemManualEntryChange(system.id, isManual)}
                      isManualEntry={system.isManualEntry || false}
                    />

                    {/* Manual Entry Fields - Only show when manual entry is enabled */}
                    {system.isManualEntry && (
                      <div className="bg-gradient-to-br from-slate-50/60 via-gray-50/60 to-slate-50/60 backdrop-blur-sm rounded-3xl p-6 border border-slate-200/50 shadow-lg mb-6">
                        <div className="flex items-center mb-6">
                          <div className="p-2 bg-gradient-to-br from-slate-600 to-gray-600 rounded-xl shadow-md mr-3">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-bold text-slate-800">Manual System Configuration</h4>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <SelectField
                            label="Heating Source"
                            name={`heatingType-${system.id}`}
                            value={system.heatingType}
                            onChange={(e) => updateHeatingSystem(system.id, { heatingType: e.target.value })}
                            options={heatingTypeOptions}
                          />
                          <SelectField
                            label="System Type"
                            name={`heatingSystemType-${system.id}`}
                            value={system.heatingSystemType}
                            onChange={(e) => updateHeatingSystem(system.id, { heatingSystemType: e.target.value })}
                            options={heatingSystemTypeOptions}
                          />
                          <SelectField
                            label="Fuel Type"
                            name={`fuelType-${system.id}`}
                            value={system.heatingFuelType || ''}
                            onChange={(e) => updateHeatingSystem(system.id, { heatingFuelType: e.target.value })}
                            options={heatingFuelTypeOptions}
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField 
                            label="Brand" 
                            name={`heatingBrand-${system.id}`}
                            value={system.heatingBrand} 
                            onChange={(e) => updateHeatingSystem(system.id, { heatingBrand: e.target.value })}
                            placeholder="e.g., Worcester Bosch"
                          />
                          <FormField 
                            label="Model" 
                            name={`heatingModel-${system.id}`}
                            value={system.heatingModel} 
                            onChange={(e) => updateHeatingSystem(system.id, { heatingModel: e.target.value })}
                            placeholder="e.g., Greenstar 30i"
                          />
                        </div>

                        <FormField 
                          label="Serial Number / GC Number" 
                          name={`heatingSerialNumber-${system.id}`}
                          value={system.heatingSerialNumber} 
                          onChange={(e) => updateHeatingSystem(system.id, { heatingSerialNumber: e.target.value })}
                          placeholder="Enter serial or GC number"
                        />

                        {/* Additional SAP Fields */}
                        <SelectField
                          label="Heating Control"
                          name={`heatingControl-${system.id}`}
                          value={system.heatingControl || ''}
                          onChange={(e) => updateHeatingSystem(system.id, { heatingControl: e.target.value })}
                          options={heatingControlOptions}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField 
                            label="Flow Temperature (°C)" 
                            name={`flowTemperature-${system.id}`}
                            value={system.flowTemperature?.toString() || ''} 
                            onChange={(e) => updateHeatingSystem(system.id, { flowTemperature: e.target.value ? parseFloat(e.target.value) : undefined })}
                            placeholder="e.g., 55"
                          />
                          <FormField
                            label="System Serial Number"
                            name={`serialNumber-${system.id}`}
                            value={system.heatingSerialNumber || ''}
                            onChange={(e) => updateHeatingSystem(system.id, { heatingSerialNumber: e.target.value })}
                            placeholder="Enter serial number"
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <ToggleField 
                            label="Condensing System" 
                            name={`isCondensing-${system.id}`} 
                            value={system.isCondensing === true ? 'yes' : system.isCondensing === false ? 'no' : ''} 
                            onChange={(e) => updateHeatingSystem(system.id, { isCondensing: e.target.value === 'yes' ? true : e.target.value === 'no' ? false : undefined })} 
                            options={[
                              { value: '', label: 'Not specified' },
                              { value: 'yes', label: 'Yes' },
                              { value: 'no', label: 'No' }
                            ]}
                          />
                          <ToggleField 
                            label="Low Temperature System" 
                            name={`isLowTemperature-${system.id}`} 
                            value={system.isLowTemperature === true ? 'yes' : system.isLowTemperature === false ? 'no' : ''} 
                            onChange={(e) => updateHeatingSystem(system.id, { isLowTemperature: e.target.value === 'yes' ? true : e.target.value === 'no' ? false : undefined })} 
                            options={[
                              { value: '', label: 'Not specified' },
                              { value: 'yes', label: 'Yes' },
                              { value: 'no', label: 'No' }
                            ]}
                          />
                        </div>
                      </div>
                    )}

                    {/* PCDB System Info Display - Show when not in manual mode */}
                    {!system.isManualEntry && system.pcdbSystem && (
                      <div className="bg-gradient-to-br from-green-50/80 via-emerald-50/80 to-green-50/80 backdrop-blur-sm rounded-3xl p-6 border border-green-200/50 shadow-lg mb-6">
                        <div className="flex items-center mb-4">
                          <div className="p-2 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl shadow-md mr-3">
                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-bold text-green-800">PCDB Verified System</h4>
                        </div>
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Brand</p>
                              <p className="text-sm font-bold text-gray-900 mt-1">{system.pcdbSystem.brand}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Model</p>
                              <p className="text-sm font-bold text-gray-900 mt-1">{system.pcdbSystem.model}</p>
                            </div>
                          </div>
                          {system.pcdbSystem.model_qualifier && (
                            <div>
                              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Model Qualifier</p>
                              <p className="text-sm font-bold text-gray-900 mt-1">{system.pcdbSystem.model_qualifier}</p>
                            </div>
                          )}
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Type</p>
                              <p className="text-sm font-semibold text-gray-900 mt-1">{system.pcdbSystem.installation_type || system.pcdbSystem.type}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Fuel</p>
                              <p className="text-sm font-semibold text-gray-900 mt-1">{system.pcdbSystem.fuel || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Power</p>
                              <p className="text-sm font-semibold text-gray-900 mt-1">{system.pcdbSystem.output_power_kw ? `${system.pcdbSystem.output_power_kw} kW` : 'N/A'}</p>
                            </div>
                          </div>
                          {(system.pcdbSystem.sap_winter_seasonal_efficiency || system.pcdbSystem.sap_summer_seasonal_efficiency) && (
                            <div className="grid grid-cols-2 gap-4 pt-3 border-t border-green-200">
                              {system.pcdbSystem.sap_winter_seasonal_efficiency && (
                                <div>
                                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Winter Efficiency</p>
                                  <p className="text-sm font-bold text-green-700 mt-1">{system.pcdbSystem.sap_winter_seasonal_efficiency}%</p>
                                </div>
                              )}
                              {system.pcdbSystem.sap_summer_seasonal_efficiency && (
                                <div>
                                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Summer Efficiency</p>
                                  <p className="text-sm font-bold text-green-700 mt-1">{system.pcdbSystem.sap_summer_seasonal_efficiency}%</p>
                                </div>
                              )}
                            </div>
                          )}
                          {system.pcdbSystem.pcdb_reference && (
                            <div className="pt-3 border-t border-green-200">
                              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">PCDB Reference</p>
                              <p className="text-xs font-mono text-gray-700 mt-1 bg-white px-2 py-1 rounded">{system.pcdbSystem.pcdb_reference}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Common Fields - Always show */}
                    <div className="bg-gradient-to-br from-indigo-50/60 via-blue-50/60 to-indigo-50/60 backdrop-blur-sm rounded-3xl p-6 border border-indigo-200/50 shadow-lg">
                      <div className="flex items-center mb-6">
                        <div className="p-2 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-xl shadow-md mr-3">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-bold text-slate-800">System Configuration</h4>
                      </div>

                      {system.isRadiatorCountManuallyOverridden && (
                        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-4 mb-6 shadow-md">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="p-2 bg-amber-100 rounded-xl mr-3">
                                <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 15.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                              </div>
                              <div>
                                <span className="text-sm font-bold text-amber-800">Manual Override Active</span>
                                <p className="text-xs text-amber-700 mt-1">
                                  Radiator counts won't sync from floor plan automatically
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => resetRadiatorCountOverride(system.id)}
                              className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-4 py-2 rounded-xl hover:from-amber-600 hover:to-yellow-600 transition-all duration-300 text-sm font-semibold shadow-md hover:shadow-lg hover:scale-105"
                            >
                              Reset to Auto
                            </button>
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-4">
                        <CounterField 
                          label="Total Radiators" 
                          name={`totalRads-${system.id}`}
                          value={system.totalRads} 
                          onChange={(e) => handleRadiatorCountChange(system.id, 'totalRads', e.target.value)}
                          max={50}
                        />
                        <CounterField 
                          label="TRVs" 
                          name={`trvs-${system.id}`}
                          value={system.trvs} 
                          onChange={(e) => handleRadiatorCountChange(system.id, 'trvs', e.target.value)}
                          max={50}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <FormField 
                        label="Heating Source Location" 
                        name={`heatingSourceLocation-${system.id}`}
                        value={system.heatingSourceLocation} 
                        onChange={(e) => updateHeatingSystem(system.id, { heatingSourceLocation: e.target.value })}
                        placeholder="e.g., Kitchen cupboard, Garage, etc."
                      />
                      <FormField 
                        label="Tank Location" 
                        name={`tankLocation-${system.id}`}
                        value={system.tankLocation} 
                        onChange={(e) => updateHeatingSystem(system.id, { tankLocation: e.target.value })}
                        placeholder="e.g., Loft, Airing cupboard, etc."
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}
          </div>
        );

      case 'ventilation':
        return (
          <div className="space-y-8">
            {/* MEVS */}
            <SectionCard icon={Settings} title="MEVS (Mechanical Extract Ventilation)">
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    <ToggleField 
                      label="Kitchen" 
                      name="mevsKitchen" 
                      value={formData.mevsKitchen} 
                      onChange={handleFormChange} 
                      options={yesNoOptions}
                    />
                    <ToggleField 
                      label="Bathroom" 
                      name="mevsBathroom" 
                      value={formData.mevsBathroom} 
                      onChange={handleFormChange} 
                      options={yesNoOptions}
                    />
                    <ToggleField 
                      label="En-Suite" 
                      name="mevsEnSuite" 
                      value={formData.mevsEnSuite} 
                      onChange={handleFormChange} 
                      options={yesNoOptions}
                    />
                    <ToggleField 
                      label="WC" 
                      name="mevsWC" 
                      value={formData.mevsWC} 
                      onChange={handleFormChange} 
                      options={yesNoOptions}
                    />
                    <ToggleField 
                      label="Utility" 
                      name="mevsUtility" 
                      value={formData.mevsUtility} 
                      onChange={handleFormChange} 
                      options={yesNoOptions}
                    />
                  </div>
                </SectionCard>

            {/* Additional Ventilation */}
            <SectionCard icon={Wind} title="Additional Ventilation">
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <CounterField 
                  label="Open Chimneys" 
                  name="openChimneys" 
                  value={formData.openChimneys} 
                  onChange={handleFormChange} 
                  max={50}
                />
                <CounterField 
                  label="Open Flues" 
                  name="openFlues" 
                  value={formData.openFlues} 
                  onChange={handleFormChange} 
                  max={50}
                />
                <CounterField 
                  label="Open Chimneys/Flues Attached to Close" 
                  name="openChimneysFluesAttachedToClose" 
                  value={formData.openChimneysFluesAttachedToClose} 
                  onChange={handleFormChange} 
                  max={50}
                />
                <CounterField 
                  label="Flues Attached to Solid Fuel Boiler" 
                  name="fluesAttachedToSolidFuelBoiler" 
                  value={formData.fluesAttachedToSolidFuelBoiler} 
                  onChange={handleFormChange} 
                  max={50}
                />
                <CounterField 
                  label="Open Flues Attached to Other Heater" 
                  name="openFluesAttachedToOtherHeater" 
                  value={formData.openFluesAttachedToOtherHeater} 
                  onChange={handleFormChange} 
                  max={50}
                />
                <CounterField 
                  label="Blocked Chimneys" 
                  name="blockedChimneys" 
                  value={formData.blockedChimneys} 
                  onChange={handleFormChange} 
                  max={50}
                />
                <CounterField 
                  label="Intermittent Extract Fans" 
                  name="intermittentExtractFans" 
                  value={formData.intermittentExtractFans} 
                  onChange={handleFormChange} 
                  max={50}
                />
                <CounterField 
                  label="Passive Vents" 
                  name="passiveVents" 
                  value={formData.passiveVents} 
                  onChange={handleFormChange} 
                  max={50}
                />
                <CounterField 
                  label="Flueless Gas Fires" 
                  name="fluelessGasFires" 
                  value={formData.fluelessGasFires} 
                  onChange={handleFormChange} 
                  max={50}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <ToggleField 
                  label="Fixed Space Cooling" 
                  name="fixedSpaceCooling" 
                  value={formData.fixedSpaceCooling} 
                  onChange={handleFormChange} 
                  options={yesNoOptions}
                />
                <SelectField
                  label="Draught Lobby"
                  name="draughtLobby"
                  value={formData.draughtLobby}
                  onChange={handleFormChange}
                  options={[
                    { value: '', label: 'Select option' },
                    { value: 'not-present', label: 'Not present' },
                    { value: 'present', label: 'Present' },
                    { value: 'partial', label: 'Partial' }
                  ]}
                />
              </div>
            </SectionCard>
            </div>
          );


      case 'utilities':
        return (
          <div className="space-y-8">
            {/* Doors & Utilities */}
            <SectionCard icon={Home} title="Doors & Utilities">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <CounterField 
                        label="Total Doors" 
                        name="totalDoors" 
                        value={formData.totalDoors} 
                        onChange={handleFormChange} 
                        max={50}
                      />
                      <CounterField 
                        label="Doors w/o Undercuts" 
                        name="doorsWithoutUndercuts" 
                        value={formData.doorsWithoutUndercuts} 
                        onChange={handleFormChange} 
                        max={50}
                      />
                    </div>
                    <FormField 
                      label="Location of Doors with No Undercuts" 
                      name="doorsWithoutUndercutsLocation" 
                      value={formData.doorsWithoutUndercutsLocation} 
                      onChange={handleFormChange} 
                      placeholder="e.g., Bedroom 1, Living room, Kitchen, etc."
                    />
                  </div>
                  <div className="space-y-6">
                    <FormField 
                      label="Gas Meter Location" 
                      name="gmLocation" 
                      value={formData.gmLocation} 
                      onChange={handleFormChange} 
                      placeholder="e.g., Front garden, Side of house, etc."
                    />
                    <FormField 
                      label="Electric Meter Location" 
                      name="emLocations" 
                      value={formData.emLocations} 
                      onChange={handleFormChange} 
                      placeholder="e.g., Front porch, Garage, etc."
                    />
                    <FormField 
                      label="Stop Tap Location" 
                      name="stapLocation" 
                      value={formData.stapLocation} 
                      onChange={handleFormChange} 
                      placeholder="e.g., Kitchen sink, Under stairs, etc."
                    />
                  </div>
                </div>
            </SectionCard>

            {/* Lighting Summary - Moved from separate tab */}
            <SectionCard icon={Lightbulb} title="Lighting Summary">
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <Lightbulb className="text-amber-600" size={24} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-amber-800">Low Energy Lights</h4>
                        <p className="text-sm text-amber-600">Energy efficient lighting (X)</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <FormField 
                        label="Total Count" 
                        name="totalLightsX" 
                        value={formData.totalLightsX} 
                        onChange={handleFormChange} 
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-orange-50 to-red-50 border border-orange-200 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <Lightbulb className="text-orange-600" size={24} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-orange-800">Non-Low Energy Lights</h4>
                        <p className="text-sm text-orange-600">Standard lighting (O)</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <FormField 
                        label="Total Count" 
                        name="totalLightsO" 
                        value={formData.totalLightsO} 
                        onChange={handleFormChange} 
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Total Summary */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-blue-100 rounded-xl">
                        <Database className="text-blue-600" size={28} />
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-blue-800">Total Lighting Points</h4>
                        <p className="text-sm text-blue-600">Combined count from floor plan</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-blue-800">
                        {(parseInt(formData.totalLightsX || '0') + parseInt(formData.totalLightsO || '0'))}
                      </div>
                      <div className="text-sm text-blue-600">lighting points</div>
                    </div>
                  </div>
                </div>

                {/* Additional Notes */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-800 flex items-center gap-2">
                    <Edit3 size={18} />
                    Lighting Notes
                  </h4>
                  <textarea 
                    name="additionalNotes" 
                    value={formData.additionalNotes || ''} 
                    onChange={handleFormChange} 
                    rows={4} 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent placeholder-gray-400 bg-gray-50"
                    placeholder="Any additional lighting observations or notes..."
                  />
                </div>
              </div>
            </SectionCard>
            </div>
          );

      case 'rooms':
        return (
          <div className="space-y-8">
            {/* Rooms */}
            <div className="space-y-6">
              {floors.map((floor, index) => {
                  const floorLevelLabel = floorLevelOptions.find(option => option.value === floor.floorLevel)?.label || 'Ground Floor';
                  const buildingTypeLabel = floor.buildingType === 'main building' ? 'Main Building' : 
                                          floor.buildingType.charAt(0).toUpperCase() + floor.buildingType.slice(1);
                  
                  const floorName = `${floorLevelLabel} - ${buildingTypeLabel}`;
                  
                  return (
                  <div key={floor.id} className="">
                    {/* Modern Floor Header */}
                    <div className="flex items-center justify-between mb-6 bg-white/60 rounded-xl p-4 border border-slate-200">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-gradient-to-r from-slate-600 to-gray-600 rounded-xl shadow-md">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h4a1 1 0 011 1v5m-6 0h6" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-slate-900">{floorName}</h4>
                          <p className="text-sm text-slate-600">{floor.floorLevel.charAt(0).toUpperCase() + floor.floorLevel.slice(1)} configuration & room data</p>
                        </div>
                      </div>
                      {floors.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeFloor(floor.id)}
                          className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2.5 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 text-sm font-semibold flex items-center gap-2 shadow-md hover:shadow-lg"
                          title="Remove Floor"
                        >
                          <Trash2 size={16} />
                          Remove Floor
                        </button>
                      )}
                    </div>
                    {/* Integrated Floor Properties & Calculations Section */}
                    <div className="bg-gradient-to-br from-emerald-50 via-green-50 to-emerald-50 rounded-xl p-5 border-2 border-emerald-200 shadow-sm">
                      {/* Card Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-gradient-to-r from-emerald-600 to-green-600 rounded-xl shadow-md">
                            <Ruler size={18} className="text-white" />
                          </div>
                          <div>
                            <h6 className="text-base font-bold text-emerald-900">Floor Properties & Calculations</h6>
                            <p className="text-xs text-emerald-600">Configuration & performance metrics</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleGetCalculations(floor.id)}
                          className="bg-gradient-to-r from-emerald-600 to-emerald-500 text-white px-4 py-2.5 rounded-lg hover:from-emerald-700 hover:to-emerald-600 transition-all duration-200 text-sm font-semibold flex items-center gap-2 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={!onGetFloorCalculations}
                        >
                          <Ruler size={16} />
                          Get Calculations
                        </button>
                      </div>

                      {/* Floor Configuration Section */}
                      <div className="bg-white/80 rounded-xl p-4 border border-emerald-200 mb-4">
                        <div className="flex items-center mb-4">
                          <svg className="w-4 h-4 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h4a1 1 0 011 1v5m-6 0h6" />
                          </svg>
                          <span className="text-sm font-semibold text-emerald-800">Floor Configuration</span>
                          {/* Floor volume badge if height and area available */}
                          {floor.height && floor.floorArea && (
                            <div className="ml-auto bg-emerald-100 border border-emerald-300 rounded-lg px-3 py-1 text-xs font-semibold text-emerald-800">
                              Volume: {(parseFloat(floor.floorArea) * parseFloat(floor.height)).toFixed(1)}m³
                            </div>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Floor Level Card */}
                          <div className="bg-emerald-50/50 rounded-lg p-3 border border-emerald-200">
                            <div className="flex items-center mb-2">
                              <svg className="w-4 h-4 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                              </svg>
                              <span className="text-xs font-semibold text-emerald-700">Floor Level</span>
                            </div>
                            <SelectField
                              label=""
                              name={`floorLevel-${floor.id}`}
                              value={floor.floorLevel}
                              onChange={(e) => handleFloorChange(floor.id, 'floorLevel', e.target.value)}
                              options={floorLevelOptions}
                            />
                          </div>

                          {/* Building Type Card */}
                          <div className="bg-emerald-50/50 rounded-lg p-3 border border-emerald-200">
                            <div className="flex items-center mb-2">
                              <Home size={16} className="text-emerald-600 mr-2" />
                              <span className="text-xs font-semibold text-emerald-700">Building Type</span>
                            </div>
                            <SelectField
                              label=""
                              name={`buildingType-${floor.id}`}
                              value={floor.buildingType}
                              onChange={(e) => handleFloorChange(floor.id, 'buildingType', e.target.value)}
                              options={buildingTypeOptions}
                            />
                          </div>

                          {/* Height Card */}
                          <div className="bg-emerald-50/50 rounded-lg p-3 border border-emerald-200">
                            <div className="flex items-center mb-2">
                              <svg className="w-4 h-4 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7l4-4m0 0l4 4m-4-4v18" />
                              </svg>
                              <span className="text-xs font-semibold text-emerald-700">Height (m)</span>
                            </div>
                            <FormField
                              label=""
                              name={`height-${floor.id}`}
                              value={floor.height}
                              onChange={(e) => handleFloorChange(floor.id, 'height', e.target.value)}
                              placeholder="2.4"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Performance Metrics Section */}
                      <div className="bg-white/80 rounded-xl p-4 border border-emerald-200">
                        <div className="flex items-center mb-4">
                          <svg className="w-4 h-4 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          <span className="text-sm font-semibold text-emerald-800">Performance Metrics</span>
                          {/* Total area badge if available */}
                          {floor.floorArea && (
                            <div className="ml-auto bg-emerald-100 border border-emerald-300 rounded-lg px-3 py-1 text-xs font-semibold text-emerald-800">
                              Area: {floor.floorArea}m²
                            </div>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Floor Area Card */}
                          <div className="bg-emerald-50/50 rounded-lg p-3 border border-emerald-200">
                            <div className="flex items-center mb-2">
                              <svg className="w-4 h-4 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                              </svg>
                              <span className="text-xs font-semibold text-emerald-700">Floor Area (m²)</span>
                            </div>
                            <FormField
                              label=""
                              name={`floorArea-${floor.id}`}
                              value={floor.floorArea}
                              onChange={(e) => handleFloorChange(floor.id, 'floorArea', e.target.value)}
                              placeholder="45.5"
                            />
                          </div>

                          {/* HLP Card */}
                          <div className="bg-emerald-50/50 rounded-lg p-3 border border-emerald-200">
                            <div className="flex items-center mb-2">
                              <svg className="w-4 h-4 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              <span className="text-xs font-semibold text-emerald-700">HLP</span>
                            </div>
                            <FormField
                              label=""
                              name={`hlp-${floor.id}`}
                              value={floor.hlp}
                              onChange={(e) => handleFloorChange(floor.id, 'hlp', e.target.value)}
                              placeholder="Heat Loss Parameter"
                            />
                          </div>

                          {/* P/W Card */}
                          <div className="bg-emerald-50/50 rounded-lg p-3 border border-emerald-200">
                            <div className="flex items-center mb-2">
                              <svg className="w-4 h-4 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                              <span className="text-xs font-semibold text-emerald-700">P/W</span>
                            </div>
                            <FormField
                              label=""
                              name={`pw-${floor.id}`}
                              value={floor.pw}
                              onChange={(e) => handleFloorChange(floor.id, 'pw', e.target.value)}
                              placeholder="Perimeter/Width"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Rooms Section */}
                    <div className="mt-6 border-t border-gray-200 pt-6">
                      <div className="mb-4">
                      </div>
                      
                      <div className="space-y-6">
                        {floor.rooms.map((room, roomIndex) => (
                          <div key={room.id} className="relative">
                            {/* Modern Room Container */}
                            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-slate-200/30 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 group">
                              {/* Sleek Room Header */}
                              <div className="flex items-center justify-between mb-8">
                                <div className="flex items-center space-x-4">
                                  <div className="relative">
                                    <div className="p-3 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h4a1 1 0 011 1v5m-6 0h6" />
                                      </svg>
                                    </div>
                                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full border-2 border-white shadow-sm"></div>
                                  </div>
                                  <div>
                                    <h5 className="text-2xl font-black text-slate-800 tracking-tight">{room.name || `Room ${roomIndex + 1}`}</h5>
                                    <p className="text-sm font-medium text-slate-500 mt-1">Complete room configuration</p>
                                  </div>
                                </div>
                                
                                {/* Modern Element Indicators */}
                                <div className="flex items-center space-x-3">
                                  {/* Radiator indicator */}
                                  {((room.radiatorWidth || room.radiatorHeight) || (room.radiators && room.radiators.length > 0)) && (
                                    <div className="relative group/badge">
                                      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl px-4 py-2 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2">
                                        <Thermometer size={14} />
                                        <span className="text-sm font-bold">{(room.radiators?.length || 0) + (room.radiatorWidth || room.radiatorHeight ? 1 : 0)}</span>
                                      </div>
                                      <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded-lg opacity-0 group-hover/badge:opacity-100 transition-opacity whitespace-nowrap">
                                        Radiators
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Window indicator */}
                                  {(((room.windowWidth && room.windowWidth.trim() !== '' && room.windowWidth !== '0') || 
                                     (room.windowHeight && room.windowHeight.trim() !== '' && room.windowHeight !== '0') || 
                                     (room.windows && room.windows.length > 0))) && (
                                    <div className="relative group/badge">
                                      <div className="bg-gradient-to-r from-sky-500 to-blue-500 text-white rounded-2xl px-4 py-2 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2">
                                        <RectangleHorizontal size={14} />
                                        <span className="text-sm font-bold">{(room.windows?.length || 0) + ((room.windowWidth || room.windowHeight) && (!room.windows || room.windows.length === 0) ? 1 : 0)}</span>
                                      </div>
                                      <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded-lg opacity-0 group-hover/badge:opacity-100 transition-opacity whitespace-nowrap">
                                        Windows
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className="space-y-6">
                              {/* Sleek Room Configuration Card */}
                              <div className="bg-gradient-to-br from-indigo-50/60 via-purple-50/60 to-violet-50/60 backdrop-blur-sm rounded-3xl p-6 border border-indigo-200/50 shadow-lg hover:shadow-xl transition-all duration-300">
                                {/* Card Header */}
                                <div className="flex items-center justify-between mb-6">
                                  <div className="flex items-center space-x-4">
                                    <div className="p-3 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl shadow-lg">
                                      <RectangleHorizontal size={20} className="text-white" />
                                    </div>
                                    <div>
                                      <h6 className="text-xl font-bold text-slate-800">Configuration</h6>
                                      <p className="text-sm text-slate-600">Basic room properties & dimensions</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-3">
                                    {/* Room area calculation badge */}
                                    {room.length && room.width && (
                                      <div className="bg-white/90 backdrop-blur-sm border border-indigo-200 rounded-2xl px-4 py-2 text-sm font-bold text-indigo-700 shadow-md">
                                        {(parseFloat(room.length) * parseFloat(room.width)).toFixed(2)}m²
                                      </div>
                                    )}
                                    {/* Delete Room Button */}
                                    {floor.rooms.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => removeRoom(floor.id, room.id)}
                                        className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-2xl hover:from-red-600 hover:to-red-700 transition-all duration-300 text-sm font-semibold flex items-center gap-2 shadow-lg hover:shadow-xl hover:scale-105"
                                        title="Remove Room"
                                      >
                                        <Trash2 size={14} />
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {/* Room Configuration Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {/* Room Name */}
                                  <div className="bg-white/60 rounded-xl p-4 border border-purple-200">
                                    <FormField
                                      label="Room Name"
                                      name={`roomName-${floor.id}-${room.id}`}
                                      value={room.name || ''}
                                      onChange={(e) => handleRoomChange(floor.id, room.id, 'name', e.target.value)}
                                      placeholder="e.g., BR1, Kitchen, Living Room"
                                    />
                                  </div>

                                  {/* Room Height */}
                                  <div className="bg-white/60 rounded-xl p-4 border border-purple-200">
                                    <FormField
                                      label="Height (m)"
                                      name={`roomHeight-${floor.id}-${room.id}`}
                                      value={room.height}
                                      onChange={(e) => handleRoomChange(floor.id, room.id, 'height', e.target.value)}
                                      placeholder="2.4"
                                    />
                                  </div>
                                </div>

                                {/* Dimensions Grid */}
                                <div className="bg-white/60 rounded-xl p-4 border border-purple-200">
                                  <div className="flex items-center mb-3">
                                    <svg className="w-4 h-4 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                    </svg>
                                    <span className="text-sm font-semibold text-purple-800">Floor Dimensions</span>
                                  </div>
                                  <div className="grid grid-cols-2 gap-3">
                                    <FormField
                                      label="Length (m)"
                                      name={`length-${floor.id}-${room.id}`}
                                      value={room.length}
                                      onChange={(e) => handleRoomChange(floor.id, room.id, 'length', e.target.value)}
                                      placeholder="4.5"
                                    />
                                    <FormField
                                      label="Width (m)"
                                      name={`width-${floor.id}-${room.id}`}
                                      value={room.width}
                                      onChange={(e) => handleRoomChange(floor.id, room.id, 'width', e.target.value)}
                                      placeholder="3.2"
                                    />
                                  </div>
                                </div>

                              </div>
                              
                              {/* Sleek Radiators Card */}
                              <div className="bg-gradient-to-br from-orange-50/60 via-red-50/60 to-orange-50/60 backdrop-blur-sm rounded-3xl p-6 border border-orange-200/50 shadow-lg hover:shadow-xl transition-all duration-300">
                                {/* Card Header */}
                                <div className="flex items-center justify-between mb-6">
                                  <div className="flex items-center space-x-4">
                                    <div className="p-3 bg-gradient-to-br from-orange-600 to-red-600 rounded-2xl shadow-lg">
                                      <Thermometer size={20} className="text-white" />
                                    </div>
                                    <div>
                                      <h6 className="text-xl font-bold text-slate-800">Heating Elements</h6>
                                      <p className="text-sm text-slate-600">Radiators & heating system components</p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => addRadiator(floor.id, room.id)}
                                    className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-5 py-2.5 rounded-2xl hover:from-orange-600 hover:to-red-600 transition-all duration-300 text-sm font-semibold flex items-center gap-2 shadow-lg hover:shadow-xl hover:scale-105"
                                  >
                                    <Plus size={14} />
                                    Add Radiator
                                  </button>
                                </div>
                                
                                {/* Floor Plan Synced Radiator (Auto-filled) */}
                                {(room.radiatorWidth || room.radiatorHeight) && (
                                  <div className="bg-white/80 rounded-xl p-4 mb-4 border-2 border-orange-300 shadow-sm">
                                    <div className="flex items-center justify-between mb-3">
                                      <div className="flex items-center space-x-2">
                                        <div className="p-1.5 bg-orange-100 rounded-lg">
                                          <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                          </svg>
                                        </div>
                                        <span className="text-sm font-semibold text-orange-700">Auto-synced from Floor Plan</span>
                                      </div>
                                    </div>

                                    {/* Radiator Type Selection */}
                                    <div className="mb-4">
                                      <SelectField
                                        label="Radiator Type"
                                        name={`radiatorType-${floor.id}-${room.id}`}
                                        value={room.radiatorType}
                                        onChange={(e) => handleRoomChange(floor.id, room.id, 'radiatorType', e.target.value)}
                                        options={[
                                          { value: '', label: 'Select type...' },
                                          { value: 'with-trvs', label: 'With TRVs (RADT)' },
                                          { value: 'without-trvs', label: 'Without TRVs (RAD)' },
                                          { value: 'panel-heater', label: 'Electric Panel Heater (PH)' },
                                          { value: 'fan-heater', label: 'Fan Heater (FH)' },
                                          { value: 'electric-storage-heater', label: 'Electric Storage Heater (ESH)' },
                                          { value: 'high-heat-retention', label: 'High Heat Retention (HHR)' }
                                        ]}
                                      />
                                    </div>

                                    {/* Dimensions Section */}
                                    <div className="bg-orange-50/50 rounded-lg p-3 border border-orange-200">
                                      <div className="flex items-center mb-3">
                                        <svg className="w-4 h-4 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                        </svg>
                                        <span className="text-sm font-semibold text-orange-800">Dimensions</span>
                                      </div>
                                      <div className="grid grid-cols-2 gap-3">
                                        <FormField
                                          label="Length (mm)"
                                          name={`radiatorWidth-${floor.id}-${room.id}`}
                                          value={room.radiatorWidth}
                                          onChange={(e) => handleRoomChange(floor.id, room.id, 'radiatorWidth', e.target.value)}
                                          placeholder="600"
                                          disabled={true}
                                        />
                                        <FormField
                                          label="Height (mm)"
                                          name={`radiatorHeight-${floor.id}-${room.id}`}
                                          value={room.radiatorHeight}
                                          onChange={(e) => handleRoomChange(floor.id, room.id, 'radiatorHeight', e.target.value)}
                                          placeholder="600"
                                          disabled={true}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Additional Manual Radiators */}
                                {room.radiators && room.radiators.length > 0 && (
                                  <div className="space-y-3">
                                    {room.radiators.map((radiator, index) => (
                                      <div key={radiator.id} className="bg-white rounded-xl p-4 border-2 border-orange-200 shadow-sm">
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center space-x-2">
                                            <div className="p-1.5 bg-orange-600 rounded-lg">
                                              <Thermometer size={14} className="text-white" />
                                            </div>
                                            <span className="text-sm font-semibold text-orange-800">Additional Radiator {index + 1}</span>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => removeRadiator(floor.id, room.id, radiator.id)}
                                            className="bg-red-500 text-white px-2.5 py-1.5 rounded-lg hover:bg-red-600 transition-colors text-xs font-semibold"
                                          >
                                            Remove
                                          </button>
                                        </div>

                                        {/* Radiator Type */}
                                        <div className="mb-3">
                                          <SelectField
                                            label="Radiator Type"
                                            name={`radiatorType-${floor.id}-${room.id}-${radiator.id}`}
                                            value={radiator.type}
                                            onChange={(e) => handleRadiatorChange(floor.id, room.id, radiator.id, 'type', e.target.value)}
                                            options={[
                                              { value: '', label: 'Select type...' },
                                              { value: 'with-trvs', label: 'With TRVs (RADT)' },
                                              { value: 'without-trvs', label: 'Without TRVs (RAD)' },
                                              { value: 'panel-heater', label: 'Electric Panel Heater (PH)' },
                                              { value: 'fan-heater', label: 'Fan Heater (FH)' },
                                              { value: 'electric-storage-heater', label: 'Electric Storage Heater (ESH)' },
                                              { value: 'high-heat-retention', label: 'High Heat Retention (HHR)' }
                                            ]}
                                          />
                                        </div>

                                        {/* Dimensions Section */}
                                        <div className="bg-orange-50/50 rounded-lg p-3 border border-orange-200">
                                          <div className="flex items-center mb-3">
                                            <svg className="w-4 h-4 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                            </svg>
                                            <span className="text-sm font-semibold text-orange-800">Dimensions</span>
                                          </div>
                                          <div className="grid grid-cols-2 gap-3">
                                            <FormField
                                              label="Length (mm)"
                                              name={`radiatorWidth-${floor.id}-${room.id}-${radiator.id}`}
                                              value={radiator.width}
                                              onChange={(e) => handleRadiatorChange(floor.id, room.id, radiator.id, 'width', e.target.value)}
                                              placeholder="600"
                                            />
                                            <FormField
                                              label="Height (mm)"
                                              name={`radiatorHeight-${floor.id}-${room.id}-${radiator.id}`}
                                              value={radiator.height}
                                              onChange={(e) => handleRadiatorChange(floor.id, room.id, radiator.id, 'height', e.target.value)}
                                              placeholder="600"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Modern Windows Card - Only show if room has meaningful window data */}
                              {((room.windowWidth && room.windowWidth.trim() !== '' && room.windowWidth !== '0') || 
                                (room.windowHeight && room.windowHeight.trim() !== '' && room.windowHeight !== '0') || 
                                (room.windows && room.windows.length > 0 && room.windows.some(w => 
                                  (w.width && w.width.trim() !== '' && w.width !== '0') || 
                                  (w.height && w.height.trim() !== '' && w.height !== '0') || 
                                  (w.orientation && w.orientation.trim() !== '')
                                ))) && (
                                <div className="bg-gradient-to-br from-sky-50/60 via-blue-50/60 to-sky-50/60 backdrop-blur-sm rounded-3xl p-6 border border-sky-200/50 shadow-lg hover:shadow-xl transition-all duration-300">
                                  {/* Card Header */}
                                  <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center space-x-4">
                                      <div className="p-3 bg-gradient-to-br from-sky-600 to-blue-600 rounded-2xl shadow-lg">
                                        <RectangleHorizontal size={20} className="text-white" />
                                      </div>
                                      <div>
                                        <h6 className="text-xl font-bold text-slate-800">Window Elements</h6>
                                        <p className="text-sm text-slate-600">Windows, glazing & natural light</p>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => addWindow(floor.id, room.id)}
                                      className="bg-gradient-to-r from-sky-500 to-blue-500 text-white px-5 py-2.5 rounded-2xl hover:from-sky-600 hover:to-blue-600 transition-all duration-300 text-sm font-semibold flex items-center gap-2 shadow-lg hover:shadow-xl hover:scale-105"
                                    >
                                      <Plus size={14} />
                                      Add Window
                                    </button>
                                  </div>
                                
                                {/* Floor Plan Synced Window (Auto-filled) - Only show if no windows array or array is empty */}
                                {(room.windowWidth || room.windowHeight) && (!room.windows || room.windows.length === 0) && (
                                  <div className="bg-white/80 rounded-xl p-4 mb-4 border-2 border-sky-300 shadow-sm">
                                    <div className="flex items-center justify-between mb-3">
                                      <div className="flex items-center space-x-2">
                                        <div className="p-1.5 bg-sky-100 rounded-lg">
                                          <svg className="w-4 h-4 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                          </svg>
                                        </div>
                                        <span className="text-sm font-semibold text-sky-700">Auto-synced from Floor Plan</span>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => removeSingleWindow(floor.id, room.id)}
                                        className="bg-red-500 text-white px-2.5 py-1.5 rounded-lg hover:bg-red-600 transition-colors text-xs font-semibold"
                                      >
                                        Remove
                                      </button>
                                    </div>

                                    {/* Compact Window Information */}
                                    <div className="bg-sky-50/50 rounded-lg p-3 border border-sky-200 space-y-3">
                                      {/* Dimensions - Compact Row */}
                                      <div>
                                        <div className="flex items-center mb-2">
                                          <svg className="w-3.5 h-3.5 text-sky-600 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                          </svg>
                                          <span className="text-xs font-medium text-sky-700">Dimensions</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                          <NumericFormField
                                            label="Width (m)"
                                            name={`windowWidth-${floor.id}-${room.id}`}
                                            value={room.windowWidth || ''}
                                            onChange={(e) => handleRoomChange(floor.id, room.id, 'windowWidth', e.target.value)}
                                            onBlur={() => handleSingleWindowBlur(floor.id, room.id, 'windowWidth')}
                                            placeholder="1.20"
                                            min={0.1}
                                            max={10}
                                            step={0.01}
                                          />
                                          <NumericFormField
                                            label="Height (m)"
                                            name={`windowHeight-${floor.id}-${room.id}`}
                                            value={room.windowHeight || ''}
                                            onChange={(e) => handleRoomChange(floor.id, room.id, 'windowHeight', e.target.value)}
                                            onBlur={() => handleSingleWindowBlur(floor.id, room.id, 'windowHeight')}
                                            placeholder="1.10"
                                            min={0.1}
                                            max={10}
                                            step={0.01}
                                          />
                                        </div>
                                      </div>

                                      {/* Type & Category - Compact Row */}
                                      <div>
                                        <div className="flex items-center mb-2">
                                          <svg className="w-3.5 h-3.5 text-sky-600 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                          </svg>
                                          <span className="text-xs font-medium text-sky-700">Type & Material</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                          <SelectField
                                            label="Category"
                                            name={`windowCategory-${floor.id}-${room.id}`}
                                            value={(() => {
                                              const value = (room as any).windowCategory || '';
                                              console.log(`🪟 DEBUG: Reading windowCategory for room "${room.name}": "${value}"`);
                                              return value;
                                            })()}
                                            onChange={(e) => handleRoomChange(floor.id, room.id, 'windowCategory', e.target.value)}
                                            options={[
                                              { value: '', label: 'Select...' },
                                              { value: 'Window', label: 'Window' },
                                              { value: 'Roof window', label: 'Roof window' }
                                            ]}
                                          />
                                          <SelectField
                                            label="Frame"
                                            name={`windowType-${floor.id}-${room.id}`}
                                            value={(room as any).windowType || ''}
                                            onChange={(e) => handleRoomChange(floor.id, room.id, 'windowType', e.target.value)}
                                            options={[
                                              { value: '', label: 'Select...' },
                                              { value: 'pvc', label: 'PVC' },
                                              { value: 'wood', label: 'Wood' },
                                              { value: 'metal', label: 'Metal' }
                                            ]}
                                          />
                                        </div>
                                      </div>

                                      {/* Glazing - Compact Row */}
                                      <div>
                                        <div className="flex items-center mb-2">
                                          <svg className="w-3.5 h-3.5 text-sky-600 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          </svg>
                                          <span className="text-xs font-medium text-sky-700">Glazing</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                          <SelectField
                                            label="Type"
                                            name={`windowGlazingType-${floor.id}-${room.id}`}
                                            value={(() => {
                                              const value = (room as any).windowGlazingType || '';
                                              console.log(`🪟 DEBUG: Reading windowGlazingType for room "${room.name}": "${value}"`);
                                              return value;
                                            })()}
                                            onChange={(e) => handleRoomChange(floor.id, room.id, 'windowGlazingType', e.target.value)}
                                            options={[
                                              { value: '', label: 'Select...' },
                                              { value: 'Single glazing', label: 'Single glazing' },
                                              { value: 'Double pre 2002', label: 'Double pre 2002' },
                                              { value: 'Double between 2002 and 2021', label: 'Double 2002-2021' },
                                              { value: 'Double post or during 2022', label: 'Double post 2022' },
                                              { value: 'Double with unknown install date', label: 'Double unknown' },
                                              { value: 'Secondary glazing', label: 'Secondary glazing' },
                                              { value: 'Triple pre 2002', label: 'Triple pre 2002' },
                                              { value: 'Triple between 2002 and 2021', label: 'Triple 2002-2021' },
                                              { value: 'Triple post or during 2022', label: 'Triple post 2022' },
                                              { value: 'Triple with unknown install date', label: 'Triple unknown' },
                                              { value: 'Single glazing, known data', label: 'Single known' },
                                              { value: 'Double glazing, known data', label: 'Double known' },
                                              { value: 'Triple glazing, known data', label: 'Triple known' }
                                            ]}
                                          />
                                          <SelectField
                                            label="Gap"
                                            name={`windowGlazingGap-${floor.id}-${room.id}`}
                                            value={(() => {
                                              const value = (room as any).windowGlazingGap || '';
                                              console.log(`🪟 DEBUG: Reading windowGlazingGap for room "${room.name}": "${value}"`);
                                              return value;
                                            })()}
                                            onChange={(e) => handleRoomChange(floor.id, room.id, 'windowGlazingGap', e.target.value)}
                                            options={[
                                              { value: '', label: 'Select...' },
                                              { value: '6 mm', label: '6 mm' },
                                              { value: '12 mm', label: '12 mm' },
                                              { value: '16 mm or more', label: '16 mm+' }
                                            ]}
                                          />
                                        </div>
                                      </div>

                                      {/* Properties - Compact Row */}
                                      <div>
                                        <div className="flex items-center mb-2">
                                          <svg className="w-3.5 h-3.5 text-sky-600 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                          </svg>
                                          <span className="text-xs font-medium text-sky-700">Properties</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                          <SelectField
                                            label="Trickle Vents"
                                            name={`windowTrickleVents-${floor.id}-${room.id}`}
                                            value={(() => {
                                              const value = room.windowTrickleVents || '';
                                            console.log(`🪟 DEBUG: Reading windowTrickleVents for room "${room.name}": "${value}"`);
                                            return value;
                                          })()}
                                          onChange={(e) => handleRoomChange(floor.id, room.id, 'windowTrickleVents', e.target.value)}
                                          options={[
                                            { value: '', label: 'Select...' },
                                            { value: 'Yes', label: 'Yes' },
                                            { value: 'No', label: 'No' }
                                          ]}
                                        />
                                          <SelectField
                                            label="Draught Proofed"
                                            name={`windowDraughtProofed-${floor.id}-${room.id}`}
                                            value={(room as any).windowDraughtProofed || ''}
                                            onChange={(e) => handleRoomChange(floor.id, room.id, 'windowDraughtProofed', e.target.value)}
                                            options={[
                                              { value: '', label: 'Select...' },
                                              { value: 'Yes', label: 'Yes' },
                                              { value: 'No', label: 'No' }
                                            ]}
                                          />
                                        </div>
                                        <div className="mt-2">
                                          <SelectField
                                            label="Orientation"
                                            name={`windowOrientation-${floor.id}-${room.id}`}
                                            value={(() => {
                                              const storedValue = (room as any).windowOrientation || '';
                                              // Convert from long format (north, northeast) to short format (N, NE) for dropdown
                                              const orientationMap: Record<string, string> = {
                                                'north': 'N', 'northeast': 'NE', 'east': 'E', 'southeast': 'SE',
                                                'south': 'S', 'southwest': 'SW', 'west': 'W', 'northwest': 'NW'
                                              };
                                              const displayValue = orientationMap[storedValue] || storedValue;
                                              console.log(`🪟 DEBUG: Reading windowOrientation for room "${room.name}": "${storedValue}" -> "${displayValue}"`);
                                              return displayValue;
                                            })()}
                                            onChange={(e) => handleRoomChange(floor.id, room.id, 'windowOrientation', e.target.value)}
                                            options={[
                                              { value: '', label: 'Select...' },
                                              { value: 'N', label: 'N' },
                                              { value: 'NE', label: 'NE' },
                                              { value: 'E', label: 'E' },
                                              { value: 'SE', label: 'SE' },
                                              { value: 'S', label: 'S' },
                                              { value: 'SW', label: 'SW' },
                                              { value: 'W', label: 'W' },
                                              { value: 'NW', label: 'NW' }
                                            ]}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Additional Manual Windows */}
                                {room.windows && room.windows.length > 0 && (
                                  <div className="space-y-3">
                                    {room.windows.map((window, index) => {
                                      return (
                                      <div key={window.id} className="bg-white rounded-xl p-4 border-2 border-sky-200 shadow-sm">
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center space-x-2">
                                            <div className="p-1.5 bg-sky-600 rounded-lg">
                                              <RectangleHorizontal size={14} className="text-white" />
                                            </div>
                                            <span className="text-sm font-semibold text-sky-800">Window {index + 1}</span>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => removeWindow(floor.id, room.id, window.id)}
                                            className="bg-red-500 text-white px-2.5 py-1.5 rounded-lg hover:bg-red-600 transition-colors text-xs font-semibold"
                                          >
                                            Remove
                                          </button>
                                        </div>

                                        {/* Compact Window Information */}
                                        <div className="bg-sky-50/50 rounded-lg p-3 border border-sky-200 space-y-3">
                                          {/* Dimensions - Compact Row */}
                                          <div>
                                            <div className="flex items-center mb-2">
                                              <svg className="w-3.5 h-3.5 text-sky-600 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                              </svg>
                                              <span className="text-xs font-medium text-sky-700">Dimensions</span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                              <NumericFormField
                                                label="Width (m)"
                                                name={`windowWidth-${floor.id}-${room.id}-${window.id}`}
                                                value={window.width || ''}
                                                onChange={(e) => handleWindowChange(floor.id, room.id, window.id, 'width', e.target.value)}
                                                onBlur={() => handleMultipleWindowBlur(floor.id, room.id, window.id, 'width')}
                                                placeholder="1.20"
                                                min={0.1}
                                                max={10}
                                                step={0.01}
                                              />
                                              <NumericFormField
                                                label="Height (m)"
                                                name={`windowHeight-${floor.id}-${room.id}-${window.id}`}
                                                value={window.height || ''}
                                                onChange={(e) => handleWindowChange(floor.id, room.id, window.id, 'height', e.target.value)}
                                                onBlur={() => handleMultipleWindowBlur(floor.id, room.id, window.id, 'height')}
                                                placeholder="1.10"
                                                min={0.1}
                                                max={10}
                                                step={0.01}
                                              />
                                            </div>
                                          </div>

                                          {/* Type & Category - Compact Row */}
                                          <div>
                                            <div className="flex items-center mb-2">
                                              <svg className="w-3.5 h-3.5 text-sky-600 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                              </svg>
                                              <span className="text-xs font-medium text-sky-700">Type & Material</span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                              <SelectField
                                                label="Category"
                                                name={`windowCategory-${floor.id}-${room.id}-${window.id}`}
                                                value={(window as any).windowCategory || ''}
                                                onChange={(e) => handleWindowChange(floor.id, room.id, window.id, 'windowCategory', e.target.value)}
                                                options={[
                                                  { value: '', label: 'Select...' },
                                                  { value: 'Window', label: 'Window' },
                                                  { value: 'Roof window', label: 'Roof window' }
                                                ]}
                                              />
                                              <SelectField
                                                label="Frame"
                                                name={`windowType-${floor.id}-${room.id}-${window.id}`}
                                                value={(window as any).windowType || ''}
                                                onChange={(e) => handleWindowChange(floor.id, room.id, window.id, 'windowType', e.target.value)}
                                                options={[
                                                  { value: '', label: 'Select...' },
                                                  { value: 'pvc', label: 'PVC' },
                                                  { value: 'wood', label: 'Wood' },
                                                  { value: 'metal', label: 'Metal' }
                                                ]}
                                              />
                                            </div>
                                          </div>

                                          {/* Glazing - Compact Row */}
                                          <div>
                                            <div className="flex items-center mb-2">
                                              <svg className="w-3.5 h-3.5 text-sky-600 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                              </svg>
                                              <span className="text-xs font-medium text-sky-700">Glazing</span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                              <SelectField
                                                label="Type"
                                                name={`glazingType-${floor.id}-${room.id}-${window.id}`}
                                                value={(window as any).glazingType || ''}
                                                onChange={(e) => handleWindowChange(floor.id, room.id, window.id, 'glazingType', e.target.value)}
                                                options={[
                                                  { value: '', label: 'Select...' },
                                                  { value: 'Single glazing', label: 'Single glazing' },
                                                  { value: 'Double pre 2002', label: 'Double pre 2002' },
                                                  { value: 'Double between 2002 and 2021', label: 'Double 2002-2021' },
                                                  { value: 'Double post or during 2022', label: 'Double post 2022' },
                                                  { value: 'Double with unknown install date', label: 'Double unknown' },
                                                  { value: 'Secondary glazing', label: 'Secondary glazing' },
                                                  { value: 'Triple pre 2002', label: 'Triple pre 2002' },
                                                  { value: 'Triple between 2002 and 2021', label: 'Triple 2002-2021' },
                                                  { value: 'Triple post or during 2022', label: 'Triple post 2022' },
                                                  { value: 'Triple with unknown install date', label: 'Triple unknown' },
                                                  { value: 'Single glazing, known data', label: 'Single known' },
                                                  { value: 'Double glazing, known data', label: 'Double known' },
                                                  { value: 'Triple glazing, known data', label: 'Triple known' }
                                                ]}
                                              />
                                              <SelectField
                                                label="Gap"
                                                name={`glazingGap-${floor.id}-${room.id}-${window.id}`}
                                                value={(window as any).glazingGap || ''}
                                                onChange={(e) => handleWindowChange(floor.id, room.id, window.id, 'glazingGap', e.target.value)}
                                                options={[
                                                  { value: '', label: 'Select...' },
                                                  { value: '6 mm', label: '6 mm' },
                                                  { value: '12 mm', label: '12 mm' },
                                                  { value: '16 mm or more', label: '16 mm+' }
                                                ]}
                                              />
                                            </div>
                                          </div>

                                          {/* Properties - Compact Row */}
                                          <div>
                                            <div className="flex items-center mb-2">
                                              <svg className="w-3.5 h-3.5 text-sky-600 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                              </svg>
                                              <span className="text-xs font-medium text-sky-700">Properties</span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                              <SelectField
                                                label="Trickle Vents"
                                                name={`windowTrickleVents-${floor.id}-${room.id}-${window.id}`}
                                                value={window.trickleVents ? 'Yes' : 'No'}
                                                onChange={(e) => handleWindowChange(floor.id, room.id, window.id, 'trickleVents', e.target.value)}
                                                options={[
                                                  { value: '', label: 'Select...' },
                                                  { value: 'Yes', label: 'Yes' },
                                                  { value: 'No', label: 'No' }
                                                ]}
                                              />
                                              <SelectField
                                                label="Draught Proofed"
                                                name={`windowDraughtProofed-${floor.id}-${room.id}-${window.id}`}
                                                value={(window as any).isDraughtProofed ? 'Yes' : 'No'}
                                                onChange={(e) => handleWindowChange(floor.id, room.id, window.id, 'isDraughtProofed', e.target.value)}
                                                options={[
                                                  { value: '', label: 'Select...' },
                                                  { value: 'Yes', label: 'Yes' },
                                                  { value: 'No', label: 'No' }
                                                ]}
                                              />
                                            </div>
                                            <div className="mt-2">
                                              <SelectField
                                                label="Orientation"
                                                name={`windowOrientation-${floor.id}-${room.id}-${window.id}`}
                                                value={window.orientation || ''}
                                                onChange={(e) => handleWindowChange(floor.id, room.id, window.id, 'orientation', e.target.value)}
                                                options={[
                                                  { value: '', label: 'Select...' },
                                                  { value: 'N', label: 'N' },
                                                  { value: 'NE', label: 'NE' },
                                                  { value: 'E', label: 'E' },
                                                  { value: 'SE', label: 'SE' },
                                                  { value: 'S', label: 'S' },
                                                  { value: 'SW', label: 'SW' },
                                                  { value: 'W', label: 'W' },
                                                  { value: 'NW', label: 'NW' }
                                                ]}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                              )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  );
                })}
                <button
                  type="button"
                  onClick={addFloor}
                  className="w-full py-3 px-4 border-2 border-dashed border-yellow-300 rounded-lg text-yellow-600 hover:border-yellow-400 hover:text-yellow-700 hover:bg-yellow-50 transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <Plus size={20} />
                  Add New Floor
                </button>
            </div>
          </div>
        );
        return (
          <div className="space-y-8">
            {/* Room Labels Section */}
            <SectionCard icon={Home} title="Room Labels">
              <div className="space-y-6">
                <p className="text-sm text-gray-600 mb-4">
                  Room labels from your floor plan are automatically synchronized here. These reflect the current room naming from your floor plan.
                </p>
                
                {/* Display Room Labels */}
                {roomCustomLabels && Object.keys(roomCustomLabels || {}).length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(roomCustomLabels || {}).map(([roomId, label]) => (
                      <div key={roomId} className="bg-gradient-to-br from-emerald-50/60 via-teal-50/60 to-cyan-50/60 backdrop-blur-sm rounded-xl p-4 border border-emerald-200/50 shadow-sm hover:shadow-md transition-all duration-200">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-lg shadow-sm">
                            <Home size={16} className="text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">Room ID</p>
                            <p className="text-xs text-gray-500 font-mono">{roomId}</p>
                          </div>
                        </div>
                        <div className="mt-3">
                          <p className="text-lg font-bold text-gray-800">{label}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-8 text-center border border-gray-200">
                    <Home size={48} className="text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">No room labels found</p>
                    <p className="text-sm text-gray-500">Room labels will appear here when you create and label rooms in your floor plan.</p>
                  </div>
                )}

                {/* Info Card */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-blue-800 mb-1">Room Label Management</h4>
                      <p className="text-sm text-blue-700">
                        To create or edit room labels, use the floor plan editor. Room labels are automatically synchronized 
                        between the floor plan and this section. You can create new rooms and edit existing room labels 
                        directly in the floor plan view.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>
        );

      case 'evidence':
        const evidenceCategories = [
          { id: 'exterior', label: 'Exterior', icon: Home },
          { id: 'wall-construction', label: 'Wall Construction Type', icon: Building },
          { id: 'wall-insulation', label: 'Wall Insulation', icon: Settings },
          { id: 'floor', label: 'Floor', icon: Ruler },
          { id: 'roof-interior', label: 'Roof Interior', icon: Home },
          { id: 'heating-system', label: 'Heating System', icon: Thermometer },
          { id: 'heating-controls', label: 'Heating Controls', icon: Settings },
          { id: 'windows-doors', label: 'Windows & Doors', icon: RectangleHorizontal },
          { id: 'ventilation', label: 'Ventilation', icon: Wind },
          { id: 'lighting', label: 'Lighting', icon: Lightbulb },
          { id: 'renewables', label: 'Renewables', icon: Zap },
          { id: 'additional', label: 'Additional Images', icon: ImageIcon }
        ];

        const renderEvidencePhotoThumbnail = (categoryPhotos: any[], category: typeof evidenceCategories[0]) => {
          if (!categoryPhotos || categoryPhotos.length === 0) {
            return <p className="text-xs text-gray-500 mt-2">No photos added yet.</p>;
          }
          
          const firstPhoto = categoryPhotos[0];
          const imageUrl = firstPhoto.url || firstPhoto.dataUrl;
          if (!imageUrl) return <p className="text-xs text-gray-500 mt-2">No photos added yet.</p>;
          
          return (
            <div className="mt-2">
              <div 
                className="w-24 h-24 relative cursor-pointer group flex-shrink-0"
                onClick={() => setViewingEvidenceGallery(category.id)}
              >
                <img 
                  src={imageUrl} 
                  alt={category.label} 
                  className="w-full h-full object-cover rounded-lg border-2 border-yellow-300 shadow-sm hover:border-yellow-400 transition-colors" 
                />
                {categoryPhotos.length > 1 && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-white text-sm font-semibold">{categoryPhotos.length} photos</span>
                  </div>
                )}
                <div className="absolute bottom-1 right-1 bg-yellow-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                  {categoryPhotos.length}
                </div>
              </div>
            </div>
          );
        };

        const renderEvidenceSection = (category: typeof evidenceCategories[0]) => {
          const categoryPhotos = (formData.evidencePhotos || []).filter((p: any) => p.category === category.id);
          const CategoryIcon = category.icon;
          
          return (
            <div key={category.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50/30 space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <CategoryIcon className="text-blue-600" size={18} />
                {category.label}
              </label>
              <PhotoCapture
                onPhotosCaptured={(capturedPhotos) => {
                  console.log(`[Evidence] Captured ${capturedPhotos.length} photos for ${category.id}`);
                  const newPhotos = capturedPhotos.map((p, idx) => {
                    const timestamp = Date.now();
                    const displayId = `evidence-${category.id}-${timestamp}-${idx}`;
                    const dataUrl = `data:image/${p.format};base64,${p.base64String}`;
                    
                    return {
                      id: displayId,
                      displayId,
                      dataUrl, // Compressed base64 for offline viewing
                      url: '', // Will be populated with CDN URL after upload
                      timestamp: new Date().toISOString(),
                      description: '',
                      category: category.id,
                      uploading: true,
                      format: p.format
                    };
                  });
                  
                  // Add photos to state immediately
                  setFormData(prev => ({
                    ...prev,
                    evidencePhotos: [...(prev.evidencePhotos || []), ...newPhotos]
                  }));
                  
                  // Queue CDN uploads in background
                  newPhotos.forEach((photo) => {
                    const photoDisplayId = photo.displayId;
                    const photoId = photo.id;
                    queuePhotoUpload(
                      photoDisplayId,
                      photo.dataUrl,
                      'evidence',
                      category.id,
                      (cdnUrl) => {
                        // Update photo with CDN URL on success
                        console.log(`[Evidence] CDN upload success for ${photoDisplayId}:`, cdnUrl);
                        setFormData(prev => ({
                          ...prev,
                          evidencePhotos: (prev.evidencePhotos || []).map(p =>
                            p.id === photoId
                              ? { ...p, url: cdnUrl, uploading: false, dataUrl: '' } // Clear base64 after CDN upload
                              : p
                          )
                        }));
                      },
                      (error) => {
                        console.error(`[Evidence] CDN upload failed for ${photoDisplayId}:`, error);
                        // Keep base64 on failure, mark as not uploading
                        setFormData(prev => ({
                          ...prev,
                          evidencePhotos: (prev.evidencePhotos || []).map(p =>
                            p.id === photoId
                              ? { ...p, uploading: false }
                              : p
                          )
                        }));
                      }
                    );
                  });
                }}
                maxPhotos={999}
                buttonLabel={`Capture ${category.label}`}
                buttonClass="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 transition-all duration-200"
              />
              {renderEvidencePhotoThumbnail(categoryPhotos, category)}
            </div>
          );
        };

        // Get category photos for gallery
        const getGalleryPhotos = (categoryId: string) => {
          return (formData.evidencePhotos || []).filter((p: any) => p.category === categoryId);
        };

        return (
          <>
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                <h2 className="text-xl font-bold text-gray-900 mb-2">Evidence Documentation</h2>
                <p className="text-sm text-gray-600">
                  Capture and organize evidence photos by category for comprehensive property documentation.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {evidenceCategories.map(renderEvidenceSection)}
              </div>
            </div>

            {/* Photo Gallery Modal */}
            {viewingEvidenceGallery && (() => {
              const category = evidenceCategories.find(c => c.id === viewingEvidenceGallery);
              if (!category) return null;
              const categoryPhotos = getGalleryPhotos(viewingEvidenceGallery);
              return (
                <PhotoGalleryModal
                  isOpen={true}
                  onClose={() => setViewingEvidenceGallery(null)}
                  photos={categoryPhotos.map((p: any) => ({
                    id: p.displayId || p.id,
                    fileName: p.fileName,
                    url: p.url,
                    localPath: p.dataUrl,
                    uploading: p.uploading,
                    error: p.error,
                    note: p.description || '',
                    timestamp: p.timestamp,
                  }))}
                  sectionTitle={`Evidence - ${category.label}`}
                  onDeletePhoto={(photoId) => {
                    setFormData(prev => ({
                      ...prev,
                      evidencePhotos: prev.evidencePhotos?.filter((p: any) => (p.displayId || p.id) !== photoId) || []
                    }));
                  }}
                  onUpdateNote={(photoId, note) => {
                    setFormData(prev => ({
                      ...prev,
                      evidencePhotos: prev.evidencePhotos?.map((p: any) => 
                        (p.displayId || p.id) === photoId ? { ...p, description: note } : p
                      ) || []
                    }));
                  }}
                  zIndex={9999}
                />
              );
            })()}
          </>
        );

      case 'additional':
        return (
          <div className="space-y-8">
            {/* Additional Information */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center gap-3 pb-4 mb-6 border-b-2 border-yellow-100">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Edit3 className="text-yellow-600" size={20} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">Additional Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-800">Additional Notes</label>
                      <textarea 
                        name="additionalNotes" 
                        value={formData.additionalNotes} 
                        onChange={handleFormChange} 
                        rows={8} 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent placeholder-gray-400 bg-gray-50"
                        placeholder="Any additional observations or notes..."
                      />
                    </div>
                  </div>
                </div>
              </div>

            {/* Action Button */}
            <div className="flex justify-end pt-6">
              <button 
                type="button" 
                onClick={handleSaveAndClose}
                className="px-8 py-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-semibold rounded-lg shadow-md hover:from-yellow-600 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02]"
              >
                Save & Close
              </button>
            </div>
          </div>
        );

      default:
        return <div>Section not found</div>;
    }
  }
};

export default SurveyorSiteNotes; 